<template>
  <div class="designer-v3" :style="{ padding: viewMode ? '0' : '10px 45px', '--content-height': `${contentHeight}mm`, '--content-width':`${contentWidth}mm` }">
    <div v-if="!viewMode && !showPDFViewer" class="left-side">
      <button style="margin-bottom: 5px; margin-right: 5px" @click="changeToPreviewLayout">Preview</button>
      <button
        style="margin-bottom: 5px; margin-right: 5px"
        @click="clearEmpty"
        disabled
        title="Temporarily disabled until a bug is fixed"
        >Clear All Empty Fields</button
      >
      <button
        style="margin-bottom: 5px"
        :title="saveReportButtonTitle"
        :disabled="isDevelopmentMode"
        @click="saveReportClicked"
        >Save Report</button
      >
      <!-- =============== wiggle this button if it's disabled -->
      <button style="margin-bottom: 5px" @click="loadReportClicked">Load Report</button>
      <br />

      <Dropdown
        v-if="!selectedFieldID"
        placeholder="Paper Size"
        :items="reportPaperList"
        itemKey="name"
        :selectedObject.sync="selectedPaper"
        :isDisabled="true"
      />

      <Dropdown
        v-if="!selectedFieldID"
        placeholder="Section"
        :items="reportSections"
        itemKey="name"
        :selectedObject.sync="selectedReportSection"
        :errorMessage.sync="reportSectionErrorMessage"
        @selected="reportSectionDropdownSelected"
      />
      <BoxContainer v-if="!selectedFieldID" :title="reportSectionStylingTitle" style="margin-top: 10px">
        <Input
          placeholder="Padding Top"
          :inputValue.sync="reportSectionStylingPaddingTopInputValue"
          :isDisabled="!reportSectionIsSelected"
          @inputChanged="reportSectionStylingPaddingInputChanged('paddingTop', $event)"
        />
        <Input
          placeholder="Padding Right"
          :inputValue.sync="reportSectionStylingPaddingRightInputValue"
          :isDisabled="!reportSectionIsSelected"
          @inputChanged="reportSectionStylingPaddingInputChanged('paddingRight', $event)"
        />
        <Input
          placeholder="Padding Bottom"
          :inputValue.sync="reportSectionStylingPaddingBottomInputValue"
          :isDisabled="!reportSectionIsSelected"
          @inputChanged="reportSectionStylingPaddingInputChanged('paddingBottom', $event)"
        />
        <Input
          placeholder="Padding Left"
          :inputValue.sync="reportSectionStylingPaddingLeftInputValue"
          :isDisabled="!reportSectionIsSelected"
          @inputChanged="reportSectionStylingPaddingInputChanged('paddingLeft', $event)"
        />
      </BoxContainer>

      <CheckBoxV2
        v-if="!selectedFieldID && selectedReportSection['name'] === 'Footer'"
        title="Preserve Space"
        :isChecked.sync="footerPreserveSpace"
      />

      <button style="margin-top: 5px; margin-right: 5px" @click="addFieldClicked('string')">Add Text</button>
      <button style="margin-top: 5px" @click="addFieldClicked('table')">Add Table</button>

      <div v-if="selectedFieldID">
        <div
          ><b><i>** All unit for numbers below are in millimeters (mm)</i></b></div
        >
        <BoxContainer
          title="Conditional Rendering"
          :isCheckBox="true"
          :isChecked.sync="selectedFieldIsConditionallyRendered"
          @checkBoxClicked="updateConditionalRenderingRenderif"
        >
          <div class="field-details-row">
            <Input
              placeholder="Data Key"
              :inputValue.sync="selectedFieldConditionalRenderingDataKey"
              :isDisabled="!selectedFieldIsConditionallyRendered"
              @inputChanged="updateConditionalRenderingRenderif"
            />
            <span>is</span>
            <Dropdown
              placeholder="Condition"
              :items="conditionalRenderingConditionItems"
              itemKey="name"
              :selectedObject.sync="selectedFieldConditionalRenderingCondition"
              :isDisabled="!selectedFieldIsConditionallyRendered"
              @selected="updateConditionalRenderingRenderif"
            />
          </div>
          <div class="field-details-row" v-if="selectedFieldIsInHeaderOrFooter">
            <Dropdown
              placeholder="Operator"
              :items="conditionalRenderingOperatorItems"
              itemKey="name"
              :selectedObject.sync="selectedFieldConditionalRenderingOperator"
              :isDisabled="!selectedFieldIsConditionallyRendered"
              @selected="updateConditionalRenderingRenderif"
            />
            <Dropdown
              placeholder="On Pages"
              :items="conditionalRenderingOnPagesItems"
              itemKey="name"
              :selectedObject.sync="selectedFieldConditionalRenderingOnPages"
              :isDisabled="!selectedFieldIsConditionallyRendered"
              @selected="updateConditionalRenderingRenderif"
            />
          </div>
          <CheckBoxV2
            title="Preserve Space"
            :isChecked.sync="selectedFieldConditionalRenderingPreserveSpace"
            :isDisabled="!selectedFieldIsConditionallyRendered"
            @checkBoxClicked="updateConditionalRenderingRenderif"
          />
        </BoxContainer>

        <BoxContainer title="Content" style="margin-top: 20px">
          <CheckBoxV2
            title="Is Image"
            :isChecked.sync="selectedFieldIsImage"
            @checkBoxClicked="isImageCheckBoxClicked"
          />

          <div>{{ selectedFieldIsImage ? 'Image Link' : 'Text Content' }}</div>
          <textarea class="value-textarea" v-model="selectedFieldTextContent" ref="valueTextarea"></textarea>

          <div class="field-details-row" style="margin-top: 5px" v-if="!selectedFieldIsImage">
            <img
              src="@/assets/designer/BoldText.png"
              @click="textContentFormatStyleClicked('b')"
              :class="['text-format-icons']"
            />
            <img
              src="@/assets/designer/ItalicText.png"
              @click="textContentFormatStyleClicked('i')"
              :class="['text-format-icons']"
            />
            <img
              src="@/assets/designer/UnderlineText.png"
              @click="textContentFormatStyleClicked('u')"
              :class="['text-format-icons']"
            />
          </div>
        </BoxContainer>

        <!-- ============ add a reactive text somewhere showing details about the selected field, which row, which cell, etc. -->
        <!-- ============ so that developers don't need to count manually when updating looping configuration -->

        <BoxContainer
          title="Looping"
          style="margin-top: 20px"
          :isCheckBox="true"
          :isChecked.sync="selectedFieldIsLooped"
          @checkBoxClicked="updateSelectedFieldLoopon"
        >
          <div class="field-details-row">
            <Input
              placeholder="Field Key"
              :inputValue.sync="selectedFieldLoopingFieldKey"
              :isDisabled="!selectedFieldIsLooped"
              @inputChanged="updateSelectedFieldLoopon"
            />
            <Input
              placeholder="Margin In Between"
              :inputValue.sync="selectedFieldLoopingMarginInBetween"
              :isDisabled="!selectedFieldIsLooped"
              @inputChanged="updateSelectedFieldLoopon"
            />
          </div>
          <div v-for="(depthLevel, index) in selectedFieldLoopingDepthLevels" :key="index" class="field-details-row">
            <Input
              :placeholder="`Depth Level ${index + 1} Key`"
              style="flex: 1.5"
              :inputValue.sync="depthLevel.depthKey"
              :isDisabled="!selectedFieldIsLooped"
              @inputChanged="updateSelectedFieldLoopon"
            />
            <Input
              placeholder="From Row #"
              style="flex: 1"
              :inputValue.sync="depthLevel.fromRow"
              :isDisabled="!selectedFieldIsLooped"
              validator="positiveInteger"
              @inputChanged="updateSelectedFieldLoopon"
            />
            <span>-</span>
            <Input
              placeholder="To Row #"
              style="flex: 1"
              :inputValue.sync="depthLevel.toRow"
              :isDisabled="!selectedFieldIsLooped"
              validator="positiveInteger"
              @inputChanged="updateSelectedFieldLoopon"
            />
            <img
              v-if="selectedFieldIsLooped"
              src="https://dsmlib.logistics.systems/Icons/Delete.png"
              style="height: 20px; cursor: pointer"
              @click="deleteDepthLevelClicked(index)"
            />
          </div>
          <button v-if="selectedFieldIsLooped && selectedFieldIsTable" @click="addDepthLevelClicked"
            >Add Depth Level</button
          >
        </BoxContainer>

        <!-- ============ add up down left and right buttons somewhere here to easily move fields around the report template -->
        <!-- ============ the buttons should respect the selected styling section, this will allow for moving of rows and cells within table fields as well -->

        <div v-if="selectedFieldIsTable">
          <button @click="addRemoveTableHeaderClicked" style="margin-top: 5px">{{
            selectedTableFieldHasHeader ? 'Remove Header' : 'Add Header'
          }}</button>
          <button @click="addRemoveTableFooterClicked" style="margin: 5px 0 0 5px">{{
            selectedTableFieldHasFooter ? 'Remove Footer' : 'Add Footer'
          }}</button>
          <br />
          <button style="margin-top: 5px" @click="addRowClicked">Add Row</button>
          <button style="margin: 5px 0 0 5px" @click="addColumnClicked">Add Column</button>
          <br />
          <button
            style="margin-top: 5px"
            :disabled="selectedTableFieldCellIsTheLeftmostCell"
            :title="mergeLeftButtonTitle"
            @click="mergeLeftClicked"
            >Merge Left</button
          >
          <button
            style="margin: 5px 0 0 5px"
            :disabled="selectedTableFieldCellIsTheRightmostCell"
            :title="mergeRightButtonTitle"
            @click="mergeRightClicked"
            >Merge Right</button
          >
          <br />
          <button
            style="margin-top: 5px"
            :disabled="selectedTableFieldCellIsMergedCell"
            :title="splitLeftRightButtonTitle"
            @click="splitLeftClicked"
            >Split Left</button
          >
          <button
            style="margin: 5px 0 0 5px"
            :disabled="selectedTableFieldCellIsMergedCell"
            :title="splitLeftRightButtonTitle"
            @click="splitRightClicked"
            >Split Right</button
          >
          <br />
          <button class="delete-button" style="margin-top: 5px" @click="deleteRowClicked">Delete Row</button>
          <button class="delete-button" style="margin: 5px 0 0 5px" @click="deleteColumnClicked">Delete Column</button>
          <br />
          <button class="delete-button" style="margin-top: 5px" @click="deleteTableClicked">Delete Table</button>
        </div>
        <div v-else>
          <button class="delete-button" style="margin-top: 5px" @click="deleteTextClicked">Delete Text</button>
        </div>

        <Dropdown
          v-if="selectedFieldIsTable"
          placeholder="Styling Section"
          :items="stylingSectionItems"
          itemKey="name"
          :selectedObject.sync="selectedStylingSection"
          style="margin-top: 15px"
          @selected="stylingSectionDropdownSelected"
        />

        <BoxContainer v-if="!selectedFieldIsImage" title="Text Formatting" style="margin-top: 20px">
          <Dropdown
            placeholder="Font Family"
            :items="fontFamilyListing"
            itemKey="name"
            :selectedObject.sync="selectedFieldFontFamily"
            @selected="fontFamilyDropdownSelected"
          />
          <Input placeholder="Font Size" :inputValue.sync="selectedField['CSSStyles']['fontSize']" />
          <div class="field-details-row">
            <img
              title="Align Left"
              src="@/assets/designer/LeftTextAlign.png"
              @click="horizontalTextAlignClicked('left')"
              :class="[
                'text-format-icons',
                { 'text-format-selected': selectedField['CSSStyles']['textAlign'] === 'left' },
              ]"
            />
            <img
              title="Align Center"
              src="@/assets/designer/CenterTextAlign.png"
              @click="horizontalTextAlignClicked('center')"
              :class="[
                'text-format-icons',
                { 'text-format-selected': selectedField['CSSStyles']['textAlign'] === 'center' },
              ]"
            />
            <img
              title="Align Right"
              src="@/assets/designer/RightTextAlign.png"
              @click="horizontalTextAlignClicked('right')"
              :class="[
                'text-format-icons',
                { 'text-format-selected': selectedField['CSSStyles']['textAlign'] === 'right' },
              ]"
            />
            <img
              title="Justify"
              src="@/assets/designer/JustifyTextAlign.png"
              @click="horizontalTextAlignClicked('justify')"
              :class="[
                'text-format-icons',
                { 'text-format-selected': selectedField['CSSStyles']['textAlign'] === 'justify' },
              ]"
            />
            <img
              title="Inherit"
              src="@/assets/designer/InheritTextAlign.svg"
              @click="horizontalTextAlignClicked('inherit')"
              :class="[
                'text-format-icons',
                { 'text-format-selected': selectedField['CSSStyles']['textAlign'] === 'inherit' },
              ]"
            />
          </div>
          <div v-if="selectedFieldIsTable" class="field-details-row" style="margin-top: 5px">
            <img
              title="Align Top"
              src="@/assets/designer/AlignTextTop.png"
              @click="verticalTextAlignClicked('top')"
              :class="[
                'text-format-icons',
                { 'text-format-selected': selectedField['CSSStyles']['verticalAlign'] === 'top' },
              ]"
            />
            <img
              title="Align Center"
              src="@/assets/designer/AlignTextMiddle.png"
              @click="verticalTextAlignClicked('middle')"
              :class="[
                'text-format-icons',
                { 'text-format-selected': selectedField['CSSStyles']['verticalAlign'] === 'middle' },
              ]"
            />
            <img
              title="Align Bottom"
              src="@/assets/designer/AlignTextBottom.png"
              @click="verticalTextAlignClicked('bottom')"
              :class="[
                'text-format-icons',
                { 'text-format-selected': selectedField['CSSStyles']['verticalAlign'] === 'bottom' },
              ]"
            />
            <img
              title="Inherit"
              src="@/assets/designer/InheritTextAlign.svg"
              @click="verticalTextAlignClicked('inherit')"
              :class="[
                'text-format-icons',
                { 'text-format-selected': selectedField['CSSStyles']['verticalAlign'] === 'inherit' },
              ]"
            />
          </div>
        </BoxContainer>

        <BoxContainer v-if="selectedFieldIsTable" title="Column Sizing" style="margin-top: 20px">
          <div class="field-details-row" v-for="(c, ci) in selectedFieldColumnSizing" :key="ci">
            <div style="width: 100px">Column {{ ci + 1 }}</div>
            <Input
              :inputValue.sync="selectedFieldColumnSizing[ci]"
              placeholder=" "
              @inputChanged="columnSizingInputChanged(ci, $event)"
            />
          </div>
        </BoxContainer>

        <!-- #region Positioning -->
        <BoxContainer v-if="selectedFieldCanBePositioned" title="Positioning" style="margin-top: 20px">
          <Input placeholder="Position" :inputValue.sync="selectedField['CSSStyles']['position']" />
          <Input placeholder="Width" :inputValue.sync="selectedField['CSSStyles']['width']" />
          <div class="field-details-row">
            <div>Height</div>
            <CheckBoxV2
              title="Fit Content"
              :isChecked.sync="selectedField['styleInputValues']['fitHeight']"
              @checkBoxClicked="fitHeightCheckBoxClicked"
            />
          </div>
          <Input
            placeholder=" "
            :inputValue.sync="selectedField['styleInputValues']['heightValue']"
            :isDisabled="selectedField['styleInputValues']['fitHeight']"
            @inputChanged="heightValueInputChanged"
          />
          <Input placeholder="Top" :inputValue.sync="selectedField['CSSStyles']['top']" />
          <Input placeholder="Left" :inputValue.sync="selectedField['CSSStyles']['left']" />
        </BoxContainer>
        <!-- #endregion -->

        <BoxContainer title="Padding" style="margin-top: 20px">
          <Input placeholder="Padding Top" :inputValue.sync="selectedField['CSSStyles']['paddingTop']" />
          <Input placeholder="Padding Right" :inputValue.sync="selectedField['CSSStyles']['paddingRight']" />
          <Input placeholder="Padding Bottom" :inputValue.sync="selectedField['CSSStyles']['paddingBottom']" />
          <Input placeholder="Padding Left" :inputValue.sync="selectedField['CSSStyles']['paddingLeft']" />
        </BoxContainer>

        <BoxContainer v-if="selectedFieldCanBePositioned" title="Margin" style="margin-top: 20px">
          <Input placeholder="Margin Top" :inputValue.sync="selectedField['CSSStyles']['marginTop']" />
          <Input placeholder="Margin Right" :inputValue.sync="selectedField['CSSStyles']['marginRight']" />
          <Input placeholder="Margin Bottom" :inputValue.sync="selectedField['CSSStyles']['marginBottom']" />
          <Input placeholder="Margin Left" :inputValue.sync="selectedField['CSSStyles']['marginLeft']" />
        </BoxContainer>

        <BoxContainer title="Border" style="margin-top: 20px">
          <CheckBoxV2
            title="No Border"
            :isChecked.sync="selectedField['styleInputValues']['noBorder']"
            @checkBoxClicked="noBorderCheckBoxClicked"
          />
          <Input
            placeholder="Border Top"
            :inputValue.sync="selectedField['styleInputValues']['borderTopValue']"
            :isDisabled="selectedField['styleInputValues']['noBorder']"
            @inputChanged="borderValueInputChanged('top', $event)"
          />
          <Input
            placeholder="Border Right"
            :inputValue.sync="selectedField['styleInputValues']['borderRightValue']"
            :isDisabled="selectedField['styleInputValues']['noBorder']"
            @inputChanged="borderValueInputChanged('right', $event)"
          />
          <Input
            placeholder="Border Bottom"
            :inputValue.sync="selectedField['styleInputValues']['borderBottomValue']"
            :isDisabled="selectedField['styleInputValues']['noBorder']"
            @inputChanged="borderValueInputChanged('bottom', $event)"
          />
          <Input
            placeholder="Border Left"
            :inputValue.sync="selectedField['styleInputValues']['borderLeftValue']"
            :isDisabled="selectedField['styleInputValues']['noBorder']"
            @inputChanged="borderValueInputChanged('left', $event)"
          />
          <div class="field-details-row" style="align-items: flex-start">
            <Dropdown
              placeholder="Style"
              :items="borderStyles"
              itemKey="style"
              :selectedObject.sync="selectedFieldBorderStyle"
              :isDisabled="selectedField['styleInputValues']['noBorder']"
              @selected="borderStyleDropdownSelected"
            />
            <Input
              placeholder="Color"
              :inputValue.sync="selectedField['CSSStyles']['borderColor']"
              :isDisabled="selectedField['styleInputValues']['noBorder']"
            />
          </div>
        </BoxContainer>

        <Input
          placeholder="Background Color"
          :inputValue.sync="selectedField['CSSStyles']['backgroundColor']"
          style="margin-top: 10px"
        />
      </div>
    </div>
    <div v-else-if="!viewMode && showPDFViewer" class="left-side">
      <button @click="changeToDesignerLayout"> Edit </button>
    </div>

    <div v-show="showReportPreview" class="right-side">
      <button v-if="!viewMode" @click="addPageClicked(null)">Add Page</button>
      <div class="pdf-container">
        <div class="background-image"></div>
        <header class="page-header" ref="pageHeader" :style="headerStyle" @click="pageClicked($event, 'header')">
          <div v-for="(h, hi) in header" :key="hi">
            <div
              v-if="h['type'] == 'string'"
              :class="['field-container', { 'field-selected': fieldIsSelected('header', 'string', hi) }]"
              :style="[h['CSSStyles']]"
              :renderif="h['conditionalRendering']['renderif']"
              :loopon="h.looping.loopon"
              @click="fieldClicked($event, 'header', 'string', hi)"
            >
              <img
                v-if="h['isImage']"
                :src="h['value']"
                class="field-content"
                :style="{ width: '100%', objectFit: 'contain' }"
              />
              <div v-else v-html="h['value']" class="field-content"></div>
            </div>
            <table
              v-else-if="h['type'] == 'table'"
              :class="[
                'field-container',
                {
                  'field-selected':
                    childFieldIsSelected('header', 'table', hi) && selectedStylingSection.name === 'Table',
                },
              ]"
              :style="[h['CSSStyles']]"
              :renderif="h['conditionalRendering']['renderif']"
              :loopon="h.looping.loopon"
            >
              <thead v-if="h['tableDetails']['header']" class="field-inner-container">
                <tr
                  :style="h['tableDetails']['header']['CSSStyles']"
                  :class="[
                    'field-inner-container',
                    {
                      'field-selected':
                        childFieldIsSelected('header', 'tableheadrow', hi) && selectedStylingSection.name === 'Row',
                    },
                  ]"
                >
                  <th
                    v-for="(hc, hci) in h['tableDetails']['header']['cell']"
                    :key="hci"
                    :class="[
                      'field-inner-container',
                      {
                        'field-selected':
                          fieldIsSelected('header', 'tablehead', hi, hci) && selectedStylingSection.name === 'Cell',
                      },
                    ]"
                    :style="{ ...hc['CSSStyles'], width: h.tableDetails.header.columnWidths[hci] }"
                    :colspan="hc['colSpan']"
                    @click="fieldClicked($event, 'header', 'tablehead', hi, hci)"
                  >
                    <img
                      v-if="hc['isImage']"
                      :src="hc['value']"
                      class="field-content"
                      :style="{ width: '100%', objectFit: 'contain' }"
                    />
                    <div v-else v-html="hc['value']" class="field-content"></div>
                  </th>
                </tr>
              </thead>
              <tbody class="field-inner-container">
                <tr
                  v-for="(r, ri) in h['tableDetails']['row']"
                  :key="ri"
                  :style="r['CSSStyles']"
                  :class="[
                    'field-inner-container',
                    {
                      'field-selected':
                        childFieldIsSelected('header', 'tablebodyrow', hi, ri) && selectedStylingSection.name === 'Row',
                    },
                  ]"
                >
                  <td
                    v-for="(c, ci) in r['cell']"
                    :key="ci"
                    :class="[
                      'field-inner-container',
                      {
                        'field-selected':
                          fieldIsSelected('header', 'tablebody', hi, ri, ci) && selectedStylingSection.name === 'Cell',
                      },
                    ]"
                    :style="{ ...c['CSSStyles'], width: r.columnWidths[ci] }"
                    :colspan="c['colSpan']"
                    @click="fieldClicked($event, 'header', 'tablebody', hi, ri, ci)"
                  >
                    <img
                      v-if="c['isImage']"
                      :src="c['value']"
                      class="field-content"
                      :style="{ width: '100%', objectFit: 'contain' }"
                    />
                    <div v-else v-html="c['value']" class="field-content"></div>
                  </td>
                </tr>
              </tbody>
              <tfoot v-if="h['tableDetails']['footer']" class="field-inner-container">
                <tr
                  :style="h['tableDetails']['footer']['CSSStyles']"
                  :class="[
                    'field-inner-container',
                    {
                      'field-selected':
                        childFieldIsSelected('header', 'tablefootrow', hi) && selectedStylingSection.name === 'Row',
                    },
                  ]"
                >
                  <td
                    v-for="(fc, fci) in h['tableDetails']['footer']['cell']"
                    :key="fci"
                    :class="[
                      'field-inner-container',
                      {
                        'field-selected':
                          fieldIsSelected('header', 'tablefoot', hi, fci) && selectedStylingSection.name === 'Cell',
                      },
                    ]"
                    :style="{ ...fc['CSSStyles'], width: h.tableDetails.footer.columnWidths[fci] }"
                    :colspan="fc['colSpan']"
                    @click="fieldClicked($event, 'header', 'tablefoot', hi, fci)"
                  >
                    <img
                      v-if="fc['isImage']"
                      :src="fc['value']"
                      class="field-content"
                      :style="{ width: '100%', objectFit: 'contain' }"
                    />
                    <div v-else v-html="fc['value']" class="field-content"></div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </header>
        <div
          class="page-container"
          v-for="(p, i) in pages"
          :key="i"
          ref="page"
          @click="pageClicked($event, 'content', i)"
          :style="{ height: `${contentHeight}mm`, width: `${contentWidth}mm`, ...pagesStyle }"
        >
          <div v-for="(f, fi) in p['fields']" :key="fi">
            <div
              v-if="f['type'] == 'string'"
              :class="['field-container', { 'field-selected': fieldIsSelected('content', 'string', i, fi) }]"
              :style="[f['CSSStyles']]"
              :renderif="f['conditionalRendering']['renderif']"
              :loopon="f.looping.loopon"
              @click="fieldClicked($event, 'content', 'string', i, fi)"
            >
              <img
                v-if="f['isImage']"
                :src="f['value']"
                class="field-content"
                :style="{ width: '100%', objectFit: 'contain' }"
              />
              <div v-else v-html="f['value']" class="field-content"></div>
            </div>
            <table
              v-else-if="f['type'] == 'table'"
              :class="[
                'field-container',
                {
                  'field-selected':
                    childFieldIsSelected('content', 'table', i, fi) && selectedStylingSection.name === 'Table',
                },
              ]"
              :style="[f['CSSStyles']]"
              :renderif="f['conditionalRendering']['renderif']"
              :loopon="f.looping.loopon"
            >
              <thead v-if="f['tableDetails']['header']" class="field-inner-container">
                <tr
                  :style="f['tableDetails']['header']['CSSStyles']"
                  :class="[
                    'field-inner-container',
                    {
                      'field-selected':
                        childFieldIsSelected('content', 'tableheadrow', i, fi) && selectedStylingSection.name === 'Row',
                    },
                  ]"
                >
                  <th
                    v-for="(hc, hci) in f['tableDetails']['header']['cell']"
                    :key="hci"
                    :class="[
                      'field-inner-container',
                      {
                        'field-selected':
                          fieldIsSelected('content', 'tablehead', i, fi, hci) && selectedStylingSection.name === 'Cell',
                      },
                    ]"
                    :style="{ ...hc['CSSStyles'], width: f.tableDetails.header.columnWidths[hci] }"
                    :colspan="hc['colSpan']"
                    @click="fieldClicked($event, 'content', 'tablehead', i, fi, hci)"
                  >
                    <img
                      v-if="hc['isImage']"
                      :src="hc['value']"
                      class="field-content"
                      :style="{ width: '100%', objectFit: 'contain' }"
                    />
                    <div v-else v-html="hc['value']" class="field-content"></div>
                  </th>
                </tr>
              </thead>
              <tbody class="field-inner-container">
                <tr
                  v-for="(r, ri) in f['tableDetails']['row']"
                  :key="ri"
                  :style="r['CSSStyles']"
                  :class="[
                    'field-inner-container',
                    {
                      'field-selected':
                        childFieldIsSelected('content', 'tablebodyrow', i, fi, ri) &&
                        selectedStylingSection.name === 'Row',
                    },
                  ]"
                >
                  <td
                    v-for="(c, ci) in r['cell']"
                    :key="ci"
                    :class="[
                      'field-inner-container',
                      {
                        'field-selected':
                          fieldIsSelected('content', 'tablebody', i, fi, ri, ci) &&
                          selectedStylingSection.name === 'Cell',
                      },
                    ]"
                    :style="{ ...c['CSSStyles'], width: r.columnWidths[ci] }"
                    :colspan="c['colSpan']"
                    @click="fieldClicked($event, 'content', 'tablebody', i, fi, ri, ci)"
                  >
                    <img
                      v-if="c['isImage']"
                      :src="c['value']"
                      class="field-content"
                      :style="{ width: '100%', objectFit: 'contain' }"
                    />
                    <div v-else v-html="c['value']" class="field-content"></div>
                  </td>
                </tr>
              </tbody>
              <tfoot v-if="f['tableDetails']['footer']" class="field-inner-container">
                <tr
                  :style="f['tableDetails']['footer']['CSSStyles']"
                  :class="[
                    'field-inner-container',
                    {
                      'field-selected':
                        childFieldIsSelected('content', 'tablefootrow', i, fi) && selectedStylingSection.name === 'Row',
                    },
                  ]"
                >
                  <td
                    v-for="(fc, fci) in f['tableDetails']['footer']['cell']"
                    :key="fci"
                    :class="[
                      'field-inner-container',
                      {
                        'field-selected':
                          fieldIsSelected('content', 'tablefoot', i, fi, fci) && selectedStylingSection.name === 'Cell',
                      },
                    ]"
                    :style="{ ...fc['CSSStyles'], width: f.tableDetails.footer.columnWidths[fci] }"
                    :colspan="fc['colSpan']"
                    @click="fieldClicked($event, 'content', 'tablefoot', i, fi, fci)"
                  >
                    <img
                      v-if="fc['isImage']"
                      :src="fc['value']"
                      class="field-content"
                      :style="{ width: '100%', objectFit: 'contain' }"
                    />
                    <div v-else v-html="fc['value']" class="field-content"></div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
        <footer class="page-footer" ref="pageFooter" style="visibility:visible" :style="footerStyle" @click="pageClicked($event, 'footer')">
          <div v-for="(f, fi) in footer" :key="fi">
            <div
              v-if="f['type'] == 'string'"
              :class="['field-container', { 'field-selected': fieldIsSelected('footer', 'string', fi) }]"
              :style="[f['CSSStyles']]"
              :renderif="f['conditionalRendering']['renderif']"
              :loopon="f.looping.loopon"
              @click="fieldClicked($event, 'footer', 'string', fi)"
            >
              <img
                v-if="f['isImage']"
                :src="f['value']"
                class="field-content"
                :style="{ width: '100%', objectFit: 'contain' }"
              />
              <div v-else v-html="f['value']" class="field-content"></div>
            </div>
            <table
              v-else-if="f['type'] == 'table'"
              :class="[
                'field-container',
                {
                  'field-selected':
                    childFieldIsSelected('footer', 'table', fi) && selectedStylingSection.name === 'Table',
                },
              ]"
              :style="[f['CSSStyles']]"
              :renderif="f['conditionalRendering']['renderif']"
              :loopon="f.looping.loopon"
            >
              <thead v-if="f['tableDetails']['header']" class="field-inner-container">
                <tr
                  :style="f['tableDetails']['header']['CSSStyles']"
                  :class="[
                    'field-inner-container',
                    {
                      'field-selected':
                        childFieldIsSelected('footer', 'tableheadrow', fi) && selectedStylingSection.name === 'Row',
                    },
                  ]"
                >
                  <th
                    v-for="(fc, fci) in f['tableDetails']['header']['cell']"
                    :key="fci"
                    :class="[
                      'field-inner-container',
                      {
                        'field-selected':
                          fieldIsSelected('footer', 'tablehead', fi, fci) && selectedStylingSection.name === 'Cell',
                      },
                    ]"
                    :style="{ ...fc['CSSStyles'], width: f.tableDetails.header.columnWidths[fci] }"
                    :colspan="fc['colSpan']"
                    @click="fieldClicked($event, 'footer', 'tablehead', fi, fci)"
                  >
                    <img
                      v-if="fc['isImage']"
                      :src="fc['value']"
                      class="field-content"
                      :style="{ width: '100%', objectFit: 'contain' }"
                    />
                    <div v-else v-html="fc['value']" class="field-content"></div>
                  </th>
                </tr>
              </thead>
              <tbody class="field-inner-container">
                <tr
                  v-for="(r, ri) in f['tableDetails']['row']"
                  :key="ri"
                  :style="r['CSSStyles']"
                  :class="[
                    'field-inner-container',
                    {
                      'field-selected':
                        childFieldIsSelected('footer', 'tablebodyrow', fi, ri) && selectedStylingSection.name === 'Row',
                    },
                  ]"
                >
                  <td
                    v-for="(c, ci) in r['cell']"
                    :key="ci"
                    :class="[
                      'field-inner-container',
                      {
                        'field-selected':
                          fieldIsSelected('footer', 'tablebody', fi, ri, ci) && selectedStylingSection.name === 'Cell',
                      },
                    ]"
                    :style="{ ...c['CSSStyles'], width: r.columnWidths[ci] }"
                    :colspan="c['colSpan']"
                    @click="fieldClicked($event, 'footer', 'tablebody', fi, ri, ci)"
                  >
                    <img
                      v-if="c['isImage']"
                      :src="c['value']"
                      class="field-content"
                      :style="{ width: '100%', objectFit: 'contain' }"
                    />
                    <div v-else v-html="c['value']" class="field-content"></div>
                  </td>
                </tr>
              </tbody>
              <tfoot v-if="f['tableDetails']['footer']" class="field-inner-container">
                <tr
                  :style="f['tableDetails']['footer']['CSSStyles']"
                  :class="[
                    'field-inner-container',
                    {
                      'field-selected':
                        childFieldIsSelected('footer', 'tablefootrow', fi) && selectedStylingSection.name === 'Row',
                    },
                  ]"
                >
                  <td
                    v-for="(fc, fci) in f['tableDetails']['footer']['cell']"
                    :key="fci"
                    :class="[
                      'field-inner-container',
                      {
                        'field-selected':
                          fieldIsSelected('footer', 'tablefoot', fi, fci) && selectedStylingSection.name === 'Cell',
                      },
                    ]"
                    :style="{ ...fc['CSSStyles'], width: f.tableDetails.footer.columnWidths[fci] }"
                    :colspan="fc['colSpan']"
                    @click="fieldClicked($event, 'footer', 'tablefoot', fi, fci)"
                  >
                    <img
                      v-if="fc['isImage']"
                      :src="fc['value']"
                      class="field-content"
                      :style="{ width: '100%', objectFit: 'contain' }"
                    />
                    <div v-else v-html="fc['value']" class="field-content"></div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </footer>
      </div>
    </div>
    <div v-if="showHeightMeasurer" class="height-measurer" v-html="heightMeasurerHTML" ref="heightMeasurer"></div>
    <!-- used to calculate the margin needed to leave for header and footer, fields in the header and footer can be repeated using field key and table key, and thus the margin needed may change based on the data-->
    <div
      v-if="showPDFViewer"
      class="pdf-viewer-container"
      :style="{ width: viewMode ? '100%' : '', marginLeft: viewMode ? '0' : '5px' }"
    >
      <div class="template-breadcrumbs">{{ breadcrumbs }}</div>
      <iframe :src="PDFViewerURL" frameborder="0" height="100%" width="100%"></iframe>
    </div>

    <PopupV2
      :show="showLoadPopup"
      @buttonClicked="showLoadPopup = false"
      title="Load Report"
      :buttons="loadPopupButtons"
    >
      <div class="report-listing-body">
        <div v-if="reportListing.length > 0">Please select a report you want to load</div>
        <div v-else>There are no available report to load</div>
        <div class="listing-content">
          <div v-for="(r, ri) in reportListing" :key="ri" class="report-listing-container">
            <div class="report-filename" @click="loadReportTemplate(r.Name, r.Folder)"
              >{{ r.Folder }} / {{ r.Name }}</div
            >
            <img
              @click="deleteReportClicked(r.Name, r.Folder)"
              src="https://dsmlib.logistics.systems/Icons/Delete.png"
            />
          </div>
        </div>
      </div>
    </PopupV2>

    <PopupV2
      class="save-popup"
      :show="showSavePopup"
      @buttonClicked="savePopupButtonClicked"
      title="Save Report"
      :buttons="savePopupButtons"
    >
      <Dropdown
        class="folder-dropdown"
        :items="saveReportFolderDropdownItems"
        itemKey="folder"
        placeholder="Report Folder"
        :isRequired="true"
        :selectedObject.sync="saveReportFolderSelectedObject"
        :errorMessage.sync="saveReportFolderError"
      />
      <Input :inputValue.sync="saveReportName" placeholder="Report Name" :errorMessage.sync="saveReportNameError" />
    </PopupV2>

    <PopupV2
      class="share-popup"
      :show="showSharePopup"
      icon="https://dsmlib.logistics.systems/Icons/Share.png"
      title="Share"
      :buttons="sharePopupButtons"
      @buttonClicked="sharePopupButtonClicked"
    >
      <div class="popup-content">
        <div class="anchor-container">
          <a :href="sharePopupEmailLink" target="_blank">
            <div class="anchor-content email">
              <img src="https://dsmlib.logistics.systems/Icons/WhiteMail.png" />
              Email
            </div>
          </a>
          <a :href="sharePopupWhatsAppLink" target="_blank">
            <div class="anchor-content whatsapp">
              <img src="https://dsmlib.logistics.systems/Icons/WhiteWhatsapp.png" />
              WhatsApp
            </div>
          </a>
          <a :href="sharePopupTelegramLink" target="_blank">
            <div class="anchor-content telegram">
              <img src="https://dsmlib.logistics.systems/Icons/Telegram.png" />
              Telegram
            </div>
          </a>
        </div>
        <div class="copy-link">
          <input type="text" :value="sharePopupLink" disabled />
          <button @click="sharePopupCopyClicked"><img src="https://dsmlib.logistics.systems/Icons/Copy.png" /></button>
        </div>
        <span v-if="showCopiedNotification">Copied to Clipboard</span>
      </div>
    </PopupV2>

    <div v-if="showLoadingOverlay" class="loading-overlay">
      <div class="loading-overlay-content">{{ loadingOverlayContent }}</div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { PDFDocument } from 'pdf-lib';
import JSZip from 'jszip';

// =============== TODO:
// /Master/GetSettingsSystem {"settingsitem", "6G_ReportFolder"} ✅
//  reportFolders prop -> SettingsSystem -> CoRefname -> "General" ✅
// change template listing to display all reports for that company and the company folder (if exists) ✅
// breadcrumbs above pdf viewer ✅
// batch printing ✅ inefficient method, might have to find a way to improve in the future
// pass in background image to getPDFFromJsreport method ⏳
// show first breadcrumb if batch printing ❓
// shared data ✅
// checkout to master first before return or updating anything from the controller ✅
// fix background image bug (bug is with jsreport)⏳
// add background image to report template ⏳
// finish implementing all necessary props ✅
// pass folder path to SaveReport ✅ and DeleteReport ✅
// tooltip when trying to save in development mode ✅
// disallow saving in dsmtest as well ⏳ may have to use an .env file
// separate styling input field values from the actual styling applied to the HTML elements into separate objects ✅
// clear off blob, reportPath, selectedFields, and all that stuff in clearTemplateViewer method ⏳
// fix bug where the entire page gets moved down if first field in page has a top margin ✅
// fix the weird padding/margin bug with images, test with https://dsmapi.logistics.systems/logo/CoHeaderFin_TS.png ✅ removed height: 100% from images
// auto select all text content upon field clicked ✅
// hardcoded the password in server git remote for DesignerV3Reports, not advisable, find a workaround for this, https://stackoverflow.com/a/10054470
// in the column sizing boxcontainer, highlight the one that is selected, based on the cell
// fix the double border issue with tables, the border is being inherited by the div containing the actual content, try to adjust css first ✅
// fix the page height being incorrectly calculated when loading certain templates in a certain order, haven't figured out the pattern yet
// test by loading DSM/Quotation, then General/General Invoice, and then DSM/Quotation again
// incorrect breadcrumbs if different report templates are combined
// deal with all =============== stuff ⏳

// DeliveryNote
// JobSheet
// LOI

export default {
  components: {},
  data: function () {
    return {
      userDetails: null,
      companyReportFolder: '',

      // ============== maybe declare this as a const instead of putting it into data
      currentTemplateVersion: 5, // used to record the latest template version, this number will be stored in the report templates
      // this number needs to be incremented everytime there is a change in the expected report template format
      // if the template being loaded is using an outdated format, will need to convert it to the latest format before rendering and generating the report

      reportPaperList: [
        { name: 'A4', height: 297, width: 210 },
        { name: 'A4Landscape', height: 210, width: 297 },
      ],
      selectedPaper: { name: 'A4', height: 297, width: 210 },

      header: [],
      headerHeight: 0, // ========== only used when designing the report, not sent to jsreport when rendering
      headerStyle: {
        paddingTop: '0mm',
        paddingRight: '0mm',
        paddingBottom: '0mm',
        paddingLeft: '0mm',
      },

      pages: [],
      pagesRatio: [],
      pagesStyle: {
        paddingTop: '0mm',
        paddingRight: '0mm',
        paddingBottom: '0mm',
        paddingLeft: '0mm',
      },

      footer: [],
      footerHeight: 0, // ========== only used when designing the report, not sent to jsreport when rendering
      footerStyle: {
        paddingTop: '0mm',
        paddingRight: '0mm',
        paddingBottom: '0mm',
        paddingLeft: '0mm',
      },
      footerPreserveSpace: false,

      backgroundImage: '', // background image path stored in the report template, IS NOT NECESSARILY THE SAME AS THE PROPS
      reportPath: '',

      fontFamilyListing: [
        { name: 'Times New Roman', value: '"Times New Roman", Times, serif' },
        { name: 'Arial', value: 'Arial, Helvetica, sans-serif' },
        { name: 'inherit', value: 'inherit' },
      ],
      selectedFieldFontFamily: null,

      selectedPageIndex: -1, // should only be -1 if there is no page added, else it should always be a valid page index
      selectedField: null, // changes based on what the user clicked, and what styling section is selected
      selectedFieldID: null,

      selectedFieldIsConditionallyRendered: false,
      selectedFieldConditionalRenderingDataKey: '',
      conditionalRenderingConditionItems: [{ name: 'Truthy' }, { name: 'Falsy' }],
      selectedFieldConditionalRenderingCondition: null,
      conditionalRenderingOperatorItems: [{ name: 'AND' }, { name: 'OR' }],
      selectedFieldConditionalRenderingOperator: null,
      conditionalRenderingOnPagesItems: [
        { name: 'All Pages' },
        { name: 'First Page' },
        { name: 'Last Page' },
        { name: 'Odd Pages' },
        { name: 'Even Pages' },
      ],
      selectedFieldConditionalRenderingOnPages: null,
      selectedFieldConditionalRenderingPreserveSpace: false,

      selectedFieldIsImage: false,
      selectedFieldTextContent: '',

      // ================ reorganise later
      selectedFieldIsLooped: false,
      selectedFieldLoopingFieldKey: '',
      selectedFieldLoopingMarginInBetween: '',
      selectedFieldLoopingDepthLevels: [],

      selectedFieldColumnSizing: [],

      stylingSectionItems: [{ name: 'Table' }, { name: 'Row' }, { name: 'Cell' }],
      selectedStylingSection: { name: 'Table' },

      reportSections: [{ name: 'Header' }, { name: 'Content' }, { name: 'Footer' }],
      selectedReportSection: { name: 'Content' },
      reportSectionErrorMessage: '',
      reportSectionStylingPaddingTopInputValue: '0mm',
      reportSectionStylingPaddingRightInputValue: '0mm',
      reportSectionStylingPaddingBottomInputValue: '0mm',
      reportSectionStylingPaddingLeftInputValue: '0mm',

      borderStyles: [
        { style: 'solid' },
        { style: 'dashed' },
        { style: 'dotted' },
        { style: 'double' },
        { style: 'inherit' },
      ],
      selectedFieldBorderStyle: null,

      PDFBlobWithBackground: null, // blob object
      PDFBlobURLWithBackground: '', // URL pointing to blob

      PDFBlobWithoutBackground: null, // blob object
      PDFBlobURLWithoutBackground: '', // URL pointing to blob

      // PDFBlobURLWithoutBackgroundArray: [],
      ZipBlob: null, // blob object
      ZipBlobURL: '', // URL pointing to blob
      ZipFileName: '',

      showReportPreview: false, // show/hide the report renderer (the one shown when designing a report)

      showHeightMeasurer: false,
      heightMeasurerHTML: '',

      showPDFViewer: false, // show/hide the iframe containing pdfjs (the mozilla PDF viewer)
      PDFViewerURL: '',
      PDFViewerSignalID: '',
      PDFViewerSignalHub: null,

      //#region Load Popup Data
      showLoadPopup: false,

      reportListing: [],
      loadPopupButtons: [{ text: 'Close', textColor: 'white', color: 'gray' }],

      showSavePopup: false,
      savePopupButtons: [{ text: 'Save' }, { text: 'Cancel', textColor: 'white', color: 'gray' }],
      saveReportFolderSelectedObject: null,
      saveReportFolderError: '',
      saveReportName: '',
      saveReportNameError: '',
      //#endregion

      showSharePopup: false,
      sharePopupButtons: [{ text: 'Close' }],
      sharePopupLink: '',
      showCopiedNotification: false,

      //#region loading overlay
      // reason for not using $fullscreenLoading:
      // $fullscreenLoading controls the global loader, which means as a component, it will affect the loading overlay of the parent page
      // which can lead to unwanted behaviours
      showLoadingOverlay: true,
      loadingOverlayContent: 'Loading Report',
      //#endregion
    };
  },
  computed: {
    // Body height
    contentHeight: function () {
      // Return the default height minus with header height and footer height
      // return 297 - this.headerHeight - this.footerHeight; // ========== change later when there are other paper types
      return this.selectedPaper['height'] - this.headerHeight - this.footerHeight;
    },
    // Body width
    contentWidth: function () {
      // return 210; // ========== change later when there are other paper types
      return this.selectedPaper['width'];
    },
    reportSectionIsSelected: function () {
      // because the isDisabled prop on Input expects a Boolean, not an object, and i don't wish to cast in the template
      return Boolean(this.selectedReportSection);
    },
    reportSectionStylingTitle: function () {
      if (!this.selectedReportSection) {
        return '';
      }

      return `${this.selectedReportSection.name} Padding`; // only padding is supported now, change the title if more styling properties are supported in the future
    },
    selectedFieldIsInHeaderOrFooter: function () {
      // page based conditional rendering only works on fields that are in the header or footer, similar to page numbering
      // jsreport doesn't provide page info when rendering content fields

      if (!this.selectedFieldID) {
        return false;
      }

      return ['header', 'footer'].includes(this.getReportSectionOfSelectedField());
    },
    selectedFieldIsTable: function () {
      if (!this.selectedFieldID) {
        return false;
      }

      return ['tablehead', 'tablebody', 'tablefoot'].includes(this.getFieldTypeOfSelectedField());
    },
    selectedTableFieldHasHeader: function () {
      if (!this.selectedFieldIsTable) {
        return false;
      }

      return Boolean(this.getSelectedField().tableDetails.header);
    },
    selectedTableFieldHasFooter: function () {
      if (!this.selectedFieldIsTable) {
        return false;
      }

      return Boolean(this.getSelectedField().tableDetails.footer);
    },
    selectedTableFieldCellIsTheLeftmostCell: function () {
      if (!this.selectedFieldIsTable) {
        return false;
      }

      return this.selectedFieldID.indexes.cell === 0;
    },
    mergeLeftButtonTitle: function () {
      if (!this.selectedTableFieldCellIsTheLeftmostCell) {
        return '';
      }

      return 'This is the leftmost cell';
    },
    selectedTableFieldCellIsTheRightmostCell: function () {
      if (!this.selectedFieldIsTable) {
        return false;
      }

      let selectedRow;

      const { reportSection } = this.selectedFieldID;
      switch (reportSection) {
        case 'header': {
          const { field: fieldIndex } = this.selectedFieldID.indexes;
          selectedRow = this.header[fieldIndex];
          break;
        }

        case 'content': {
          const { page: pageIndex, field: fieldIndex } = this.selectedFieldID.indexes;
          selectedRow = this.pages[pageIndex].fields[fieldIndex];
          break;
        }

        case 'footer': {
          const { field: fieldIndex } = this.selectedFieldID.indexes;
          selectedRow = this.footer[fieldIndex];
          break;
        }
      }

      selectedRow = selectedRow.tableDetails;

      const { fieldType } = this.selectedFieldID;
      switch (fieldType) {
        case 'tablehead':
          selectedRow = selectedRow.header;
          break;

        case 'tablebody': {
          const { row: rowIndex } = this.selectedFieldID.indexes;
          selectedRow = selectedRow.row[rowIndex];
          break;
        }

        case 'tablefoot':
          selectedRow = selectedRow.footer;
          break;
      }

      const { cell: cellIndex } = this.selectedFieldID.indexes;
      return cellIndex === selectedRow.cell.length - 1;
    },
    mergeRightButtonTitle: function () {
      if (!this.selectedTableFieldCellIsTheRightmostCell) {
        return '';
      }

      return 'This is the rightmost cell';
    },
    selectedTableFieldCellIsMergedCell: function () {
      if (!this.selectedFieldIsTable) {
        return false;
      }

      let selectedCell;

      const { reportSection } = this.selectedFieldID;
      switch (reportSection) {
        case 'header': {
          const { field: fieldIndex } = this.selectedFieldID.indexes;
          selectedCell = this.header[fieldIndex];
          break;
        }

        case 'content': {
          const { page: pageIndex, field: fieldIndex } = this.selectedFieldID.indexes;
          selectedCell = this.pages[pageIndex].fields[fieldIndex];
          break;
        }

        case 'footer': {
          const { field: fieldIndex } = this.selectedFieldID.indexes;
          selectedCell = this.footer[fieldIndex];
          break;
        }
      }

      selectedCell = selectedCell.tableDetails;

      const { fieldType } = this.selectedFieldID;
      switch (fieldType) {
        case 'tablehead':
          selectedCell = selectedCell.header;
          break;

        case 'tablebody': {
          const { row: rowIndex } = this.selectedFieldID.indexes;
          selectedCell = selectedCell.row[rowIndex];
          break;
        }

        case 'tablefoot':
          selectedCell = selectedCell.footer;
          break;
      }

      const { cell: cellIndex } = this.selectedFieldID.indexes;
      selectedCell = selectedCell.cell[cellIndex];

      return selectedCell.colSpan === 1;
    },
    splitLeftRightButtonTitle: function () {
      if (!this.selectedTableFieldCellIsMergedCell) {
        return '';
      }

      return 'This is not a merged cell';
    },
    selectedFieldCanBePositioned: function () {
      // position and margin controls should only be shown when string or table is selected
      // should not be shown when table row or table cell are selected, because the position of those depend on where the table is located

      if (!this.selectedFieldID) {
        return false;
      }

      return (
        this.selectedFieldID.fieldType === 'string' ||
        ((this.selectedFieldID.fieldType === 'tablehead' ||
          this.selectedFieldID.fieldType === 'tablebody' ||
          this.selectedFieldID.fieldType === 'tablefoot') &&
          this.selectedStylingSection.name === 'Table')
      );
    },
    saveReportFolderDropdownItems: function () {
      const folders = [];

      if (this.companyReportFolder) {
        folders.push({ folder: this.companyReportFolder });
      }

      if (this.userDetails?.CoRefname) {
        folders.push({ folder: this.userDetails.CoRefname });

        if (this.userDetails.CoRefname === 'DSM') {
          // ONLY IF the user is logged in to DSM
          folders.push({ folder: 'General' }); // allow saves to General
        }
      }
      return folders;
    },
    breadcrumbs: function () {
      return this.reportPath?.replaceAll?.('/', ' / ') ?? '';
    },
    sharePopupEmailLink: function () {
      const subject = encodeURIComponent(this.shareBehaviour?.options?.subject);
      const body = encodeURIComponent(this.shareBehaviour?.options?.body?.replaceAll?.('{link}', this.sharePopupLink));
      return `mailto:?subject=${subject}&body=${body}`;
    },
    sharePopupWhatsAppLink: function () {
      const body = encodeURIComponent(this.shareBehaviour?.options?.body?.replaceAll?.('{link}', this.sharePopupLink));
      return `https://wa.me/?text=${body}`;
    },
    sharePopupTelegramLink: function () {
      const body = encodeURIComponent(this.shareBehaviour?.options?.body?.replaceAll?.('{link}', this.sharePopupLink));
      return `https://t.me/share/url?url=${body}`;
    },
    isDevelopmentMode: function () {
      return process.env['NODE_ENV'] === 'development';
    },
    saveReportButtonTitle: function () {
      return this.isDevelopmentMode ? 'Saving reports not allowed in development mode' : '';
    },
  },
  props: {
    viewMode: { type: Boolean, default: true }, // true = generate reports only | false = design and preview reports
    reportFiles: {
      // array of report files to generate a report from
      type: Array,
      default: function () {
        return null;
      },
    },
    reportFolders: {
      // (OPTIONAL) specific folders for each of the report files
      type: Array,
      default: function () {
        return null;
      },
    },
    reportData: {
      // data for each of the report files
      type: Array,
      default: function () {
        return null;
      },
    },
    sharedData: {
      // (OPTIONAL) common data among all the report files
      type: Object,
      default: function () {
        return null;
      },
    },

    downloadName: { type: String, default: 'Report' }, // default file name when the user downloads the PDF (without file extension)

    canToggleBackground: { type: Boolean, default: true }, // controls whether the button to show/hide background image is rendered or not
    showBackground: { type: Boolean, default: true }, // show/hide background image for the reports (use .sync)

    // is not necessary the same as the "backgroundImage" in data
    backgroundImageFiles: {
      // (OPTIONAL) image file names, will be prioritised over the image specified in the report template
      type: Array,
      default: function () {
        return null;
      },
    },
    backgroundImageFolders: {
      // (OPTIONAL) folders for each of the images provided
      type: Array,
      default: function () {
        return null;
      },
    },

    canShare: { type: Boolean, default: false }, // show/hide share button
    shareBehaviour: {
      type: Object,
      default: function () {
        return {
          behaviour: 'uploadAndPopup', // upload the report to dsmlib and show a standardised popup that allows the user to share via email, whatsapp and telegram
          options: {
            fileName: '', // if provided, needs to have the .pdf file extension; if not provided or left blank, it will default to the template name
            folder: '', // all reports are uploaded to the DesignerV3 folder, and will be placed in a subfolder named after the folder that the template was found in
            // specifying a folder name will create another folder within that subfolder

            body: `Your report is available at: {link}`, // {link} will be replaced with the dsmlib link to access the report
            subject: 'Report',
          },

          // behaviour: "uploadAndEmit", // upload the report to dsmlib and emit an event with the link to the report
          // options: {
          //   fileName: "", // if provided, needs to have the .pdf file extension; if not provided or left blank, it will default to the template name
          //   folder: "", // all reports are uploaded to the DesignerV3 folder, and will be placed in a subfolder named after the folder that the template was found in
          // specifying a folder name will create another folder within that subfolder
          // },

          // behaviour: "emitOnly" // emit an event with the report blob objects and their blob URLs
          // no options available for emitOnly
        };
      },
    },

    canClose: { type: Boolean, default: true }, // show/hide close button

    // events:
    // @closeClicked - fired when the close button is clicked (if it is shown), WILL NOT HIDE THIS COMPONENT AUTOMATICALLY, parent page has to hide using v-if or v-show
    // @printClicked - fired when the print button is clicked
    // @afterPrint - fired after the print dialog is closed (regardless if the print was successful or was cancelled)
    // @shareClicked - may or may not fire depending on shareBehaviour, values returned also differ based on shareBehaviour
  },
  methods: {
    // Adding a page the current designer
    // =============== maybe separate into multiple functions
    addPageClicked: async function (page) {
      if (page) {
        // If to page is not null or undefined
        // Add the page into the pages (for loading previous data)
        this.pages.push(page);
      } else {
        //select paper size if first time adding page
        if (this.pages.length === 0) {
          const ansPaperSize = await this.$showAlert('info', 'Select Paper Size', 'Select a paper size for this template', 
            [ { text: 'A4' }, { text: 'A4Landscape'}, { text: 'Cancel', color: 'lightgray' } ]
          );
          if (ansPaperSize == 'Cancel') {
            return;
          }
          this.selectedPaper = this.reportPaperList.getSelected(ansPaperSize, 'name');
        }
        // Init a new page
        this.pages.push({
          fields: [],
        });
      }

      if (this.selectedPageIndex === -1) {
        // if this is the first page being added, or the selected page index was somehow cleared
        this.selectedPageIndex = this.pages.length - 1; // set it to the last page
      }

      this.$nextTick(() => {
        // Get all pages
        const allPages = Array.from(this.$refs['page']);

        // Get the new page element
        const newPage = allPages[allPages.length - 1];

        // Get the new page ratio and push into pagesRatio data
        if (this.showReportPreview) {
          this.pagesRatio.push(this.getPageRatio(newPage.getBoundingClientRect()));
        } else {
          this.showReportPreview = true;
          this.$nextTick(() => {
            this.pagesRatio.push(this.getPageRatio(newPage.getBoundingClientRect()));
            this.showReportPreview = false;
          });
        }
      });
    },
    parseAndProcessHTMLTemplate: function (HTMLElement, data) {
      // parses the HTML template, repeats the fields or table rows as necessary depending on the looping configuration and data provided
      // calls replaceMustaches to replace mustaches with their appropriate values
      // this used to be done by jsreport with helper functions, regex patterns and string processing
      // but was moved here for easier maintenance and to enable measurement of the margin needed in header and footer if any of the fields in the header or footer are looped on

      const clonedHTMLElement = HTMLElement.cloneNode(true); // clone before making any changes, so that changes don't affect the actual DOM
      for (const fieldElement of clonedHTMLElement.children) {
        const fieldContainer = fieldElement.firstElementChild; // div or table element, this element is the one with the field-container class

        const renderifParams = fieldContainer.getAttribute('renderif').split(',');
        if ([3, 5].includes(renderifParams.length) && !renderifParams.some((param) => param === '')) {
          // there should be exactly 3 or 5 params for conditional rendering, and none of them should be empty strings
          const [dataKey, condition, preserveSpace] = renderifParams;
          let render;
          switch (condition) {
            case 'Truthy': // render if a certain key is truthy
              render = Boolean(data[dataKey]);
              break;

            case 'Falsy': // render if a certain key is falsy
              render = !data[dataKey];
              break;
          }

          if (renderifParams.length === 3 && !render) {
            this.hideHTMLElement(fieldContainer, preserveSpace);
            continue; // move on to the next field, no need to process this field if it doesn't need to be rendered
          }

          if (renderifParams.length === 5) {
            const operator = renderifParams[3];

            if (operator === 'AND' && !render) {
              // short circuit evaluation, if the first condition isn't fulfilled, then there's no need to check onPages
              this.hideHTMLElement(fieldContainer, preserveSpace);
              continue; // move on to the next field, no need to process this field if it doesn't need to be rendered
            }

            const onPages = renderifParams[4].split(' ')[0].toLowerCase(); // only send the first word to jsreport (Odd, Even, First, Last)
            if (((operator === 'AND' && render) || (operator === 'OR' && !render)) && onPages !== 'all') {
              this.markForJsreportOnPageCheck(fieldContainer, preserveSpace, onPages);
            }
          }
        }

        switch (fieldContainer.localName) {
          case 'div': // text field
            {
              const loopOn = fieldContainer.getAttribute('loopon');
              if (!loopOn) {
                // field is not looped
                fieldContainer.innerHTML = this.replaceMustaches(fieldContainer.innerHTML, data);
                break;
              }

              const [fieldKey, marginInBetween] = loopOn.split(',');
              if (!Array.isArray(data[fieldKey])) {
                // can't repeat the text field
                fieldContainer.innerHTML = this.replaceMustaches(fieldContainer.innerHTML, data);
                break;
              }

              const fieldTemplate = fieldContainer.cloneNode(true);
              fieldElement.innerHTML = '';
              data[fieldKey].forEach((fieldData, index, array) => {
                const fieldTemplateWithAdjustedMargins = this.adjustFieldMarginsForFieldKeyLooping(
                  fieldTemplate,
                  index,
                  array,
                  marginInBetween,
                );
                fieldElement.innerHTML += this.replaceMustaches(fieldTemplateWithAdjustedMargins.outerHTML, fieldData);
              });
            }
            break;

          case 'table': // table field
            {
              const loopOn = fieldContainer.getAttribute('loopon');
              if (!loopOn) {
                // field is not looped
                fieldElement.innerHTML = this.replaceMustaches(fieldElement.innerHTML, data);
                break;
              }

              const [fieldKeyDetails, ...depthLevels] = loopOn.split(';');
              const [fieldKey, marginInBetween] = fieldKeyDetails.split(',');
              const fieldKeyIsInvalid = fieldKey === '' || !Array.isArray(data[fieldKey]);

              const tbodyElementIndex = Array.from(fieldContainer.children).findIndex(
                (element) => element.localName === 'tbody',
              );
              if (tbodyElementIndex === -1) {
                // could not find tbody element in table field for some reason, this should never happen
                // tbody is supposed to always be there even though there are no rows in the table

                if (fieldKeyIsInvalid) {
                  fieldElement.innerHTML = this.replaceMustaches(fieldElement.innerHTML, data);
                } else {
                  const fieldTemplate = fieldContainer.cloneNode(true);
                  fieldElement.innerHTML = '';
                  data[fieldKey].forEach((fieldData, index, array) => {
                    const fieldTemplateWithAdjustedMargins = this.adjustFieldMarginsForFieldKeyLooping(
                      fieldTemplate,
                      index,
                      array,
                      marginInBetween,
                    );
                    fieldElement.innerHTML += this.replaceMustaches(
                      fieldTemplateWithAdjustedMargins.outerHTML,
                      fieldData,
                    );
                  });
                }
                break;
              }

              const tableBodyRows = Array.from(fieldContainer.children.item(tbodyElementIndex).children);
              const processedDepthLevels = depthLevels.map((depthLevel) => {
                const [depthKey, fromRow, toRow] = depthLevel.split(',');
                return {
                  depthKey,
                  fromRow: parseInt(fromRow),
                  toRow: parseInt(toRow),
                };
              });
              const depthLevelsAreInvalid =
                fieldKeyIsInvalid ?
                  this.depthLevelsAreInvalid(data, processedDepthLevels, tableBodyRows)
                : data[fieldKey].some((fieldData) =>
                    this.depthLevelsAreInvalid(fieldData, processedDepthLevels, tableBodyRows),
                  );

              if (fieldKeyIsInvalid && depthLevelsAreInvalid) {
                // can't loop on anything
                fieldElement.innerHTML = this.replaceMustaches(fieldElement.innerHTML, data);
                break;
              }

              if (fieldKeyIsInvalid) {
                // loop on depth levels only
                // process table body
                const processedTableBodyRows = this.parseAndProcessTableBodyRows(
                  tableBodyRows,
                  data,
                  processedDepthLevels,
                );
                fieldContainer.children.item(tbodyElementIndex).innerHTML = processedTableBodyRows;

                // process table header and footer
                fieldElement.innerHTML = this.replaceMustaches(fieldElement.innerHTML, data);
                break;
              }

              if (depthLevelsAreInvalid) {
                // loop on field key only
                const fieldTemplate = fieldContainer.cloneNode(true);
                fieldElement.innerHTML = '';
                data[fieldKey].forEach((fieldData, index, array) => {
                  const fieldTemplateWithAdjustedMargins = this.adjustFieldMarginsForFieldKeyLooping(
                    fieldTemplate,
                    index,
                    array,
                    marginInBetween,
                  );
                  fieldElement.innerHTML += this.replaceMustaches(
                    fieldTemplateWithAdjustedMargins.outerHTML,
                    fieldData,
                  );
                });
                break;
              }

              // loop on both field key and depth levels
              const fieldTemplate = fieldContainer.cloneNode(true); // clone the entire field-container div
              fieldElement.innerHTML = '';
              data[fieldKey].forEach((fieldData, index, array) => {
                const fieldTemplateWithAdjustedMargins = this.adjustFieldMarginsForFieldKeyLooping(
                  fieldTemplate,
                  index,
                  array,
                  marginInBetween,
                );

                // process table body
                const processedTableBodyRows = this.parseAndProcessTableBodyRows(
                  tableBodyRows,
                  fieldData,
                  processedDepthLevels,
                );
                fieldTemplateWithAdjustedMargins.children.item(tbodyElementIndex).innerHTML = processedTableBodyRows;

                // process table header and footer
                fieldElement.innerHTML += this.replaceMustaches(fieldTemplateWithAdjustedMargins.outerHTML, fieldData);
              });
            }
            break;
        }
      }

      return clonedHTMLElement;
    },
    hideHTMLElement: function (HTMLElement, preserveSpace) {
      switch (
        preserveSpace // preserveSpace is converted to a string when set as an attribute
      ) {
        case 'true':
          HTMLElement.style.visibility = 'hidden';
          break;

        case 'false':
          HTMLElement.hidden = true;
          break;
      }
    },
    markForJsreportOnPageCheck: function (HTMLElement, preserveSpace, onPages) {
      // sets a magic string for jsreport helper functions to find and replace
      // to show or hide certain fields depending on whether the on page condition is met or not
      switch (
        preserveSpace // preserveSpace is converted to a string when set as an attribute
      ) {
        case 'true': {
          HTMLElement.style.visibility = `var(--jsreporthide-${onPages})`;
          // need to place the magic string inside a CSS variable instead of just using the value literally
          // otherwise the styling property will be removed the next time the HTML element's style is mutated via JS
          // because the magic string is recognised as an invalid CSS value by JS
          break;
        }

        case 'false':
          HTMLElement.setAttribute(`jsreporthide-${onPages}`, 'hidden');
          break;
      }
    },
    adjustFieldMarginsForFieldKeyLooping: function (fieldElement, currentIndex, fieldDataArray, marginInBetween) {
      // modifies CSS properties of the field element passed in to insert margin in between each repeated field
      // but maintains the top and bottom margins on the first and last fields

      // clone the field element first to avoid mutating the original field element
      const clonedFieldElement = fieldElement.cloneNode(true);

      if (currentIndex === 0) {
        // first field in the series
        // apply usual top margin, replace usual bottom margin with margin in between specified
        clonedFieldElement.style.marginBottom = marginInBetween;
      } else if (currentIndex === fieldDataArray.length - 1) {
        // last field in the series
        // apply usual bottom margin, remove usual top margin
        clonedFieldElement.style.marginTop = '0mm';
      } else {
        // every other field in the series
        // remove usual top margin, replace usual bottom margin with margin in between specified
        clonedFieldElement.style.marginTop = '0mm';
        clonedFieldElement.style.marginBottom = marginInBetween;
      }

      return clonedFieldElement;
    },
    depthLevelsAreInvalid: function (data, depthLevels, tableBodyRows) {
      for (let index = 0; index < depthLevels.length; index++) {
        const { depthKey, fromRow, toRow } = depthLevels[index];

        if (depthKey === '') {
          return true;
        }

        if (isNaN(fromRow) || isNaN(toRow)) {
          return true;
        }

        if (toRow < fromRow) {
          return true;
        }

        if (index === 0) {
          continue;
        }

        // an inner depth level is supposed to be fully contained within the outer depth level
        const { fromRow: previousDepthFromRow, toRow: previousDepthToRow } = depthLevels[index - 1];
        if (fromRow < previousDepthFromRow || toRow > previousDepthToRow) {
          return true;
        }

        // an inner depth level cannot be exactly the same as the outer depth level
        if (fromRow === previousDepthFromRow && toRow === previousDepthToRow) {
          return true;
        }
      }

      const areAllArrays = this.allDepthKeyPropertiesAreArrays(
        data,
        depthLevels.map((depthLevel) => depthLevel.depthKey),
      );
      if (!areAllArrays) {
        return true;
      }

      // the logic at the top of this function checks if all inner depth levels are fully contained within their respective outer depth levels
      // with this assumption, the outermost depth level will have the widest row range
      const maxRowNumber = depthLevels[0].toRow;
      if (tableBodyRows.length < maxRowNumber) {
        // not enough rows for the depth level looping to work
        return true;
      }

      return false;
    },
    allDepthKeyPropertiesAreArrays: function (data, depthKeys) {
      const currentDepthData = data[depthKeys[0]];
      if (!Array.isArray(currentDepthData)) {
        return false;
      }

      if (depthKeys.length !== 1) {
        // not the deepest key
        const innerDepthPropertiesAreArrays = currentDepthData.every((depthData) =>
          this.allDepthKeyPropertiesAreArrays(depthData, depthKeys.slice(1)),
        );
        if (!innerDepthPropertiesAreArrays) {
          return false;
        }
      }

      return true;
    },
    parseAndProcessTableBodyRows: function (tableBodyRows, data, depthLevels) {
      // should only be used when valid depth levels are specified
      // if not, just replace mustaches in the entire table, it's simpler that way

      const generatedTableBodyRows = this.generateTableBodyRowsLoopedOn(tableBodyRows, data, depthLevels);

      const { fromRow: outermostDepthLevelsFromRow, toRow: outermostDepthLevelsToRow } = depthLevels[0];
      const tableBodyRowsHTML = tableBodyRows.map((tableBodyRow) => tableBodyRow.outerHTML);
      tableBodyRowsHTML.splice(
        outermostDepthLevelsFromRow - 1,
        outermostDepthLevelsToRow - outermostDepthLevelsFromRow + 1,
        generatedTableBodyRows,
      );
      return this.replaceMustaches(tableBodyRowsHTML.join(''), data);
    },
    generateTableBodyRowsLoopedOn: function (tableBodyRows, data, depthLevels) {
      // helper function
      // loop on depth levels and generates all the rows needed
      // ignores all rows that aren't looped on, doesn't even return them to the calling function

      const { depthKey, fromRow, toRow } = depthLevels[0];
      const tableBodyRowsToLoop = tableBodyRows.slice(fromRow - 1, toRow).map((tableBodyRow) => tableBodyRow.outerHTML);
      if (depthLevels.length === 1) {
        const tableBodyRowTemplate = tableBodyRowsToLoop.join('');

        let parsedTableBodyRows = '';
        for (const rowData of data[depthKey]) {
          parsedTableBodyRows += this.replaceMustaches(tableBodyRowTemplate, rowData);
        }

        return parsedTableBodyRows;
      }

      const { fromRow: innerFromRow, toRow: innerToRow } = depthLevels[1];
      let parsedTableBodyRows = '';
      for (const rowData of data[depthKey]) {
        const tableBodyRowsToLoopTemp = structuredClone(tableBodyRowsToLoop);
        const parsedInnerDepthTableBodyRows = this.generateTableBodyRowsLoopedOn(
          tableBodyRows,
          rowData,
          depthLevels.slice(1),
        );
        tableBodyRowsToLoopTemp.splice(
          innerFromRow - fromRow,
          innerToRow - innerFromRow + 1,
          parsedInnerDepthTableBodyRows,
        );
        parsedTableBodyRows += this.replaceMustaches(tableBodyRowsToLoopTemp.join(''), rowData);
      }

      return parsedTableBodyRows;
    },
    replaceMustaches: function (HTMLText, data) {
      // replaces all {{mustaches}} in the given text with the appropriate data values
      if (!HTMLText) {
        // if no HTML text is provided
        return ''; // return an empty string
      }

      const mustaches = HTMLText.match(/{{.+?}}/g);
      if (!mustaches) {
        // if there are no mustaches to replace
        return HTMLText; // return the original text
      }

      let formattedHTMLText = HTMLText;

      for (const mustache of mustaches) {
        // loop through all the mustaches found
        const dataKey = mustache.substring(2, mustache.length - 2);
        if (dataKey === 'jsreportPageNum' || dataKey === 'jsreportTotalPage') {
          // skip these two keys, they can only be handled by jsreport
          continue; // only jsreport will know the number of pages in the report; you can't know the number of pages that will be generated before actually generating the report
        }

        formattedHTMLText = formattedHTMLText.replaceAll(mustache, data[dataKey] ?? '');
      }

      return formattedHTMLText;
    },
    getPDFFromJsreport: async function (reportData) {
      const processedHeader = this.parseAndProcessHTMLTemplate(this.$refs.pageHeader, {
        ...this.sharedData,
        ...reportData,
      });
      const processedContent = this.$refs.page.map((pageHTMLElement) =>
        this.parseAndProcessHTMLTemplate(pageHTMLElement, { ...this.sharedData, ...reportData }),
      ); // page has v-for declared on it, ref will return an array
      const processedFooter = this.parseAndProcessHTMLTemplate(this.$refs.pageFooter, {
        ...this.sharedData,
        ...reportData,
      });

      this.showHeightMeasurer = true;
      // Replace the dynamic value because when getting the header height, if the dynamic name is big then it will extend the height more than expected. 
      // So replace with just a random text to occupy the space. 
      this.heightMeasurerHTML = processedHeader.outerHTML.replace(/{{\s*.*?}}/g, '1'); 
      await this.$nextTick(); // wait for the measurer to be rendered
      const headerHeight = this.getElementHeightInMm('heightMeasurer'); // and measure the margin needed for header
      // this is needed because the margin depends on the data, and needs to be measured after the header and footer templates have been processed

      this.heightMeasurerHTML = processedFooter.outerHTML;
      await this.$nextTick();
      let footerHeight = this.getElementHeightInMm('heightMeasurer');
      this.showHeightMeasurer = false;
      this.heightMeasurerHTML = '';

      for (const contentElement of processedContent) {
        // when designing the report template, the content height is set so that all three report sections add up to the size of the page
        contentElement.style.height = 'fit-content'; // but leaving the content height as such may cause it to unnecessarily take up multiple pages, thus the need to change the height match the content size
      }

      let backgroundImageDataURL;
      if (this.showBackground) {
        if (this.backgroundImageFiles?.[0]) {
          // ==================== CHANGE TO FOR LOOP LATER
          // ==================== have the controller get the SettingsSystem value itself, instead of relying on vue to pass it back which is potentially dangerous
          const APIURL = `/DesignerV3/GetBackgroundImage?imageName=${this.backgroundImageFiles?.[0]}&folderName=${this.backgroundImageFolders?.[0]}&companyFolderName=${this.companyReportFolder}`;
          backgroundImageDataURL = await this.$apiCallV2(APIURL);
        }

        if (!backgroundImageDataURL && this.backgroundImage) {
          // if the background image provided in the prop could not be found, and there is a background image specified in the template
          const [folderName, fileName] = this.backgroundImage.split('/');
          // ==================== have the controller get the SettingsSystem value itself, instead of relying on vue to pass it back which is potentially dangerous
          const APIURL = `/DesignerV3/GetBackgroundImage?imageName=${fileName}&folderName=${folderName}&companyFolderName=${this.companyReportFolder}`;
          backgroundImageDataURL = await this.$apiCallV2(APIURL);
        }
      }
      
      //if template have footer then will include the footer into the body but make it hidden.
      //reason for include footer outerHTML into innerHTML is because want to have the innerHTML footer to overlap with the footer div so that can allow the innerHTML to utilize the whole page
      let innerHTMLTemp = '';
      if (processedFooter.innerHTML !== '') {
        //to make the hidden div in the body to have 0 padding. If not there will duplicate padding. 
        //The JSreport already have the margin bottom set for each page using the footer padding bottom. 
        let replaceFooter = processedFooter.outerHTML;
        const haveFooter = replaceFooter.match(/<footer\b[^>]*>/);
        if (haveFooter) {
          const havePadding = haveFooter[0].match(/padding\s*:\s*[^;"]+;?/);
          if (havePadding) {
            const replacePadding = haveFooter[0].replace(/padding\s*:\s*[^;"]+;?/, '');
            replaceFooter = replaceFooter.replace(haveFooter[0], replacePadding);
          }
        }

        if (!this.footerPreserveSpace) {
          innerHTMLTemp = processedContent
          .map((contentElement) => contentElement.outerHTML)
          .join('')
          .concat('', replaceFooter)
          .replace('visibility: visible;', 'visibility: hidden;'); //should only have 1 visibility: visible in the footer. only if footer preserve space is false
          //only change to use paddingBottom if not preserve footer space
          footerHeight = parseFloat(this.footerStyle.paddingBottom); //set to footer padding bottom because want to have the body to occupy until the end of the page
        } else {
          innerHTMLTemp = processedContent
          .map((contentElement) => contentElement.outerHTML)
          .join('')
          .concat('', replaceFooter)
          .replace('visibility: visible;', 'display: none;'); //should only have 1 visibility: change to display none to completely remove so that there is no occupy space in the body.
        }
      } else {
        innerHTMLTemp = processedContent.map((contentElement) => contentElement.outerHTML).join('');
      }

      // Init the data for the JSReport
      const jsreportRequestBody = {
        // Template for the dynamic HTML report
        template: {
          shortid: 'yPinAPnArg', // Live =========== maybe create extra templates for this and switch between them based on the environment
          // shortid: 'thyBBLP', // Test =========== maybe create extra templates for this and switch between them based on the environment
          chrome: {
            width: `${this.selectedPaper['width']}mm`,
            height: `${this.selectedPaper['height']}mm`,
            marginTop: `${headerHeight}mm`,
            marginBottom: `${footerHeight}mm`,
          },
          pdfOperations: [ //add the pdf utils operation here and remove from the jsreport studio because need to define the merge template width and height
            {
              template: {
                shortid: 'HGjV_BBdSU', //Live dynamicHTMLHeaderFooter
                // shortid: '~P4~06A', //Test dynamicHTMLHeaderFooter
                chrome: {
                  width: `${this.selectedPaper['width']}mm`,
                  height: `${this.selectedPaper['height']}mm`,
                }
              },
              type: 'merge',
              mergeWholeDocument: true,
            }
          ]
        },
        // Data needed to be send to the JSReport
        data: {
          innerHTML: innerHTMLTemp, // ============ maybe add a page break in between pages in the future
          header: processedHeader.outerHTML,
          footer: processedFooter.outerHTML,
          backgroundImage: backgroundImageDataURL,
        },
        // PDF options
        options: { 'Content-Disposition': 'attachment;filename=report.pdf' },
      };
      // Header info that needed by the JSReport API
      const headerInfo = {
        'Content-Type': 'application/json',
        Authorization: 'Basic YWRtaW46ZG1fNTU2OWRz',
      };

      try {
        // API call the JSReport with the headers and content type. Response type needs to be arraybuffer to be able to convert to blob
        return await axios.post('https://dsm.logistics.systems:5488/api/report', jsreportRequestBody, {
          headers: headerInfo,
          responseType: 'arraybuffer',
        });
      } catch (e) {
        // Any error occurred when creating the PDF via JSReport
        console.log(e);
      }
    },
    createBlobFromArrayBuffer: function (arrayBuffer, contentType) {
      return new Blob([arrayBuffer], { type: contentType });
    },
    createBlobURLFromBlob: function (blob) {
      // creates a URL for the blob passed in
      return URL.createObjectURL(blob);
    },
    setupPDFViewer: async function (PDFBlobURL) {
      // prepare PDF viewer URL and start event listener
      // Generate URL for PDF Viewer
      this.PDFViewerSignalID = `${this.userDetails['LoginUserId']}_${new Date().formatString('yyMMddHHmmss')}`;
      const PDFViewerURL = this.$router.resolve(
        `/PDFViewer/Viewer/viewer.html?file=${PDFBlobURL}&vid=${this.PDFViewerSignalID}&togglebg=${this.canToggleBackground ? '1' : '0'}&bg=${this.showBackground ? '1' : '0'}&sh=${this.canShare ? '1' : '0'}&cls=${this.canClose ? '1' : '0'}`,
      );
      this.PDFViewerURL = PDFViewerURL['href'];
      this.PDFViewerSignalHub = await this.$signal.init('pdfviewerevent');
      this.PDFViewerSignalHub.on(this.PDFViewerSignalID, this.PDFViewerEventHandler);
    },
    destroyPDFViewer: function () {
      // stop listening to events and reset all associated variables
      if (this.PDFViewerSignalHub && this.PDFViewerSignalID) {
        this.PDFViewerSignalHub.off(this.PDFViewerSignalID);
      }
      this.PDFViewerSignalHub = null;
      this.PDFViewerSignalID = '';
      this.PDFViewerURL = '';
    },
    changeToDesignerLayout: function () {
      // only used in designer mode
      this.destroyPDFViewer();

      // hide PDF viewer
      this.showPDFViewer = false;
      // show report preview (the one shown in edit mode)
      this.showReportPreview = true;
    },
    changeToPreviewLayout: async function () {
      // only used in designer mode
      this.setLoadingOverlay('Generating Report');
      const jsreportResponse = await this.getPDFFromJsreport(); // ========== need to pass in sample data later
      const PDFBlob = this.createBlobFromArrayBuffer(
        jsreportResponse['data'],
        jsreportResponse['headers']['content-type'],
      );
      const PDFBlobURL = this.createBlobURLFromBlob(PDFBlob);
      this.setLoadingOverlay('Setting up PDF Viewer');
      await this.setupPDFViewer(PDFBlobURL);

      // hide report preview
      this.showReportPreview = false;
      // show iframe to view PDF generated
      this.showPDFViewer = true;
      this.setLoadingOverlay();
    },
    generateReport: async function () {
      // only used for view mode
      if (!this.reportFiles) {
        // reportFiles aren't provided
        if (this.isDevelopmentMode) {
          console.warn('DesignerV3 - ReportFiles not provided');
        }
        return;
      }

      // hide first if it was already shown before (maybe user is previewing another report after the first was generated)
      // need to hide, otherwise styling of the PDF viewer will conflict with the height measurer, causing it to return the wrong height
      this.showPDFViewer = false;

      if (this.showBackground && this.PDFBlobURLWithBackground) {
        this.setLoadingOverlay('Setting up PDF Viewer');
        await this.setupPDFViewer(this.PDFBlobURLWithBackground);
        this.showPDFViewer = true;
        this.setLoadingOverlay();
        return;
      }

      if (!this.showBackground && this.PDFBlobURLWithoutBackground) {
        this.setLoadingOverlay('Setting up PDF Viewer');
        await this.setupPDFViewer(this.PDFBlobURLWithoutBackground);
        this.showPDFViewer = true;
        this.setLoadingOverlay();
        return;
      }

      const zip = new JSZip();
      const PDFArrayBuffers = []; // to store all array buffers returned by jsreport
      let currentLoadedTemplate; // if the template to generate is already loaded, don't call the controller again
      for (let index = 0; index < this.reportFiles.length; index++) {
        if (currentLoadedTemplate !== `${this.reportFiles[index]}_${this.reportFolders?.[index]}`) {
          currentLoadedTemplate = `${this.reportFiles[index]}_${this.reportFolders?.[index]}`;

          this.setLoadingOverlay('Getting Report Template');
          const reportDetails = await this.getReportTemplate(this.reportFiles[index], this.reportFolders?.[index]);
          if (!reportDetails) {
            continue; // go to next iteration
          }

          this.setLoadingOverlay('Preparing Report Template');
          this.clearTemplateViewer();
          await this.populateTemplateViewer(reportDetails);
        }

        this.setLoadingOverlay(`Generating Report ${index + 1}/${this.reportFiles.length}`);
        const jsreportResponse = await this.getPDFFromJsreport(this.reportData?.[index]);
        PDFArrayBuffers.push(jsreportResponse['data']);
        const individualPDFBlob = this.createBlobFromArrayBuffer(jsreportResponse['data'], 'application/pdf');
        const tempFileName =
          this.reportData?.[index]['_downloadFileName']?.replace(/[^a-zA-Z0-9\s._-]/g, '-') ??
          `${this.downloadName}_${index}`;
        zip.file(`${tempFileName}.pdf`, individualPDFBlob);
      }

      this.ZipBlob = await zip.generateAsync({ type: 'blob' });
      this.ZipBlobURL = this.createBlobURLFromBlob(this.ZipBlob);
      if (this.reportFiles.length > 1) {
        this.ZipFileName = `Document_${new Date().formatString('yyyyMMddHHmm')}`;
      }
      const mergedPDF = await PDFDocument.create(); // pages from all the generated reports will be copied into this document
      for (let index = 0; index < PDFArrayBuffers.length; index++) {
        this.setLoadingOverlay(`Merging Reports ${index + 1}/${this.reportFiles.length}`);

        const loadedPDFDocument = await PDFDocument.load(PDFArrayBuffers[index]);
        const copiedPages = await mergedPDF.copyPages(loadedPDFDocument, loadedPDFDocument.getPageIndices());
        for (const page of copiedPages) {
          mergedPDF.addPage(page);
        }
      }

      this.setLoadingOverlay('Finalising Report');
      const mergedPDFBytes = await mergedPDF.save();
      const mergedPDFBlob = this.createBlobFromArrayBuffer(mergedPDFBytes.buffer, 'application/pdf');
      const mergedPDFBlobURL = this.createBlobURLFromBlob(mergedPDFBlob);
      if (this.showBackground) {
        this.PDFBlobWithBackground = mergedPDFBlob;
        this.PDFBlobURLWithBackground = mergedPDFBlobURL;
      } else {
        this.PDFBlobWithoutBackground = mergedPDFBlob;
        this.PDFBlobURLWithoutBackground = mergedPDFBlobURL;
      }

      // =========== before batch printing was introduced, can remove if batch printing is stable
      // this.setLoadingOverlay("Generating Report");
      // const jsreportResponse = await this.getPDFFromJsreport();
      // const PDFBlob = this.createBlobFromArrayBuffer(jsreportResponse['data'], jsreportResponse['headers']['content-type']);
      // const PDFBlobURL = this.createBlobURLFromBlob(PDFBlob);
      // if (this.showBackground) {
      //   this.PDFBlobWithBackground = PDFBlob;
      //   this.PDFBlobURLWithBackground = PDFBlobURL;
      // } else {
      //   this.PDFBlobWithoutBackground = PDFBlob;
      //   this.PDFBlobURLWithoutBackground = PDFBlobURL;
      // }

      this.setLoadingOverlay('Setting up PDF Viewer');
      await this.setupPDFViewer(mergedPDFBlobURL);
      this.showPDFViewer = true;

      this.setLoadingOverlay();
    },
    // When the page is clicked (adding a field is handled here)
    pageClicked: function (pointerEvent, reportSection, pageIndex) {
      this.clearSelectedField();

      this.selectedPageIndex = pageIndex;

      // When a mouse is clicked within a page while pressing shift key (Adding a new field)
      if (pointerEvent.shiftKey) {
        const fieldToAdd = this.createNewStringField();
        // some CSS properties need to be changed from the default
        fieldToAdd.CSSStyles.position = 'absolute';
        fieldToAdd.CSSStyles.width = '50mm';
        switch (reportSection) {
          case 'header':
            fieldToAdd.CSSStyles.left = `${this.pxToMm(pointerEvent.offsetX, 0)}mm`;
            fieldToAdd.CSSStyles.top = `${this.pxToMm(pointerEvent.offsetY, 0)}mm`;
            this.header.push(fieldToAdd);
            break;

          case 'content':
            fieldToAdd.CSSStyles.left = `${this.pxToMm(pointerEvent.offsetX, pageIndex)}mm`;
            fieldToAdd.CSSStyles.top = `${this.pxToMm(pointerEvent.offsetY, pageIndex)}mm`;
            this.pages[pageIndex].fields.push(fieldToAdd);
            break;

          case 'footer':
            fieldToAdd.CSSStyles.left = `${this.pxToMm(pointerEvent.offsetX, 0)}mm`;
            fieldToAdd.CSSStyles.top = `${this.pxToMm(pointerEvent.offsetY, 0)}mm`;
            this.footer.push(fieldToAdd);
            break;
        }
      }
    },
    createFieldID: function (reportSection, fieldType, indexes) {
      // used to create a unique ID for the field that was clicked on, the rest of the code can use the ID to determine which field the user clicked on
      // indexes is not a rest parameter here, pass in an array directly
      const fieldID = {
        reportSection,
        fieldType,
      };

      switch (reportSection) {
        case 'header':
        case 'footer':
          switch (fieldType) {
            case 'string':
              fieldID.indexes = {
                field: indexes[0],
              };
              break;

            case 'tablehead':
            case 'tablefoot':
              fieldID.indexes = {
                field: indexes[0],
                // only one header or footer row allowed
                cell: indexes[1],
              };
              break;

            case 'tablebody':
              fieldID.indexes = {
                field: indexes[0],
                row: indexes[1],
                cell: indexes[2],
              };
              break;
          }
          break;

        case 'content':
          switch (fieldType) {
            case 'string':
              fieldID.indexes = {
                page: indexes[0],
                field: indexes[1],
              };
              break;

            case 'tablehead':
            case 'tablefoot':
              fieldID.indexes = {
                page: indexes[0],
                field: indexes[1],
                // only one header or footer row allowed
                cell: indexes[2],
              };
              break;

            case 'tablebody':
              fieldID.indexes = {
                page: indexes[0],
                field: indexes[1],
                row: indexes[2],
                cell: indexes[3],
              };
              break;
          }
          break;
      }

      return fieldID;
    },
    getReportSectionOfSelectedField: function () {
      if (!this.selectedFieldID) {
        throw new Error('getReportSectionOfSelectedField - No selected field ID found');
      }

      return this.selectedFieldID.reportSection;
    },
    setReportSectionOfSelectedField: function (newFields) {
      const reportSection = this.getReportSectionOfSelectedField();
      switch (reportSection) {
        case 'header':
          this.header = newFields;
          break;

        case 'content': {
          const { page: pageIndex } = this.selectedFieldID.indexes;
          this.pages[pageIndex].fields = newFields;
          break;
        }

        case 'footer':
          this.footer = newFields;
      }
    },
    getAllFieldsWithinReportSectionOfSelectedField: function () {
      if (!this.selectedFieldID) {
        throw new Error('getAllFieldsWithinReportSectionOfSelectedField - No selected field ID found');
      }

      const reportSection = this.getReportSectionOfSelectedField();
      switch (reportSection) {
        case 'header':
          return this.header;

        case 'content': {
          const { page: pageIndex } = this.selectedFieldID.indexes;
          return this.pages[pageIndex].fields;
        }

        case 'footer':
          return this.footer;
      }
    },
    getSelectedField: function () {
      if (!this.selectedFieldID) {
        throw new Error('getSelectedField - No selected field ID found');
      }

      const { reportSection } = this.selectedFieldID;
      switch (reportSection) {
        case 'header': {
          const { field: fieldIndex } = this.selectedFieldID.indexes;
          return this.header[fieldIndex];
        }

        case 'content': {
          const { page: pageIndex, field: fieldIndex } = this.selectedFieldID.indexes;
          return this.pages[pageIndex].fields[fieldIndex];
        }

        case 'footer': {
          const { field: fieldIndex } = this.selectedFieldID.indexes;
          return this.footer[fieldIndex];
        }
      }
    },
    getFieldIndexOfSelectedField: function () {
      if (!this.selectedFieldID) {
        throw new Error('getFieldIndexOfSelectedField - No selected field ID found');
      }

      return this.selectedFieldID.indexes.field;
    },
    getFieldTypeOfSelectedField: function () {
      if (!this.selectedFieldID) {
        throw new Error('getFieldTypeOfSelectedField - No selected field ID found');
      }

      return this.selectedFieldID.fieldType;
    },
    getHeaderRowOfSelectedTableField: function () {
      if (!this.selectedFieldID) {
        throw new Error('getHeaderRowOfSelectedTableField - No selected field ID found');
      }

      if (!this.selectedFieldIsTable) {
        throw new Error('getHeaderRowOfSelectedTableField - Selected field is not a table');
      }

      if (!this.selectedTableFieldHasHeader) {
        throw new Error('getHeaderRowOfSelectedTableField - Selected table field does not have a header');
      }

      return this.getSelectedField().tableDetails.header;
    },
    getBodyRowsOfSelectedTableField: function () {
      if (!this.selectedFieldID) {
        throw new Error('getBodyRowsOfSelectedTableField - No selected field ID found');
      }

      if (!this.selectedFieldIsTable) {
        throw new Error('getBodyRowsOfSelectedTableField - Selected field is not a table');
      }

      return this.getSelectedField().tableDetails.row;
    },
    getFooterRowOfSelectedTableField: function () {
      if (!this.selectedFieldID) {
        throw new Error('getFooterRowOfSelectedTableField - No selected field ID found');
      }

      if (!this.selectedFieldIsTable) {
        throw new Error('getFooterRowOfSelectedTableField - Selected field is not a table');
      }

      if (!this.selectedTableFieldHasFooter) {
        throw new Error('getFooterRowOfSelectedTableField - Selected table field does not have a footer');
      }

      return this.getSelectedField().tableDetails.footer;
    },
    getSelectedRowOfSelectedTableField: function () {
      if (!this.selectedFieldID) {
        throw new Error('getSelectedRowOfSelectedTableField - No selected field ID found');
      }

      if (!this.selectedFieldIsTable) {
        throw new Error('getSelectedRowOfSelectedTableField - Selected field is not a table');
      }

      const selectedField = this.getSelectedField();
      const fieldType = this.getFieldTypeOfSelectedField();
      switch (fieldType) {
        case 'tablehead':
          return selectedField.tableDetails.header;

        case 'tablebody': {
          const rowIndex = this.getIndexOfSelectedRowOfSelectedTableField();
          return selectedField.tableDetails.row[rowIndex];
        }

        case 'tablefoot':
          return selectedField.tableDetails.footer;
      }

      throw new Error('getSelectedRowOfSelectedTableField - Unexpectedly fell through all checks');
    },
    getIndexOfSelectedRowOfSelectedTableField: function () {
      if (!this.selectedFieldID) {
        throw new Error('getIndexOfSelectedRowOfSelectedTableField - No selected field ID found');
      }

      if (!this.selectedFieldIsTable) {
        throw new Error('getIndexOfSelectedRowOfSelectedTableField - Selected field is not a table');
      }

      const { fieldType } = this.selectedFieldID;
      if (fieldType !== 'tablebody') {
        throw new Error(
          "getIndexOfSelectedRowOfSelectedTableField - Selected row isn't in table body, and only one row is allowed",
        );
      }

      return this.selectedFieldID.indexes.row;
    },
    getCellsOfSelectedRowOfSelectedTableField: function () {
      if (!this.selectedFieldID) {
        throw new Error('getCellsOfSelectedRowOfSelectedTableField - No selected field ID found');
      }

      if (!this.selectedFieldIsTable) {
        throw new Error('getCellsOfSelectedRowOfSelectedTableField - Selected field is not a table');
      }

      return this.getSelectedRowOfSelectedTableField().cell;
    },
    getSelectedCellOfSelectedTableField: function () {
      if (!this.selectedFieldID) {
        throw new Error('getSelectedCellOfSelectedTableField - No selected field ID found');
      }

      if (!this.selectedFieldIsTable) {
        throw new Error('getSelectedCellOfSelectedTableField - Selected field is not a table');
      }

      const cellIndex = this.getIndexOfSelectedCellOfSelectedTableField();
      return this.getCellsOfSelectedRowOfSelectedTableField()[cellIndex];
    },
    getIndexOfSelectedCellOfSelectedTableField: function () {
      if (!this.selectedFieldID) {
        throw new Error('getIndexOfSelectedCellOfSelectedTableField - No selected field ID found');
      }

      if (!this.selectedFieldIsTable) {
        throw new Error('getIndexOfSelectedCellOfSelectedTableField - Selected field is not a table');
      }

      return this.selectedFieldID.indexes.cell;
    },
    fieldClicked: function (pointerEvent, reportSection, fieldType, ...indexes) {
      // to prevent pageClicked from triggering when a field is clicked on
      pointerEvent.stopPropagation();

      this.clearSelectedField(); // clear off previous selected field data to start from a clean slate
      // there were issues when switching selection between a string and a table because not all data members are shared between both field types
      this.selectedFieldID = this.createFieldID(reportSection, fieldType, indexes);

      const selectedField = this.getSelectedField();
      this.selectedFieldIsConditionallyRendered = selectedField.conditionalRendering.isConditionallyRendered;
      this.selectedFieldConditionalRenderingDataKey = selectedField.conditionalRendering.dataKey;
      this.selectedFieldConditionalRenderingCondition =
        this.conditionalRenderingConditionItems.find((condition) => {
          return condition.name === selectedField.conditionalRendering.condition;
        }) ?? null;
      this.selectedFieldConditionalRenderingOperator =
        this.conditionalRenderingOperatorItems.find(
          (operator) => operator.name === selectedField.conditionalRendering.operator,
        ) ?? null;
      this.selectedFieldConditionalRenderingOnPages =
        this.conditionalRenderingOnPagesItems.find(
          (onPage) => onPage.name === selectedField.conditionalRendering.onPages,
        ) ?? null;
      this.selectedFieldConditionalRenderingPreserveSpace = selectedField.conditionalRendering.preserveSpace;

      this.selectedFieldIsLooped = selectedField.looping.isLooped;
      this.selectedFieldLoopingFieldKey = selectedField.looping.fieldKey;
      this.selectedFieldLoopingMarginInBetween = selectedField.looping.marginInBetween;

      if (!this.selectedFieldIsTable) {
        this.selectedFieldIsImage = selectedField.isImage;
        this.selectedFieldTextContent = selectedField.value;
        this.selectedField = selectedField;
      } else {
        const selectedCell = this.getSelectedCellOfSelectedTableField();
        this.selectedFieldIsImage = selectedCell.isImage;
        this.selectedFieldTextContent = selectedCell.value;

        this.selectedFieldLoopingDepthLevels = selectedField.looping.depthLevels;
        const selectedRow = this.getSelectedRowOfSelectedTableField();
        this.selectedFieldColumnSizing = selectedRow.columnWidths;

        if (!this.selectedStylingSection) {
          // just in case the dropdown was cleared
          this.selectedStylingSection = this.stylingSectionItems.find((stylingSection) => {
            return stylingSection.name === 'Table';
          });
        }

        let associatedSection;
        switch (this.selectedStylingSection.name) {
          case 'Table':
            associatedSection = selectedField;
            break;

          case 'Row':
            associatedSection = selectedRow;
            break;

          case 'Cell':
            associatedSection = selectedCell;
            break;
        }

        this.selectedField = associatedSection;
        this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
          return fontFamily.value === associatedSection.CSSStyles.fontFamily;
        });
        this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
          return borderStyle.style === associatedSection.CSSStyles.borderStyle;
        });
      }

      // ============== remove later if not needed
      // switch (reportSection) {
      //   case 'header':
      //     switch (fieldType) {
      //       case 'string':
      //         {
      //           const [fieldIndex] = indexes;

      //           this.selectedField = this.header[fieldIndex];

      //           this.selectedFieldIsConditionallyRendered =
      //             this.header[fieldIndex].conditionalRendering.isConditionallyRendered;
      //           this.selectedFieldConditionalRenderingDataKey = this.header[fieldIndex].conditionalRendering.dataKey;
      //           this.selectedFieldConditionalRenderingCondition = this.conditionalRenderingConditionItems.find(
      //             (condition) => {
      //               return condition.name === this.header[fieldIndex].conditionalRendering.condition;
      //             },
      //           );
      //           this.selectedFieldConditionalRenderingPreserveSpace =
      //             this.header[fieldIndex].conditionalRendering.preserveSpace;

      //           this.selectedFieldIsImage = this.header[fieldIndex].isImage;
      //           this.selectedFieldTextContent = this.header[fieldIndex].value;

      //           this.selectedFieldIsLooped = this.header[fieldIndex].looping.isLooped;
      //           this.selectedFieldLoopingFieldKey = this.header[fieldIndex].looping.fieldKey;
      //           this.selectedFieldLoopingMarginInBetween = this.header[fieldIndex].looping.marginInBetween;

      //           this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
      //             return fontFamily.value === this.header[fieldIndex].CSSStyles.fontFamily;
      //           });

      //           this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
      //             return borderStyle.style === this.header[fieldIndex].CSSStyles.borderStyle;
      //           });
      //         }
      //         break;

      //       case 'tablehead':
      //         if (!this.selectedStylingSection) {
      //           // just in case the dropdown was cleared
      //           this.selectedStylingSection = this.stylingSectionItems.find((stylingSection) => {
      //             return stylingSection.name === 'Table';
      //           });
      //         }

      //         {
      //           // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
      //           const [fieldIndex, cellIndex] = indexes;

      //           // these don't depend on what the selected styling section is
      //           this.selectedFieldIsConditionallyRendered =
      //             this.header[fieldIndex].conditionalRendering.isConditionallyRendered;
      //           this.selectedFieldConditionalRenderingDataKey = this.header[fieldIndex].conditionalRendering.dataKey;
      //           this.selectedFieldConditionalRenderingCondition = this.conditionalRenderingConditionItems.find(
      //             (condition) => {
      //               return condition.name === this.header[fieldIndex].conditionalRendering.condition;
      //             },
      //           );
      //           this.selectedFieldConditionalRenderingPreserveSpace =
      //             this.header[fieldIndex].conditionalRendering.preserveSpace;

      //           this.selectedFieldIsImage = this.header[fieldIndex].tableDetails.header.cell[cellIndex].isImage;
      //           this.selectedFieldTextContent = this.header[fieldIndex].tableDetails.header.cell[cellIndex].value;

      //           this.selectedFieldIsLooped = this.header[fieldIndex].looping.isLooped;
      //           this.selectedFieldLoopingFieldKey = this.header[fieldIndex].looping.fieldKey;
      //           this.selectedFieldLoopingMarginInBetween = this.header[fieldIndex].looping.marginInBetween;
      //           this.selectedFieldLoopingDepthLevels = this.header[fieldIndex].looping.depthLevels;

      //           this.selectedFieldColumnSizing = this.header[fieldIndex].tableDetails.header.columnWidths;

      //           switch (this.selectedStylingSection.name) {
      //             case 'Table':
      //               this.selectedField = this.header[fieldIndex];
      //               this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
      //                 return fontFamily.value === this.header[fieldIndex].CSSStyles.fontFamily;
      //               });
      //               this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
      //                 return borderStyle.style === this.header[fieldIndex].CSSStyles.borderStyle;
      //               });
      //               break;

      //             case 'Row':
      //               this.selectedField = this.header[fieldIndex].tableDetails.header;
      //               this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
      //                 return fontFamily.value === this.header[fieldIndex].tableDetails.header.CSSStyles.fontFamily;
      //               });
      //               this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
      //                 return borderStyle.style === this.header[fieldIndex].tableDetails.header.CSSStyles.borderStyle;
      //               });
      //               break;

      //             case 'Cell':
      //               this.selectedField = this.header[fieldIndex].tableDetails.header.cell[cellIndex];
      //               this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
      //                 return (
      //                   fontFamily.value ===
      //                   this.header[fieldIndex].tableDetails.header.cell[cellIndex].CSSStyles.fontFamily
      //                 );
      //               });
      //               this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
      //                 return (
      //                   borderStyle.style ===
      //                   this.header[fieldIndex].tableDetails.header.cell[cellIndex].CSSStyles.borderStyle
      //                 );
      //               });
      //               break;
      //           }
      //         }
      //         break;

      //       case 'tablebody':
      //         if (!this.selectedStylingSection) {
      //           // just in case the dropdown was cleared
      //           this.selectedStylingSection = this.stylingSectionItems.find((stylingSection) => {
      //             return stylingSection.name === 'Table';
      //           });
      //         }

      //         {
      //           // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
      //           const [fieldIndex, rowIndex, cellIndex] = indexes;

      //           // these don't depend on what the selected styling section is
      //           this.selectedFieldIsConditionallyRendered =
      //             this.header[fieldIndex].conditionalRendering.isConditionallyRendered;
      //           this.selectedFieldConditionalRenderingDataKey = this.header[fieldIndex].conditionalRendering.dataKey;
      //           this.selectedFieldConditionalRenderingCondition = this.conditionalRenderingConditionItems.find(
      //             (condition) => {
      //               return condition.name === this.header[fieldIndex].conditionalRendering.condition;
      //             },
      //           );
      //           this.selectedFieldConditionalRenderingPreserveSpace =
      //             this.header[fieldIndex].conditionalRendering.preserveSpace;

      //           this.selectedFieldIsImage = this.header[fieldIndex].tableDetails.row[rowIndex].cell[cellIndex].isImage;
      //           this.selectedFieldTextContent =
      //             this.header[fieldIndex].tableDetails.row[rowIndex].cell[cellIndex].value;

      //           this.selectedFieldIsLooped = this.header[fieldIndex].looping.isLooped;
      //           this.selectedFieldLoopingFieldKey = this.header[fieldIndex].looping.fieldKey;
      //           this.selectedFieldLoopingMarginInBetween = this.header[fieldIndex].looping.marginInBetween;

      //           // unique to tables
      //           this.selectedFieldLoopingDepthLevels = this.header[fieldIndex].looping.depthLevels;

      //           // unique to tables
      //           this.selectedFieldColumnSizing = this.header[fieldIndex].tableDetails.row[rowIndex].columnWidths;

      //           switch (this.selectedStylingSection.name) {
      //             case 'Table':
      //               this.selectedField = this.header[fieldIndex];
      //               this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
      //                 return fontFamily.value === this.header[fieldIndex].CSSStyles.fontFamily;
      //               });
      //               this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
      //                 return borderStyle.style === this.header[fieldIndex].CSSStyles.borderStyle;
      //               });
      //               break;

      //             case 'Row':
      //               this.selectedField = this.header[fieldIndex].tableDetails.row[rowIndex];
      //               this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
      //                 return (
      //                   fontFamily.value === this.header[fieldIndex].tableDetails.row[rowIndex].CSSStyles.fontFamily
      //                 );
      //               });
      //               this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
      //                 return (
      //                   borderStyle.style === this.header[fieldIndex].tableDetails.row[rowIndex].CSSStyles.borderStyle
      //                 );
      //               });
      //               break;

      //             case 'Cell':
      //               this.selectedField = this.header[fieldIndex].tableDetails.row[rowIndex].cell[cellIndex];
      //               this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
      //                 return (
      //                   fontFamily.value ===
      //                   this.header[fieldIndex].tableDetails.row[rowIndex].cell[cellIndex].CSSStyles.fontFamily
      //                 );
      //               });
      //               this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
      //                 return (
      //                   borderStyle.style ===
      //                   this.header[fieldIndex].tableDetails.row[rowIndex].cell[cellIndex].CSSStyles.borderStyle
      //                 );
      //               });
      //               break;
      //           }
      //         }
      //         break;

      //       case 'tablefoot':
      //         if (!this.selectedStylingSection) {
      //           // just in case the dropdown was cleared
      //           this.selectedStylingSection = this.stylingSectionItems.find((stylingSection) => {
      //             return stylingSection.name === 'Table';
      //           });
      //         }

      //         {
      //           // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
      //           const [fieldIndex, cellIndex] = indexes;

      //           // these don't depend on what the selected styling section is
      //           this.selectedFieldIsConditionallyRendered =
      //             this.header[fieldIndex].conditionalRendering.isConditionallyRendered;
      //           this.selectedFieldConditionalRenderingDataKey = this.header[fieldIndex].conditionalRendering.dataKey;
      //           this.selectedFieldConditionalRenderingCondition = this.conditionalRenderingConditionItems.find(
      //             (condition) => {
      //               return condition.name === this.header[fieldIndex].conditionalRendering.condition;
      //             },
      //           );
      //           this.selectedFieldConditionalRenderingPreserveSpace =
      //             this.header[fieldIndex].conditionalRendering.preserveSpace;

      //           this.selectedFieldIsImage = this.header[fieldIndex].tableDetails.footer.cell[cellIndex].isImage;
      //           this.selectedFieldTextContent = this.header[fieldIndex].tableDetails.footer.cell[cellIndex].value;

      //           this.selectedFieldIsLooped = this.header[fieldIndex].looping.isLooped;
      //           this.selectedFieldLoopingFieldKey = this.header[fieldIndex].looping.fieldKey;
      //           this.selectedFieldLoopingMarginInBetween = this.header[fieldIndex].looping.marginInBetween;
      //           this.selectedFieldLoopingDepthLevels = this.header[fieldIndex].looping.depthLevels;

      //           this.selectedFieldColumnSizing = this.header[fieldIndex].tableDetails.footer.columnWidths;

      //           switch (this.selectedStylingSection.name) {
      //             case 'Table':
      //               this.selectedField = this.header[fieldIndex];
      //               this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
      //                 return fontFamily.value === this.header[fieldIndex].CSSStyles.fontFamily;
      //               });
      //               this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
      //                 return borderStyle.style === this.header[fieldIndex].CSSStyles.borderStyle;
      //               });
      //               break;

      //             case 'Row':
      //               this.selectedField = this.header[fieldIndex].tableDetails.footer;
      //               this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
      //                 return fontFamily.value === this.header[fieldIndex].tableDetails.footer.CSSStyles.fontFamily;
      //               });
      //               this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
      //                 return borderStyle.style === this.header[fieldIndex].tableDetails.footer.CSSStyles.borderStyle;
      //               });
      //               break;

      //             case 'Cell':
      //               this.selectedField = this.header[fieldIndex].tableDetails.footer.cell[cellIndex];
      //               this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
      //                 return (
      //                   fontFamily.value ===
      //                   this.header[fieldIndex].tableDetails.footer.cell[cellIndex].CSSStyles.fontFamily
      //                 );
      //               });
      //               this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
      //                 return (
      //                   borderStyle.style ===
      //                   this.header[fieldIndex].tableDetails.footer.cell[cellIndex].CSSStyles.borderStyle
      //                 );
      //               });
      //               break;
      //           }
      //         }
      //         break;
      //     }
      //     break;

      //   case 'content':
      //     switch (fieldType) {
      //       case 'string':
      //         {
      //           // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
      //           const [pageIndex, fieldIndex] = indexes;

      //           this.selectedField = this.pages[pageIndex].fields[fieldIndex];

      //           this.selectedFieldIsConditionallyRendered =
      //             this.pages[pageIndex].fields[fieldIndex].conditionalRendering.isConditionallyRendered;
      //           this.selectedFieldConditionalRenderingDataKey =
      //             this.pages[pageIndex].fields[fieldIndex].conditionalRendering.dataKey;
      //           this.selectedFieldConditionalRenderingCondition = this.conditionalRenderingConditionItems.find(
      //             (condition) => {
      //               return condition.name === this.pages[pageIndex].fields[fieldIndex].conditionalRendering.condition;
      //             },
      //           );
      //           this.selectedFieldConditionalRenderingPreserveSpace =
      //             this.pages[pageIndex].fields[fieldIndex].conditionalRendering.preserveSpace;

      //           this.selectedFieldIsImage = this.pages[pageIndex].fields[fieldIndex].isImage;
      //           this.selectedFieldTextContent = this.pages[pageIndex].fields[fieldIndex].value;

      //           this.selectedFieldIsLooped = this.pages[pageIndex].fields[fieldIndex].looping.isLooped;
      //           this.selectedFieldLoopingFieldKey = this.pages[pageIndex].fields[fieldIndex].looping.fieldKey;
      //           this.selectedFieldLoopingMarginInBetween =
      //             this.pages[pageIndex].fields[fieldIndex].looping.marginInBetween;

      //           this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
      //             return fontFamily.value === this.pages[pageIndex].fields[fieldIndex].CSSStyles.fontFamily;
      //           });

      //           this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
      //             return borderStyle.style === this.pages[pageIndex].fields[fieldIndex].CSSStyles.borderStyle;
      //           });
      //         }
      //         break;

      //       case 'tablehead':
      //         if (!this.selectedStylingSection) {
      //           // just in case the dropdown was cleared
      //           this.selectedStylingSection = this.stylingSectionItems.find((stylingSection) => {
      //             return stylingSection.name === 'Table';
      //           });
      //         }

      //         {
      //           // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
      //           const [pageIndex, fieldIndex, cellIndex] = indexes;

      //           // these don't depend on what the selected styling section is
      //           this.selectedFieldIsConditionallyRendered =
      //             this.pages[pageIndex].fields[fieldIndex].conditionalRendering.isConditionallyRendered;
      //           this.selectedFieldConditionalRenderingDataKey =
      //             this.pages[pageIndex].fields[fieldIndex].conditionalRendering.dataKey;
      //           this.selectedFieldConditionalRenderingCondition = this.conditionalRenderingConditionItems.find(
      //             (condition) => {
      //               return condition.name === this.pages[pageIndex].fields[fieldIndex].conditionalRendering.condition;
      //             },
      //           );
      //           this.selectedFieldConditionalRenderingPreserveSpace =
      //             this.pages[pageIndex].fields[fieldIndex].conditionalRendering.preserveSpace;

      //           this.selectedFieldIsImage =
      //             this.pages[pageIndex].fields[fieldIndex].tableDetails.header.cell[cellIndex].isImage;
      //           this.selectedFieldTextContent =
      //             this.pages[pageIndex].fields[fieldIndex].tableDetails.header.cell[cellIndex].value;

      //           this.selectedFieldIsLooped = this.pages[pageIndex].fields[fieldIndex].looping.isLooped;
      //           this.selectedFieldLoopingFieldKey = this.pages[pageIndex].fields[fieldIndex].looping.fieldKey;
      //           this.selectedFieldLoopingMarginInBetween =
      //             this.pages[pageIndex].fields[fieldIndex].looping.marginInBetween;
      //           this.selectedFieldLoopingDepthLevels = this.pages[pageIndex].fields[fieldIndex].looping.depthLevels;

      //           this.selectedFieldColumnSizing =
      //             this.pages[pageIndex].fields[fieldIndex].tableDetails.header.columnWidths;

      //           switch (this.selectedStylingSection.name) {
      //             case 'Table':
      //               this.selectedField = this.pages[pageIndex].fields[fieldIndex];
      //               this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
      //                 return fontFamily.value === this.pages[pageIndex].fields[fieldIndex].CSSStyles.fontFamily;
      //               });
      //               this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
      //                 return borderStyle.style === this.pages[pageIndex].fields[fieldIndex].CSSStyles.borderStyle;
      //               });
      //               break;

      //             case 'Row':
      //               this.selectedField = this.pages[pageIndex].fields[fieldIndex].tableDetails.header;
      //               this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
      //                 return (
      //                   fontFamily.value ===
      //                   this.pages[pageIndex].fields[fieldIndex].tableDetails.header.CSSStyles.fontFamily
      //                 );
      //               });
      //               this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
      //                 return (
      //                   borderStyle.style ===
      //                   this.pages[pageIndex].fields[fieldIndex].tableDetails.header.CSSStyles.borderStyle
      //                 );
      //               });
      //               break;

      //             case 'Cell':
      //               this.selectedField = this.pages[pageIndex].fields[fieldIndex].tableDetails.header.cell[cellIndex];
      //               this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
      //                 return (
      //                   fontFamily.value ===
      //                   this.pages[pageIndex].fields[fieldIndex].tableDetails.header.cell[cellIndex].CSSStyles
      //                     .fontFamily
      //                 );
      //               });
      //               this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
      //                 return (
      //                   borderStyle.style ===
      //                   this.pages[pageIndex].fields[fieldIndex].tableDetails.header.cell[cellIndex].CSSStyles
      //                     .borderStyle
      //                 );
      //               });
      //               break;
      //           }
      //         }
      //         break;

      //       case 'tablebody':
      //         if (!this.selectedStylingSection) {
      //           // just in case the dropdown was cleared
      //           this.selectedStylingSection = this.stylingSectionItems.find((stylingSection) => {
      //             return stylingSection.name === 'Table';
      //           });
      //         }

      //         {
      //           // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
      //           const [pageIndex, fieldIndex, rowIndex, cellIndex] = indexes;

      //           // these don't depend on what the selected styling section is
      //           this.selectedFieldIsConditionallyRendered =
      //             this.pages[pageIndex].fields[fieldIndex].conditionalRendering.isConditionallyRendered;
      //           this.selectedFieldConditionalRenderingDataKey =
      //             this.pages[pageIndex].fields[fieldIndex].conditionalRendering.dataKey;
      //           this.selectedFieldConditionalRenderingCondition = this.conditionalRenderingConditionItems.find(
      //             (condition) => {
      //               return condition.name === this.pages[pageIndex].fields[fieldIndex].conditionalRendering.condition;
      //             },
      //           );
      //           this.selectedFieldConditionalRenderingPreserveSpace =
      //             this.pages[pageIndex].fields[fieldIndex].conditionalRendering.preserveSpace;

      //           this.selectedFieldIsImage =
      //             this.pages[pageIndex].fields[fieldIndex].tableDetails.row[rowIndex].cell[cellIndex].isImage;
      //           this.selectedFieldTextContent =
      //             this.pages[pageIndex].fields[fieldIndex].tableDetails.row[rowIndex].cell[cellIndex].value;

      //           this.selectedFieldIsLooped = this.pages[pageIndex].fields[fieldIndex].looping.isLooped;
      //           this.selectedFieldLoopingFieldKey = this.pages[pageIndex].fields[fieldIndex].looping.fieldKey;
      //           this.selectedFieldLoopingMarginInBetween =
      //             this.pages[pageIndex].fields[fieldIndex].looping.marginInBetween;
      //           this.selectedFieldLoopingDepthLevels = this.pages[pageIndex].fields[fieldIndex].looping.depthLevels;

      //           this.selectedFieldColumnSizing =
      //             this.pages[pageIndex].fields[fieldIndex].tableDetails.row[rowIndex].columnWidths;

      //           switch (this.selectedStylingSection.name) {
      //             case 'Table':
      //               this.selectedField = this.pages[pageIndex].fields[fieldIndex];
      //               this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
      //                 return fontFamily.value === this.pages[pageIndex].fields[fieldIndex].CSSStyles.fontFamily;
      //               });
      //               this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
      //                 return borderStyle.style === this.pages[pageIndex].fields[fieldIndex].CSSStyles.borderStyle;
      //               });
      //               break;

      //             case 'Row':
      //               this.selectedField = this.pages[pageIndex].fields[fieldIndex].tableDetails.row[rowIndex];
      //               this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
      //                 return (
      //                   fontFamily.value ===
      //                   this.pages[pageIndex].fields[fieldIndex].tableDetails.row[rowIndex].CSSStyles.fontFamily
      //                 );
      //               });
      //               this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
      //                 return (
      //                   borderStyle.style ===
      //                   this.pages[pageIndex].fields[fieldIndex].tableDetails.row[rowIndex].CSSStyles.borderStyle
      //                 );
      //               });
      //               break;

      //             case 'Cell':
      //               this.selectedField =
      //                 this.pages[pageIndex].fields[fieldIndex].tableDetails.row[rowIndex].cell[cellIndex];
      //               this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
      //                 return (
      //                   fontFamily.value ===
      //                   this.pages[pageIndex].fields[fieldIndex].tableDetails.row[rowIndex].cell[cellIndex].CSSStyles
      //                     .fontFamily
      //                 );
      //               });
      //               this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
      //                 return (
      //                   borderStyle.style ===
      //                   this.pages[pageIndex].fields[fieldIndex].tableDetails.row[rowIndex].cell[cellIndex].CSSStyles
      //                     .borderStyle
      //                 );
      //               });
      //               break;
      //           }
      //         }
      //         break;

      //       case 'tablefoot':
      //         if (!this.selectedStylingSection) {
      //           // just in case the dropdown was cleared
      //           this.selectedStylingSection = this.stylingSectionItems.find((stylingSection) => {
      //             return stylingSection.name === 'Table';
      //           });
      //         }

      //         {
      //           // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
      //           const [pageIndex, fieldIndex, cellIndex] = indexes;

      //           // these don't depend on what the selected styling section is
      //           this.selectedFieldIsConditionallyRendered =
      //             this.pages[pageIndex].fields[fieldIndex].conditionalRendering.isConditionallyRendered;
      //           this.selectedFieldConditionalRenderingDataKey =
      //             this.pages[pageIndex].fields[fieldIndex].conditionalRendering.dataKey;
      //           this.selectedFieldConditionalRenderingCondition = this.conditionalRenderingConditionItems.find(
      //             (condition) => {
      //               return condition.name === this.pages[pageIndex].fields[fieldIndex].conditionalRendering.condition;
      //             },
      //           );
      //           this.selectedFieldConditionalRenderingPreserveSpace =
      //             this.pages[pageIndex].fields[fieldIndex].conditionalRendering.preserveSpace;

      //           this.selectedFieldIsImage =
      //             this.pages[pageIndex].fields[fieldIndex].tableDetails.footer.cell[cellIndex].isImage;
      //           this.selectedFieldTextContent =
      //             this.pages[pageIndex].fields[fieldIndex].tableDetails.footer.cell[cellIndex].value;

      //           this.selectedFieldIsLooped = this.pages[pageIndex].fields[fieldIndex].looping.isLooped;
      //           this.selectedFieldLoopingFieldKey = this.pages[pageIndex].fields[fieldIndex].looping.fieldKey;
      //           this.selectedFieldLoopingMarginInBetween =
      //             this.pages[pageIndex].fields[fieldIndex].looping.marginInBetween;
      //           this.selectedFieldLoopingDepthLevels = this.pages[pageIndex].fields[fieldIndex].looping.depthLevels;

      //           this.selectedFieldColumnSizing =
      //             this.pages[pageIndex].fields[fieldIndex].tableDetails.footer.columnWidths;

      //           switch (this.selectedStylingSection.name) {
      //             case 'Table':
      //               this.selectedField = this.pages[pageIndex].fields[fieldIndex];
      //               this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
      //                 return fontFamily.value === this.pages[pageIndex].fields[fieldIndex].CSSStyles.fontFamily;
      //               });
      //               this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
      //                 return borderStyle.style === this.pages[pageIndex].fields[fieldIndex].CSSStyles.borderStyle;
      //               });
      //               break;

      //             case 'Row':
      //               this.selectedField = this.pages[pageIndex].fields[fieldIndex].tableDetails.footer;
      //               this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
      //                 return (
      //                   fontFamily.value ===
      //                   this.pages[pageIndex].fields[fieldIndex].tableDetails.footer.CSSStyles.fontFamily
      //                 );
      //               });
      //               this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
      //                 return (
      //                   borderStyle.style ===
      //                   this.pages[pageIndex].fields[fieldIndex].tableDetails.footer.CSSStyles.borderStyle
      //                 );
      //               });
      //               break;

      //             case 'Cell':
      //               this.selectedField = this.pages[pageIndex].fields[fieldIndex].tableDetails.footer.cell[cellIndex];
      //               this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
      //                 return (
      //                   fontFamily.value ===
      //                   this.pages[pageIndex].fields[fieldIndex].tableDetails.footer.cell[cellIndex].CSSStyles
      //                     .fontFamily
      //                 );
      //               });
      //               this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
      //                 return (
      //                   borderStyle.style ===
      //                   this.pages[pageIndex].fields[fieldIndex].tableDetails.footer.cell[cellIndex].CSSStyles
      //                     .borderStyle
      //                 );
      //               });
      //               break;
      //           }
      //         }
      //         break;
      //     }
      //     break;

      //   case 'footer':
      //     switch (fieldType) {
      //       case 'string':
      //         {
      //           // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
      //           const [fieldIndex] = indexes;

      //           this.selectedField = this.footer[fieldIndex];

      //           this.selectedFieldIsConditionallyRendered =
      //             this.footer[fieldIndex].conditionalRendering.isConditionallyRendered;
      //           this.selectedFieldConditionalRenderingDataKey = this.footer[fieldIndex].conditionalRendering.dataKey;
      //           this.selectedFieldConditionalRenderingCondition = this.conditionalRenderingConditionItems.find(
      //             (condition) => {
      //               return condition.name === this.footer[fieldIndex].conditionalRendering.condition;
      //             },
      //           );
      //           this.selectedFieldConditionalRenderingPreserveSpace =
      //             this.footer[fieldIndex].conditionalRendering.preserveSpace;

      //           this.selectedFieldIsImage = this.footer[fieldIndex].isImage;
      //           this.selectedFieldTextContent = this.footer[fieldIndex].value;

      //           this.selectedFieldIsLooped = this.footer[fieldIndex].looping.isLooped;
      //           this.selectedFieldLoopingFieldKey = this.footer[fieldIndex].looping.fieldKey;
      //           this.selectedFieldLoopingMarginInBetween = this.footer[fieldIndex].looping.marginInBetween;

      //           this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
      //             return fontFamily.value === this.footer[fieldIndex].CSSStyles.fontFamily;
      //           });

      //           this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
      //             return borderStyle.style === this.footer[fieldIndex].CSSStyles.borderStyle;
      //           });
      //         }
      //         break;

      //       case 'tablehead':
      //         if (!this.selectedStylingSection) {
      //           // just in case the dropdown was cleared
      //           this.selectedStylingSection = this.stylingSectionItems.find((stylingSection) => {
      //             return stylingSection.name === 'Table';
      //           });
      //         }

      //         {
      //           // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
      //           const [fieldIndex, cellIndex] = indexes;

      //           // these don't depend on what the selected styling section is
      //           this.selectedFieldIsConditionallyRendered =
      //             this.footer[fieldIndex].conditionalRendering.isConditionallyRendered;
      //           this.selectedFieldConditionalRenderingDataKey = this.footer[fieldIndex].conditionalRendering.dataKey;
      //           this.selectedFieldConditionalRenderingCondition = this.conditionalRenderingConditionItems.find(
      //             (condition) => {
      //               return condition.name === this.footer[fieldIndex].conditionalRendering.condition;
      //             },
      //           );
      //           this.selectedFieldConditionalRenderingPreserveSpace =
      //             this.footer[fieldIndex].conditionalRendering.preserveSpace;

      //           this.selectedFieldIsImage = this.footer[fieldIndex].tableDetails.header.cell[cellIndex].isImage;
      //           this.selectedFieldTextContent = this.footer[fieldIndex].tableDetails.header.cell[cellIndex].value;

      //           this.selectedFieldIsLooped = this.footer[fieldIndex].looping.isLooped;
      //           this.selectedFieldLoopingFieldKey = this.footer[fieldIndex].looping.fieldKey;
      //           this.selectedFieldLoopingMarginInBetween = this.footer[fieldIndex].looping.marginInBetween;
      //           this.selectedFieldLoopingDepthLevels = this.footer[fieldIndex].looping.depthLevels;

      //           this.selectedFieldColumnSizing = this.footer[fieldIndex].tableDetails.header.columnWidths;

      //           switch (this.selectedStylingSection.name) {
      //             case 'Table':
      //               this.selectedField = this.footer[fieldIndex];
      //               this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
      //                 return fontFamily.value === this.footer[fieldIndex].CSSStyles.fontFamily;
      //               });
      //               this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
      //                 return borderStyle.style === this.footer[fieldIndex].CSSStyles.borderStyle;
      //               });
      //               break;

      //             case 'Row':
      //               this.selectedField = this.footer[fieldIndex].tableDetails.header;
      //               this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
      //                 return fontFamily.value === this.footer[fieldIndex].tableDetails.header.CSSStyles.fontFamily;
      //               });
      //               this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
      //                 return borderStyle.style === this.footer[fieldIndex].tableDetails.header.CSSStyles.borderStyle;
      //               });
      //               break;

      //             case 'Cell':
      //               this.selectedField = this.footer[fieldIndex].tableDetails.header.cell[cellIndex];
      //               this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
      //                 return (
      //                   fontFamily.value ===
      //                   this.footer[fieldIndex].tableDetails.header.cell[cellIndex].CSSStyles.fontFamily
      //                 );
      //               });
      //               this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
      //                 return (
      //                   borderStyle.style ===
      //                   this.footer[fieldIndex].tableDetails.header.cell[cellIndex].CSSStyles.borderStyle
      //                 );
      //               });
      //               break;
      //           }
      //         }
      //         break;

      //       case 'tablebody':
      //         if (!this.selectedStylingSection) {
      //           // just in case the dropdown was cleared
      //           this.selectedStylingSection = this.stylingSectionItems.find((stylingSection) => {
      //             return stylingSection.name === 'Table';
      //           });
      //         }

      //         {
      //           // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
      //           const [fieldIndex, rowIndex, cellIndex] = indexes;

      //           // these don't depend on what the selected styling section is
      //           this.selectedFieldIsConditionallyRendered =
      //             this.footer[fieldIndex].conditionalRendering.isConditionallyRendered;
      //           this.selectedFieldConditionalRenderingDataKey = this.footer[fieldIndex].conditionalRendering.dataKey;
      //           this.selectedFieldConditionalRenderingCondition = this.conditionalRenderingConditionItems.find(
      //             (condition) => {
      //               return condition.name === this.footer[fieldIndex].conditionalRendering.condition;
      //             },
      //           );
      //           this.selectedFieldConditionalRenderingPreserveSpace =
      //             this.footer[fieldIndex].conditionalRendering.preserveSpace;

      //           this.selectedFieldIsImage = this.footer[fieldIndex].tableDetails.row[rowIndex].cell[cellIndex].isImage;
      //           this.selectedFieldTextContent =
      //             this.footer[fieldIndex].tableDetails.row[rowIndex].cell[cellIndex].value;

      //           this.selectedFieldIsLooped = this.footer[fieldIndex].looping.isLooped;
      //           this.selectedFieldLoopingFieldKey = this.footer[fieldIndex].looping.fieldKey;
      //           this.selectedFieldLoopingMarginInBetween = this.footer[fieldIndex].looping.marginInBetween;
      //           this.selectedFieldLoopingDepthLevels = this.footer[fieldIndex].looping.depthLevels;

      //           this.selectedFieldColumnSizing = this.footer[fieldIndex].tableDetails.row[rowIndex].columnWidths;

      //           switch (this.selectedStylingSection.name) {
      //             case 'Table':
      //               this.selectedField = this.footer[fieldIndex];
      //               this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
      //                 return fontFamily.value === this.footer[fieldIndex].CSSStyles.fontFamily;
      //               });
      //               this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
      //                 return borderStyle.style === this.footer[fieldIndex].CSSStyles.borderStyle;
      //               });
      //               break;

      //             case 'Row':
      //               this.selectedField = this.footer[fieldIndex].tableDetails.row[rowIndex];
      //               this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
      //                 return (
      //                   fontFamily.value === this.footer[fieldIndex].tableDetails.row[rowIndex].CSSStyles.fontFamily
      //                 );
      //               });
      //               this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
      //                 return (
      //                   borderStyle.style === this.footer[fieldIndex].tableDetails.row[rowIndex].CSSStyles.borderStyle
      //                 );
      //               });
      //               break;

      //             case 'Cell':
      //               this.selectedField = this.footer[fieldIndex].tableDetails.row[rowIndex].cell[cellIndex];
      //               this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
      //                 return (
      //                   fontFamily.value ===
      //                   this.footer[fieldIndex].tableDetails.row[rowIndex].cell[cellIndex].CSSStyles.fontFamily
      //                 );
      //               });
      //               this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
      //                 return (
      //                   borderStyle.style ===
      //                   this.footer[fieldIndex].tableDetails.row[rowIndex].cell[cellIndex].CSSStyles.borderStyle
      //                 );
      //               });
      //               break;
      //           }
      //         }
      //         break;

      //       case 'tablefoot':
      //         if (!this.selectedStylingSection) {
      //           // just in case the dropdown was cleared
      //           this.selectedStylingSection = this.stylingSectionItems.find((stylingSection) => {
      //             return stylingSection.name === 'Table';
      //           });
      //         }

      //         {
      //           // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
      //           const [fieldIndex, cellIndex] = indexes;

      //           // these don't depend on what the selected styling section is
      //           this.selectedFieldIsConditionallyRendered =
      //             this.footer[fieldIndex].conditionalRendering.isConditionallyRendered;
      //           this.selectedFieldConditionalRenderingDataKey = this.footer[fieldIndex].conditionalRendering.dataKey;
      //           this.selectedFieldConditionalRenderingCondition = this.conditionalRenderingConditionItems.find(
      //             (condition) => {
      //               return condition.name === this.footer[fieldIndex].conditionalRendering.condition;
      //             },
      //           );
      //           this.selectedFieldConditionalRenderingPreserveSpace =
      //             this.footer[fieldIndex].conditionalRendering.preserveSpace;

      //           this.selectedFieldIsImage = this.footer[fieldIndex].tableDetails.footer.cell[cellIndex].isImage;
      //           this.selectedFieldTextContent = this.footer[fieldIndex].tableDetails.footer.cell[cellIndex].value;

      //           this.selectedFieldIsLooped = this.footer[fieldIndex].looping.isLooped;
      //           this.selectedFieldLoopingFieldKey = this.footer[fieldIndex].looping.fieldKey;
      //           this.selectedFieldLoopingMarginInBetween = this.footer[fieldIndex].looping.marginInBetween;
      //           this.selectedFieldLoopingDepthLevels = this.footer[fieldIndex].looping.depthLevels;

      //           this.selectedFieldColumnSizing = this.footer[fieldIndex].tableDetails.footer.columnWidths;

      //           switch (this.selectedStylingSection.name) {
      //             case 'Table':
      //               this.selectedField = this.footer[fieldIndex];
      //               this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
      //                 return fontFamily.value === this.footer[fieldIndex].CSSStyles.fontFamily;
      //               });
      //               this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
      //                 return borderStyle.style === this.footer[fieldIndex].CSSStyles.borderStyle;
      //               });
      //               break;

      //             case 'Row':
      //               this.selectedField = this.footer[fieldIndex].tableDetails.footer;
      //               this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
      //                 return fontFamily.value === this.footer[fieldIndex].tableDetails.footer.CSSStyles.fontFamily;
      //               });
      //               this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
      //                 return borderStyle.style === this.footer[fieldIndex].tableDetails.footer.CSSStyles.borderStyle;
      //               });
      //               break;

      //             case 'Cell':
      //               this.selectedField = this.footer[fieldIndex].tableDetails.footer.cell[cellIndex];
      //               this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
      //                 return (
      //                   fontFamily.value ===
      //                   this.footer[fieldIndex].tableDetails.footer.cell[cellIndex].CSSStyles.fontFamily
      //                 );
      //               });
      //               this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
      //                 return (
      //                   borderStyle.style ===
      //                   this.footer[fieldIndex].tableDetails.footer.cell[cellIndex].CSSStyles.borderStyle
      //                 );
      //               });
      //               break;
      //           }
      //         }
      //         break;
      //     }
      //     break;
      // }

      this.$nextTick(() => {
        this.$refs['valueTextarea'].select(); // select all text content in <textarea>
      });
    },
    fieldIsSelected: function (reportSection, fieldType, ...indexes) {
      if (!this.selectedFieldID) {
        return false;
      }

      // check reportSection and fieldType first, if those don't match then there's no need to check the indexes
      if (this.selectedFieldID.reportSection !== reportSection || this.selectedFieldID.fieldType !== fieldType) {
        return false;
      }
      // ============== simplify this later
      switch (reportSection) {
        case 'header':
        case 'footer':
          switch (fieldType) {
            case 'string':
              return this.selectedFieldID.indexes.field === indexes[0];

            case 'tablehead':
            case 'tablefoot':
              return (
                this.selectedFieldID.indexes.field === indexes[0] &&
                // only one header and footer row allowed
                this.selectedFieldID.indexes.cell === indexes[1]
              );

            case 'tablebody':
              return (
                this.selectedFieldID.indexes.field === indexes[0] &&
                this.selectedFieldID.indexes.row === indexes[1] &&
                this.selectedFieldID.indexes.cell === indexes[2]
              );
          }
          break;

        case 'content':
          switch (fieldType) {
            case 'string':
              return (
                this.selectedFieldID.indexes.page === indexes[0] && this.selectedFieldID.indexes.field === indexes[1]
              );

            case 'tablehead':
            case 'tablefoot':
              return (
                this.selectedFieldID.indexes.page === indexes[0] &&
                this.selectedFieldID.indexes.field === indexes[1] &&
                // only one header and footer row allowed
                this.selectedFieldID.indexes.cell === indexes[2]
              );

            case 'tablebody':
              return (
                this.selectedFieldID.indexes.page === indexes[0] &&
                this.selectedFieldID.indexes.field === indexes[1] &&
                this.selectedFieldID.indexes.row === indexes[2] &&
                this.selectedFieldID.indexes.cell === indexes[3]
              );
          }
          break;
      }

      return false; // it should never reach this point, but if it does just return false
    },
    childFieldIsSelected: function (reportSection, parentFieldType, ...indexes) {
      if (!this.selectedFieldID) {
        return false;
      }

      // check reportSection first, fieldType will depend on what the parentFieldType is
      if (this.selectedFieldID.reportSection !== reportSection) {
        return false;
      }
      // ============== simplify this later
      switch (reportSection) {
        case 'header':
        case 'footer':
          switch (parentFieldType) {
            case 'table':
              if (
                this.selectedFieldID.fieldType !== 'tablehead' &&
                this.selectedFieldID.fieldType !== 'tablebody' &&
                this.selectedFieldID.fieldType !== 'tablefoot'
              ) {
                return false;
              }

              return this.selectedFieldID.indexes.field === indexes[0];

            case 'tableheadrow':
              if (this.selectedFieldID.fieldType !== 'tablehead') {
                return false;
              }

              // there can only be one header row for table heads, so you only need to make sure the selected child is from the same table as the parent
              return this.selectedFieldID.indexes.field === indexes[0];

            case 'tablebodyrow':
              if (this.selectedFieldID.fieldType !== 'tablebody') {
                return false;
              }

              return (
                this.selectedFieldID.indexes.field === indexes[0] && this.selectedFieldID.indexes.row === indexes[1]
              );

            case 'tablefootrow':
              if (this.selectedFieldID.fieldType !== 'tablefoot') {
                return false;
              }

              // there can only be one footer row for table foots, so you only need to make sure the selected child is from the same table as the parent
              return this.selectedFieldID.indexes.field === indexes[0];
          }
          break;

        case 'content':
          switch (parentFieldType) {
            case 'table':
              if (
                this.selectedFieldID.fieldType !== 'tablehead' &&
                this.selectedFieldID.fieldType !== 'tablebody' &&
                this.selectedFieldID.fieldType !== 'tablefoot'
              ) {
                return false;
              }

              return (
                this.selectedFieldID.indexes.page === indexes[0] && this.selectedFieldID.indexes.field === indexes[1]
              );

            case 'tableheadrow':
              if (this.selectedFieldID.fieldType !== 'tablehead') {
                return false;
              }

              // there can only be one header row for table heads, so you only need to make sure the selected child is from the same table as the parent
              return (
                this.selectedFieldID.indexes.page === indexes[0] && this.selectedFieldID.indexes.field === indexes[1]
              );

            case 'tablebodyrow':
              if (this.selectedFieldID.fieldType !== 'tablebody') {
                return false;
              }

              return (
                this.selectedFieldID.indexes.page === indexes[0] &&
                this.selectedFieldID.indexes.field === indexes[1] &&
                this.selectedFieldID.indexes.row === indexes[2]
              );

            case 'tablefootrow':
              if (this.selectedFieldID.fieldType !== 'tablefoot') {
                return false;
              }

              // there can only be one footer row for table foots, so you only need to make sure the selected child is from the same table as the parent
              return (
                this.selectedFieldID.indexes.page === indexes[0] && this.selectedFieldID.indexes.field === indexes[1]
              );
          }
          break;
      }
    },
    clearSelectedField: function () {
      // do not clear selected page index here, that should only be cleared when a template is unloaded
      this.selectedFieldID = null;
      this.selectedFieldIsImage = false;
      this.selectedFieldIsLooped = false;
      this.selectedFieldLoopingFieldKey = '';
      this.selectedFieldLoopingMarginInBetween = '';
      this.selectedFieldLoopingDepthLevels = [];

      this.selectedFieldTextContent = '';
      this.selectedFieldColumnSizing = [];
      this.selectedField = null;
      this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
        return fontFamily.name === 'Arial';
      });
      this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
        return borderStyle.style === 'solid';
      });
    },
    // creates a new string field and returns it
    createNewStringField: function () {
      return {
        type: 'string',
        value: 'Text',
        isImage: false,
        CSSStyles: {
          position: 'relative',
          width: '100%',

          borderTopWidth: '0mm',
          borderRightWidth: '0mm',
          borderBottomWidth: '0mm',
          borderLeftWidth: '0mm',
          borderStyle: 'solid',
          borderColor: 'black',

          paddingTop: '0mm',
          paddingRight: '0mm',
          paddingBottom: '0mm',
          paddingLeft: '0mm',

          marginTop: '0mm',
          marginRight: '0mm',
          marginBottom: '0mm',
          marginLeft: '0mm',

          left: '0mm',
          top: '0mm',

          height: 'fit-content',
          fontFamily: 'Arial, Helvetica, sans-serif',
          backgroundColor: 'transparent',
          fontSize: '4mm',
          textAlign: 'left',
        },
        styleInputValues: {
          // used to store input value for styles that have extra configurations
          // e.g. border has the no border checkbox, which essentially overrides the border inputs
          // height has a fit-height checkbox, which also overrides the height input
          // for styles that aren't like the above, the inputs will directly modify CSSStyles
          // this way, it reduces the number of event listeners and watchers needed

          noBorder: true,
          borderTopValue: '0.1mm', // different from the border widths set in css
          borderRightValue: '0.1mm', // so that when the user toggles the no border checkbox, the border will appear automatically
          borderBottomValue: '0.1mm', // without the user having to adjust the border width
          borderLeftValue: '0.1mm',

          fitHeight: true,
          heightValue: '5.5mm',
        },
        conditionalRendering: {
          // is slightly different from style input values in that the method of hiding the field needs to differ depending on how it's configured,
          // it doesn't change how the report is rendered when being designed, it only affects the final render
          // whereas style input values will only affect the CSS and will change the look for the report immediately once the value has been changed
          // which is why this has it's own object instead of being combined with style input values

          isConditionallyRendered: false,
          dataKey: '',
          condition: 'Truthy',
          operator: 'AND', // only works for fields in the header or footer, but create these props regardless
          onPages: 'All Pages', // just update the magic value accordingly based on whether the field is within the header and footer or not
          preserveSpace: false,
          renderif: '', // this is the resolved magic value based on the configuration, needs to be stored separately because the isConditionallyRendered checkbox essentially overrides the inputs, similar to border styling
          // empty string if not conditionally rendered
          // else if field is in header or footer: {dataKey},{condition},{preserveSpace},{operator},{onPage}
          // else {dataKey},{condition},{preserveSpace}
        },
        looping: {
          // text fields can only specify a field key and the margin between repeated fields

          isLooped: false,
          fieldKey: '',
          marginInBetween: '0mm',
          loopon: '', // this is the resolved magic value based on the configuration, needs to be stored separately because the isLooped checkbox essentially overrides the inputs, similar to border styling
          // empty string if not looped
          // else: {fieldKey},{marginInBetween}
        },
      };
    },
    createNewTableCell: function (colSpan, cellNumber) {
      // creates a new table cell with the specified column span, and returns it
      // cell number is optional
      return {
        value: cellNumber ? `Cell ${cellNumber}` : 'New Cell',
        isImage: false,
        colSpan,
        CSSStyles: {
          borderTopWidth: 'inherit',
          borderRightWidth: 'inherit',
          borderBottomWidth: 'inherit',
          borderLeftWidth: 'inherit',
          borderStyle: 'inherit',
          borderColor: 'inherit',

          paddingTop: 'inherit',
          paddingRight: 'inherit',
          paddingBottom: 'inherit',
          paddingLeft: 'inherit',

          fontFamily: 'inherit',
          fontSize: 'inherit',
          textAlign: 'inherit',
          verticalAlign: 'inherit',
          backgroundColor: 'transparent',
        },
        styleInputValues: {
          noBorder: false,
          borderTopValue: 'inherit',
          borderRightValue: 'inherit',
          borderBottomValue: 'inherit',
          borderLeftValue: 'inherit',
        },
      };
    },
    createNewTableRow: function (columnSpans) {
      // creates a new table row with the number of cells specified, and returns it

      // ============= on second thought, this function should allow specifying of column widths as well

      const dividedColumnWidth = parseFloat((100 / columnSpans.length).toFixed(6));
      const columnWidthIsEvenlyDivided = dividedColumnWidth * columnSpans.length === 100;

      return {
        CSSStyles: {
          borderTopWidth: 'inherit',
          borderRightWidth: 'inherit',
          borderBottomWidth: 'inherit',
          borderLeftWidth: 'inherit',
          borderStyle: 'inherit',
          borderColor: 'inherit',

          paddingTop: 'inherit',
          paddingRight: 'inherit',
          paddingBottom: 'inherit',
          paddingLeft: 'inherit',

          fontFamily: 'inherit',
          fontSize: 'inherit',
          textAlign: 'inherit',
          verticalAlign: 'inherit',
          backgroundColor: 'transparent',
        },
        styleInputValues: {
          noBorder: false,
          borderTopValue: 'inherit',
          borderRightValue: 'inherit',
          borderBottomValue: 'inherit',
          borderLeftValue: 'inherit',
        },
        columnWidths: Array.from({ length: columnSpans.length }, (_element, index) => {
          // all widths should sum up to 100% to prevent any weird behaviour
          // and the number of widths need to match the number of cells in this row

          if (index !== columnSpans.length - 1) {
            // if not the last column
            return `${dividedColumnWidth}%`;
          }

          if (columnWidthIsEvenlyDivided) {
            // last column but column width can be evenly divided
            return `${dividedColumnWidth}%`;
          }

          // if column width can't be evenly divided
          // to handle cases where the divided widths don't add up to exactly 100%
          // for example, splitting 100% across 3 columns means each column gets 33.33%, but that adds up to 99.99% and not 100%
          // in these scenarios, add extra width to the last column to make up for the difference
          const sumOfOtherColumnWidths = dividedColumnWidth * (columnSpans.length - 1);
          const remainder = (100 - sumOfOtherColumnWidths).toFixed(6);
          return `${remainder}%`;
        }),
        cell: columnSpans.map((columnSpan, index) => this.createNewTableCell(columnSpan, index + 1)),
      };
    },
    createNewTableField: function () {
      // creates a new table field with one row containing two cells and no table header, and returns it
      return {
        type: 'table',
        CSSStyles: {
          position: 'relative',
          width: '100%',

          borderTopWidth: '0mm',
          borderRightWidth: '0mm',
          borderBottomWidth: '0mm',
          borderLeftWidth: '0mm',
          borderStyle: 'solid',
          borderColor: 'black',

          paddingTop: '0mm',
          paddingRight: '0mm',
          paddingBottom: '0mm',
          paddingLeft: '0mm',

          marginTop: '0mm',
          marginRight: '0mm',
          marginBottom: '0mm',
          marginLeft: '0mm',

          left: '0mm',
          top: '0mm',

          height: 'fit-content',
          fontFamily: 'Arial, Helvetica, sans-serif',
          fontSize: '4mm',
          textAlign: 'left',
          verticalAlign: 'middle',
          backgroundColor: 'transparent',

          // tableLayout: 'fixed', // Set the table layout style to be fixed in order for the column 100% width to be the same width for all columns

          // ============ can no longer use table-layout fixed with the introduction of merged cells
          // table-layout fixed will cause the column widths to be taken from the first row in the table, the rest will be ignored
          // https://stackoverflow.com/a/61333821
          // https://developer.mozilla.org/en-US/docs/Web/CSS/table-layout#fixed
          // then comes the problem of how to handle column widths
          // creating the table and adding new rows shouldn't be an issue, the number of columns is known, just evenly distribute the width across all columns
          // adding and deleting columns will be an issue, not all devs will use percentages when setting column widths, hard to calculate and adjust the widths of all columns accordingly
          // or i could just leave it at 100% and let the dev handle it themselves
        },
        styleInputValues: {
          // used to store input value for styles that have extra configurations
          // e.g. border has the no border checkbox, which essentially overrides the border inputs
          // height has a fit-height checkbox, which also overrides the height input
          // for styles that aren't like the above, the inputs will directly modify CSSStyles
          // this way, it reduces the number of event listeners and watchers needed

          noBorder: true,
          borderTopValue: '0.1mm',
          borderRightValue: '0.1mm',
          borderBottomValue: '0.1mm',
          borderLeftValue: '0.1mm',

          fitHeight: true,
          heightValue: '5.5mm',
        },
        tableDetails: {
          // ================== consider renaming in the future?
          row: [this.createNewTableRow([1, 1])],
        },
        conditionalRendering: {
          // is slightly different from style input values in that the method of hiding the field needs to differ depending on how it's configured,
          // it doesn't change how the report is rendered when being designed, it only affects the final render
          // whereas style input values will only affect the CSS and will change the look for the report immediately once the value has been changed
          // which is why this has it's own object instead of being combined with style input values
          // also, this applies to the entire table, it doesn't support hiding individual rows or cells

          isConditionallyRendered: false,
          dataKey: '',
          condition: 'Truthy',
          operator: 'AND', // only works for fields in the header or footer, but create these props regardless
          onPages: 'All Pages', // just update the magic value accordingly based on whether the field is within the header and footer or not
          preserveSpace: false,
          renderif: '', // this is the resolved magic value based on the configuration, needs to be stored separately because the isConditionallyRendered checkbox essentially overrides the inputs, similar to border styling
          // empty string if not conditionally rendered
          // else if field is in header or footer: {dataKey},{condition},{preserveSpace},{operator},{onPage}
          // else {dataKey},{condition},{preserveSpace}
        },
        looping: {
          // table fields can specify a field key, the margin between repeated fields
          // and any number of depth level keys, along with each of their respective row numbers

          isLooped: false,
          fieldKey: '',
          marginInBetween: '0mm',
          depthLevels: [
            // { sample depth level object
            //   depthKey: '',
            //   fromRow: '1',
            //   toRow: '1'
            // }
          ],
          loopon: '', // this is the resolved magic value based on the configuration, needs to be stored separately because the isLooped checkbox essentially overrides the inputs, similar to border styling
          // empty string if not looped
          // if only fieldKey is specified: {fieldKey},{marginInBetween};
          // if only depth keys are specified: ;{depthKey1},{from},{to};{depthKey2},{from},{to};.......
          // if both are specified: {fieldKey},{marginInBetween};{depthKey1},{from},{to};{depthKey2},{from},{to};........
        },
      };
    },
    // Handled when the add field is clicked
    addFieldClicked: function (type) {
      if (!this.selectedReportSection) {
        // if no report section is selected
        this.reportSectionErrorMessage = 'Select a report section first!';
        return;
      }

      if (this.pages.length === 0) {
        this.$showAlert('error', 'No Pages Added', 'Add at least one page before adding fields', [{ text: 'OK' }]);
        return;
      }

      let fieldToAdd = null;
      switch (type) {
        case 'string':
          fieldToAdd = this.createNewStringField();
          break;

        case 'table':
          fieldToAdd = this.createNewTableField();
          break;
      }

      if (this.selectedFieldID) {
        // if a field is selected, add the new field directly below the selected field
        const { reportSection } = this.selectedFieldID;
        switch (reportSection) {
          case 'header':
            {
              const { field: fieldIndex } = this.selectedFieldID.indexes;
              this.header.splice(fieldIndex + 1, 0, fieldToAdd);
            }
            break;

          case 'content':
            {
              const { page: pageIndex, field: fieldIndex } = this.selectedFieldID.indexes;
              this.pages[pageIndex].fields.splice(fieldIndex + 1, 0, fieldToAdd);
            }
            break;

          case 'footer':
            {
              const { field: fieldIndex } = this.selectedFieldID.indexes;
              this.footer.splice(fieldIndex + 1, 0, fieldToAdd);
            }
            break;
        }
        return;
      }

      // else, add the new field to the end of the selected section
      switch (this.selectedReportSection['name']) {
        case 'Header':
          this.header.push(fieldToAdd);
          break;

        case 'Content':
          this.pages[this.selectedPageIndex].fields.push(fieldToAdd);
          break;

        case 'Footer':
          this.footer.push(fieldToAdd);
          break;
      }
    },
    updateConditionalRenderingRenderif: async function () {
      // the input fields cannot be synced to the selected field object, since the conditionalRendering property only exists on the root field objects
      // the conditionalRendering property doesn't exist on the table row and table cell objects
      // this function updates the root field objects whenever an input change is made

      await this.$nextTick(); // wait for the input changes to be emitted and the data members to be updated

      const conditionalRenderingObjectOfSelectedField = this.getSelectedField().conditionalRendering;
      conditionalRenderingObjectOfSelectedField.isConditionallyRendered = this.selectedFieldIsConditionallyRendered;
      conditionalRenderingObjectOfSelectedField.dataKey = this.selectedFieldConditionalRenderingDataKey;
      conditionalRenderingObjectOfSelectedField.condition = this.selectedFieldConditionalRenderingCondition?.name ?? ''; // just in case the dropdown was cleared
      conditionalRenderingObjectOfSelectedField.operator = this.selectedFieldConditionalRenderingOperator?.name ?? '';
      conditionalRenderingObjectOfSelectedField.onPages = this.selectedFieldConditionalRenderingOnPages?.name ?? '';
      conditionalRenderingObjectOfSelectedField.preserveSpace = this.selectedFieldConditionalRenderingPreserveSpace;

      if (!conditionalRenderingObjectOfSelectedField.isConditionallyRendered) {
        conditionalRenderingObjectOfSelectedField.renderif = '';
        return;
      }

      conditionalRenderingObjectOfSelectedField.renderif = `${conditionalRenderingObjectOfSelectedField.dataKey},${conditionalRenderingObjectOfSelectedField.condition},${conditionalRenderingObjectOfSelectedField.preserveSpace}`;
      if (this.selectedFieldIsInHeaderOrFooter) {
        conditionalRenderingObjectOfSelectedField.renderif += `,${conditionalRenderingObjectOfSelectedField.operator},${conditionalRenderingObjectOfSelectedField.onPages}`;
      }

      // ============= remove later if not needed
      // const { reportSection, fieldType } = this.selectedFieldID;
      // switch (fieldType) {
      //   case 'string':
      //     if (changedField === 'condition') {
      //       // because condition is a dropdown, and there rest are inputs and checkboxes
      //       this.selectedField.conditionalRendering[changedField] = newValue?.name ?? ''; // just in case the dropdown was cleared
      //     } else {
      //       this.selectedField.conditionalRendering[changedField] = newValue;
      //     }

      //     if (changedField === 'isConditionallyRendered') {
      //       // depending on whether this checkbox is checked or not, renderif either needs to be cleared or populated
      //       if (newValue) {
      //         this.selectedField.conditionalRendering.renderif = `${this.selectedField.conditionalRendering.dataKey},${this.selectedField.conditionalRendering.condition},${this.selectedField.conditionalRendering.preserveSpace}`;
      //       } else {
      //         this.selectedField.conditionalRendering.renderif = '';
      //       }
      //     } else {
      //       // any changes to the other controls should always populate renderif
      //       this.selectedField.conditionalRendering.renderif = `${this.selectedField.conditionalRendering.dataKey},${this.selectedField.conditionalRendering.condition},${this.selectedField.conditionalRendering.preserveSpace}`;
      //     }
      //     break;

      //   case 'tablehead':
      //   case 'tablebody':
      //   case 'tablefoot':
      //     {
      //       // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
      //       const { page: pageIndex, field: fieldIndex } = this.selectedFieldID.indexes;
      //       let associatedField;

      //       switch (reportSection) {
      //         case 'header':
      //           associatedField = this.header[fieldIndex];
      //           break;

      //         case 'content':
      //           associatedField = this.pages[pageIndex].fields[fieldIndex];
      //           break;

      //         case 'footer':
      //           associatedField = this.footer[fieldIndex];
      //           break;
      //       }

      //       if (changedField === 'condition') {
      //         // because condition is a dropdown, and there rest are inputs and checkboxes
      //         associatedField.conditionalRendering[changedField] = newValue?.name ?? ''; // just in case the dropdown was cleared
      //       } else {
      //         associatedField.conditionalRendering[changedField] = newValue;
      //       }

      //       if (changedField === 'isConditionallyRendered') {
      //         // depending on whether this checkbox is checked or not, renderif either needs to be cleared or populated
      //         if (newValue) {
      //           associatedField.conditionalRendering.renderif = `${associatedField.conditionalRendering.dataKey},${associatedField.conditionalRendering.condition},${associatedField.conditionalRendering.preserveSpace}`;
      //         } else {
      //           associatedField.conditionalRendering.renderif = '';
      //         }
      //       } else {
      //         // any changes to the other controls should always populate renderif
      //         associatedField.conditionalRendering.renderif = `${associatedField.conditionalRendering.dataKey},${associatedField.conditionalRendering.condition},${associatedField.conditionalRendering.preserveSpace}`;
      //       }
      //     }
      //     break;
      // }
    },
    isImageCheckBoxClicked: function (isChecked) {
      const { reportSection, fieldType } = this.selectedFieldID;

      // ================= simplify this later
      switch (reportSection) {
        case 'header':
          switch (fieldType) {
            case 'string':
              this.selectedField.isImage = isChecked;
              break;

            case 'tablehead':
              {
                // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                const { field: fieldIndex, cell: cellIndex } = this.selectedFieldID.indexes;
                this.header[fieldIndex].tableDetails.header.cell[cellIndex].isImage = isChecked;
              }
              break;

            case 'tablebody':
              {
                // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                const { field: fieldIndex, row: rowIndex, cell: cellIndex } = this.selectedFieldID.indexes;
                this.header[fieldIndex].tableDetails.row[rowIndex].cell[cellIndex].isImage = isChecked;
              }
              break;

            case 'tablefoot':
              {
                // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                const { field: fieldIndex, cell: cellIndex } = this.selectedFieldID.indexes;
                this.header[fieldIndex].tableDetails.footer.cell[cellIndex].isImage = isChecked;
              }
              break;
          }
          break;

        case 'content':
          switch (fieldType) {
            case 'string':
              this.selectedField.isImage = isChecked;
              break;

            case 'tablehead':
              {
                // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                const { page: pageIndex, field: fieldIndex, cell: cellIndex } = this.selectedFieldID.indexes;
                this.pages[pageIndex].fields[fieldIndex].tableDetails.header.cell[cellIndex].isImage = isChecked;
              }
              break;

            case 'tablebody':
              {
                // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                const {
                  page: pageIndex,
                  field: fieldIndex,
                  row: rowIndex,
                  cell: cellIndex,
                } = this.selectedFieldID.indexes;
                this.pages[pageIndex].fields[fieldIndex].tableDetails.row[rowIndex].cell[cellIndex].isImage = isChecked;
              }
              break;

            case 'tablefoot':
              {
                // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                const { page: pageIndex, field: fieldIndex, cell: cellIndex } = this.selectedFieldID.indexes;
                this.pages[pageIndex].fields[fieldIndex].tableDetails.footer.cell[cellIndex].isImage = isChecked;
              }
              break;
          }
          break;

        case 'footer':
          switch (fieldType) {
            case 'string':
              this.selectedField.isImage = isChecked;
              break;

            case 'tablehead':
              {
                // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                const { field: fieldIndex, cell: cellIndex } = this.selectedFieldID.indexes;
                this.footer[fieldIndex].tableDetails.header.cell[cellIndex].isImage = isChecked;
              }
              break;

            case 'tablebody':
              {
                // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                const { field: fieldIndex, row: rowIndex, cell: cellIndex } = this.selectedFieldID.indexes;
                this.footer[fieldIndex].tableDetails.row[rowIndex].cell[cellIndex].isImage = isChecked;
              }
              break;

            case 'tablefoot':
              {
                // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                const { field: fieldIndex, cell: cellIndex } = this.selectedFieldID.indexes;
                this.footer[fieldIndex].tableDetails.footer.cell[cellIndex].isImage = isChecked;
              }
              break;
          }
          break;
      }
    },
    // Handles text style format (bold, italics and underline)
    textContentFormatStyleClicked: function (type) {
      // Get the textarea element
      const textarea = this.$refs['valueTextarea'];
      // Set the start and end tag
      const tagStart = `<${type}>`;
      const tagEnd = `</${type}>`;

      // Get the current selection position within the textarea
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;

      // Get the left section, mid section (selected text) and right section of the whole text
      const leftSectionText = this.selectedFieldTextContent.substring(0, start);
      let midSectionText = this.selectedFieldTextContent.substring(start, end);
      const rightSectionText = this.selectedFieldTextContent.substring(end);

      // Finding if there is any tag within the selection
      const tagWithinSelection = midSectionText.match(new RegExp(`${tagStart}|${tagEnd}`, 'g'));
      if (tagWithinSelection) {
        // If there is at lease a tag within the selection
        if (midSectionText.startsWith(tagStart) && midSectionText.endsWith(tagEnd)) {
          // If the current selection starts and ends with the same tag. Eg of selection is: <b>my text</b>
          // Remove the tag from the mid section
          midSectionText = midSectionText.replace(new RegExp(`${tagStart}|${tagEnd}`, 'g'), '');
        } else {
          // The current selection contains 1 or more tag
          // Get the start and end text in the mid section
          const midStartTags = midSectionText.match(new RegExp(tagStart, 'g')) || [];
          const midEndTags = midSectionText.match(new RegExp(tagEnd, 'g')) || [];

          if (midStartTags.length == midEndTags.length) {
            // If the current selection has the same amount of start and end tag
            // Checking if there is any reversed tag. Eg: my</b> text <b>here
            const midReversedTags = midSectionText.match(new RegExp(`${tagEnd}(.*?)${tagStart}`, 'g'));
            if (midReversedTags) {
              // If contains reversed tag
              // Remove all the tags within the mid section
              midSectionText = `${midSectionText.replace(new RegExp(`${tagStart}|${tagEnd}`, 'g'), '')}`;
            } else {
              // Do not contains reversed tag
              // Clear the tags from mid section, then wrap the mid section with the tag
              midSectionText = `${tagStart}${midSectionText.replace(new RegExp(`${tagStart}|${tagEnd}`, 'g'), '')}${tagEnd}`;
            }
          } else if (midStartTags.length > midEndTags.length) {
            // If the current selection has more start tags than end tags
            // Clear the tags from mid section, then add a start tag at the start of the selection
            midSectionText = `${tagStart}${midSectionText.replace(new RegExp(`${tagStart}|${tagEnd}`, 'g'), '')}`;
          } else if (midEndTags.length > midStartTags.length) {
            // If the current selection has more end tags than start tags
            // Clear the tags from the mid section, then add an end tag at the end of the selection
            midSectionText = `${midSectionText.replace(new RegExp(`${tagStart}|${tagEnd}`, 'g'), '')}${tagEnd}`;
          }
        }
      } else {
        // No tag found within the selection
        // Get the start and end tags of the left section
        const leftStartTags = leftSectionText.match(new RegExp(tagStart, 'g')) || [];
        const leftEndTags = leftSectionText.match(new RegExp(tagEnd, 'g')) || [];

        if (leftStartTags.length == leftEndTags.length) {
          // If the start and end tag has the same amount
          // Wrap the selection with the start and end tag
          midSectionText = `${tagStart}${midSectionText}${tagEnd}`;
        } else if (leftStartTags.length > leftEndTags.length) {
          // If start tags is more than end tag
          // Remove all the tag from the mid section then wrap the selection with end tag first then start tag
          midSectionText = `${tagEnd}${midSectionText.replace(new RegExp(`${tagStart}|${tagEnd}`, 'g'), '')}${tagStart}`;
        }
      }

      this.selectedFieldTextContent = `${leftSectionText}${midSectionText}${rightSectionText}`;

      // Focus on the text area element
      textarea.focus();
      this.$nextTick(() => {
        // Get the start index of the mid section (selection)
        const rangeStart = this.selectedFieldTextContent.indexOf(midSectionText);
        // Get the end index of the mid section
        const rangeEnd = rangeStart + midSectionText.length;
        // Re-select the selection including the newly added tags
        textarea.setSelectionRange(rangeStart, rangeEnd);
      });
    },

    addDepthLevelClicked: function () {
      this.selectedFieldLoopingDepthLevels.push({
        depthKey: '',
        fromRow: '1',
        toRow: '1',
      });
      this.updateSelectedFieldLoopon();
    },
    deleteDepthLevelClicked: function (index) {
      // =========== add confirmation message
      this.selectedFieldLoopingDepthLevels.splice(index, 1);
      this.updateSelectedFieldLoopon();
    },
    updateSelectedFieldLoopon: async function () {
      // the input fields cannot be synced to the selected field object, since the looping property only exists on the root field objects
      // the looping property doesn't exist on the table row and table cell objects
      // this function updates the root field objects whenever an input change is made

      // ========== maybe add validators to the input fields as necessary to prevent semicolons and commas
      // ========== and maybe do the same with conditional rendering

      await this.$nextTick(); // wait for the input changes to be emitted and the data members to be updated

      const loopingObjectOfSelectedField = this.getSelectedField().looping;
      loopingObjectOfSelectedField.isLooped = this.selectedFieldIsLooped;
      loopingObjectOfSelectedField.fieldKey = this.selectedFieldLoopingFieldKey;
      loopingObjectOfSelectedField.marginInBetween = this.selectedFieldLoopingMarginInBetween;
      if (this.selectedFieldIsTable) {
        loopingObjectOfSelectedField.depthLevels = this.selectedFieldLoopingDepthLevels;
      }

      if (!this.selectedFieldIsLooped) {
        loopingObjectOfSelectedField.loopon = '';
        return;
      }

      loopingObjectOfSelectedField.loopon = `${this.selectedFieldLoopingFieldKey},${this.selectedFieldLoopingMarginInBetween}`;
      if (!this.selectedFieldIsTable) {
        // string fields can only have a field key and some margin in between each repeated field
        return;
      }

      // table fields can specify several depths of looping
      loopingObjectOfSelectedField.loopon += ';';
      loopingObjectOfSelectedField.loopon += this.selectedFieldLoopingDepthLevels
        .map(({ depthKey, fromRow, toRow }) => `${depthKey},${fromRow},${toRow}`)
        .join(';');
    },

    addRemoveTableHeaderClicked: function () {
      // ============== remove if no longer needed
      // const { reportSection } = this.selectedFieldID;
      // switch (reportSection) {
      //   case 'header':
      //     {
      //       const { field: fieldIndex } = this.selectedFieldID.indexes;

      //       if (this.selectedTableFieldHasHeader) {
      //         if (this.header[fieldIndex].tableDetails.row.length === 0) {
      //           this.deleteTableClicked(); // this function will prompt the user and delete the entire table if user responded positively
      //           return;
      //         }

      //         const clonedHeader = structuredClone(this.header);
      //         delete clonedHeader[fieldIndex].tableDetails.header;
      //         this.header = clonedHeader;
      //         if (this.selectedFieldID.fieldType === 'tablehead') {
      //           // if the user clicked on the table header, and then clicked on remove header
      //           this.clearSelectedField(); // the selected field ID needs to be cleared, because it will point to a non-existant field after the table header was removed
      //         }
      //         return;
      //       }

      //       const numberOfCells = this.header[fieldIndex].tableDetails.row[0].cell.length; // access the first row instead of using row index from selected field ID, because row index won't exist if the user clicked on the table head or table foot
      //       const headerRowToAdd = this.createNewTableRow(numberOfCells);

      //       // need to create a deep clone and reassign after mutating, otherwise the DOM won't update
      //       // not sure why a deep clone is not needed in other methods that mutate the fields
      //       // https://developer.mozilla.org/en-US/docs/Web/API/structuredClone
      //       const clonedHeader = structuredClone(this.header);
      //       clonedHeader[fieldIndex].tableDetails.header = headerRowToAdd;
      //       this.header = clonedHeader;
      //     }
      //     break;

      //   case 'content':
      //     {
      //       const { page: pageIndex, field: fieldIndex } = this.selectedFieldID.indexes;

      //       if (this.selectedTableFieldHasHeader) {
      //         if (this.pages[pageIndex].fields[fieldIndex].tableDetails.row.length === 0) {
      //           this.deleteTableClicked(); // this function will prompt the user and delete the entire table if user responded positively
      //           return;
      //         }

      //         const clonedPages = structuredClone(this.pages);
      //         delete clonedPages[pageIndex].fields[fieldIndex].tableDetails.header;
      //         this.pages = clonedPages;
      //         if (this.selectedFieldID.fieldType === 'tablehead') {
      //           // if the user clicked on the table header, and then clicked on remove header
      //           this.clearSelectedField(); // the selected field ID needs to be cleared, because it will point to a non-existant field after the table header was removed
      //         }
      //         return;
      //       }

      //       const numberOfCells = this.pages[pageIndex].fields[fieldIndex].tableDetails.row[0].cell.length; // access the first row instead of using row index from selected field ID, because row index won't exist if the user clicked on the table head or table foot
      //       const headerRowToAdd = this.createNewTableRow(numberOfCells);

      //       // need to create a deep clone and reassign after mutating, otherwise the DOM won't update
      //       // not sure why a deep clone is not needed in other methods that mutate the fields
      //       // https://developer.mozilla.org/en-US/docs/Web/API/structuredClone
      //       const clonedPages = structuredClone(this.pages);
      //       clonedPages[pageIndex].fields[fieldIndex].tableDetails.header = headerRowToAdd;
      //       this.pages = clonedPages;
      //     }
      //     break;

      //   case 'footer':
      //     {
      //       const { field: fieldIndex } = this.selectedFieldID.indexes;

      //       if (this.selectedTableFieldHasHeader) {
      //         if (this.footer[fieldIndex].tableDetails.row.length === 0) {
      //           this.deleteTableClicked(); // this function will prompt the user and delete the entire table if user responded positively
      //           return;
      //         }

      //         const clonedFooter = structuredClone(this.footer);
      //         delete clonedFooter[fieldIndex].tableDetails.header;
      //         this.footer = clonedFooter;
      //         if (this.selectedFieldID.fieldType === 'tablehead') {
      //           // if the user clicked on the table header, and then clicked on remove header
      //           this.clearSelectedField(); // the selected field ID needs to be cleared, because it will point to a non-existant field after the table header was removed
      //         }
      //         return;
      //       }

      //       const numberOfCells = this.footer[fieldIndex].tableDetails.row[0].cell.length; // access the first row instead of using row index from selected field ID, because row index won't exist if the user clicked on the table head or table foot
      //       const headerRowToAdd = this.createNewTableRow(numberOfCells);

      //       // need to create a deep clone and reassign after mutating, otherwise the DOM won't update
      //       // not sure why a deep clone is not needed in other methods that mutate the fields
      //       // https://developer.mozilla.org/en-US/docs/Web/API/structuredClone
      //       const clonedFooter = structuredClone(this.footer);
      //       clonedFooter[fieldIndex].tableDetails.header = headerRowToAdd;
      //       this.footer = clonedFooter;
      //     }
      //     break;
      // }

      const removingHeader = this.selectedTableFieldHasHeader; // needs to be stored in a constant because the original value is needed after the fields have been mutated
      const bodyRows = this.getBodyRowsOfSelectedTableField();
      if (removingHeader && bodyRows.length === 0) {
        this.deleteTableClicked(); // this function will prompt the user and delete the entire table if user responded positively
        return;
      }

      const clonedFields = structuredClone(this.getAllFieldsWithinReportSectionOfSelectedField());
      // need to create a deep clone and reassign after mutating, otherwise the DOM won't update
      // not sure why a deep clone isn't needed in other methods that mutate the fields
      const selectedTableFieldIndex = this.getFieldIndexOfSelectedField();

      if (removingHeader) {
        delete clonedFields[selectedTableFieldIndex].tableDetails.header;
      } else {
        const numberOfUnitCells = bodyRows[0].cell.reduce((sum, currentCell) => (sum += currentCell.colSpan), 0); // access the first row instead of using row index from selected field ID, because row index won't exist if the user clicked on the table head or table foot
        clonedFields[selectedTableFieldIndex].tableDetails.header = this.createNewTableRow(
          Array.from({ length: numberOfUnitCells }, () => 1),
        );
        // ============= on second thought, header rows should follow the column widths of the row it is based off of (which is hardcoded to always be the first row of the table)
        // ============= currently, when editing an existing template and adding a table header, it is very likely for the table to go out of proportion
        // ============= because the column widths are equally divided into however many cells there are in the header row, which can be vastly different to how the table body rows are configured
        // ============= having the header row follow the column widths of an existing row should somewhat overcome this
      }
      this.setReportSectionOfSelectedField(clonedFields);

      if (removingHeader && this.getFieldTypeOfSelectedField() === 'tablehead') {
        // if the user clicked on the table header, and then clicked on remove header
        this.clearSelectedField(); // the selected field ID needs to be cleared, because it will point to a non-existant field after the table header was removed
      }
    },
    addRemoveTableFooterClicked: function (_pointerEvent) {
      // ============== remove if no longer needed
      // const { reportSection } = this.selectedFieldID;
      // switch (reportSection) {
      //   case 'header':
      //     {
      //       // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
      //       const { field: fieldIndex } = this.selectedFieldID.indexes;

      //       if (this.selectedTableFieldHasFooter) {
      //         if (this.header[fieldIndex].tableDetails.row.length === 0) {
      //           this.deleteTableClicked(); // this function will prompt the user and delete the entire table if user responded positively
      //           return;
      //         }

      //         const clonedHeader = structuredClone(this.header);
      //         delete clonedHeader[fieldIndex].tableDetails.footer;
      //         this.header = clonedHeader;
      //         if (this.selectedFieldID.fieldType === 'tablefoot') {
      //           // if the user clicked on the table footer, and then clicked on remove footer
      //           this.clearSelectedField(); // the selected field ID needs to be cleared, because it will point to a non-existant field after the table footer was removed
      //         }
      //         return;
      //       }

      //       const numberOfCells = this.header[fieldIndex].tableDetails.row[0].cell.length; // access the first row instead of using row index from selected field ID, because row index won't exist if the user clicked on the table head or table foot
      //       const footerRowToAdd = this.createNewTableRow(numberOfCells);

      //       // need to create a deep clone and reassign after mutating, otherwise the DOM won't update
      //       // not sure why a deep clone is not needed in other methods that mutate the fields
      //       // https://developer.mozilla.org/en-US/docs/Web/API/structuredClone
      //       const clonedHeader = structuredClone(this.header);
      //       clonedHeader[fieldIndex].tableDetails.footer = footerRowToAdd;
      //       this.header = clonedHeader;
      //     }
      //     break;

      //   case 'content':
      //     {
      //       // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
      //       const { page: pageIndex, field: fieldIndex } = this.selectedFieldID.indexes;

      //       if (this.selectedTableFieldHasFooter) {
      //         if (this.pages[pageIndex].fields[fieldIndex].tableDetails.row.length === 0) {
      //           this.deleteTableClicked(); // this function will prompt the user and delete the entire table if user responded positively
      //           return;
      //         }

      //         const clonedPages = structuredClone(this.pages);
      //         delete clonedPages[pageIndex].fields[fieldIndex].tableDetails.footer;
      //         this.pages = clonedPages;
      //         if (this.selectedFieldID.fieldType === 'tablefoot') {
      //           // if the user clicked on the table footer, and then clicked on remove footer
      //           this.clearSelectedField(); // the selected field ID needs to be cleared, because it will point to a non-existant field after the table footer was removed
      //         }
      //         return;
      //       }

      //       const numberOfCells = this.pages[pageIndex].fields[fieldIndex].tableDetails.row[0].cell.length; // access the first row instead of using row index from selected field ID, because row index won't exist if the user clicked on the table head or table foot
      //       const footerRowToAdd = this.createNewTableRow(numberOfCells);

      //       // need to create a deep clone and reassign after mutating, otherwise the DOM won't update
      //       // not sure why a deep clone is not needed in other methods that mutate the fields
      //       // https://developer.mozilla.org/en-US/docs/Web/API/structuredClone
      //       const clonedPages = structuredClone(this.pages);
      //       clonedPages[pageIndex].fields[fieldIndex].tableDetails.footer = footerRowToAdd;
      //       this.pages = clonedPages;
      //     }
      //     break;

      //   case 'footer':
      //     {
      //       // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
      //       const { field: fieldIndex } = this.selectedFieldID.indexes;

      //       if (this.selectedTableFieldHasFooter) {
      //         if (this.footer[fieldIndex].tableDetails.row.length === 0) {
      //           this.deleteTableClicked(); // this function will prompt the user and delete the entire table if user responded positively
      //           return;
      //         }

      //         const clonedFooter = structuredClone(this.footer);
      //         delete clonedFooter[fieldIndex].tableDetails.footer;
      //         this.footer = clonedFooter;
      //         if (this.selectedFieldID.fieldType === 'tablefoot') {
      //           // if the user clicked on the table footer, and then clicked on remove footer
      //           this.clearSelectedField(); // the selected field ID needs to be cleared, because it will point to a non-existant field after the table footer was removed
      //         }
      //         return;
      //       }

      //       const numberOfCells = this.footer[fieldIndex].tableDetails.row[0].cell.length; // access the first row instead of using row index from selected field ID, because row index won't exist if the user clicked on the table head or table foot
      //       const footerRowToAdd = this.createNewTableRow(numberOfCells);

      //       // need to create a deep clone and reassign after mutating, otherwise the DOM won't update
      //       // not sure why a deep clone is not needed in other methods that mutate the fields
      //       // https://developer.mozilla.org/en-US/docs/Web/API/structuredClone
      //       const clonedFooter = structuredClone(this.footer);
      //       clonedFooter[fieldIndex].tableDetails.footer = footerRowToAdd;
      //       this.footer = clonedFooter;
      //     }
      //     break;
      // }

      const removingFooter = this.selectedTableFieldHasFooter; // needs to be stored in a constant because the original value is needed after the fields have been mutated
      const bodyRows = this.getBodyRowsOfSelectedTableField();
      if (removingFooter && bodyRows.length === 0) {
        this.deleteTableClicked(); // this function will prompt the user and delete the entire table if user responded positively
        return;
      }

      const clonedFields = structuredClone(this.getAllFieldsWithinReportSectionOfSelectedField());
      // need to create a deep clone and reassign after mutating, otherwise the DOM won't update
      // not sure why a deep clone isn't needed in other methods that mutate the fields
      const selectedTableFieldIndex = this.getFieldIndexOfSelectedField();

      if (removingFooter) {
        delete clonedFields[selectedTableFieldIndex].tableDetails.footer;
      } else {
        const numberOfUnitCells = bodyRows[0].cell.reduce((sum, currentCell) => (sum += currentCell.colSpan), 0); // access the first row instead of using row index from selected field ID, because row index won't exist if the user clicked on the table head or table foot
        clonedFields[selectedTableFieldIndex].tableDetails.footer = this.createNewTableRow(
          Array.from({ length: numberOfUnitCells }, () => 1),
        );
        // ============= on second thought, header rows should follow the column widths of the row it is based off of (which is hardcoded to always be the first row of the table)
        // ============= currently, when editing an existing template and adding a table header, it is very likely for the table to go out of proportion
        // ============= because the column widths are equally divided into however many cells there are in the header row, which can be vastly different to how the table body rows are configured
        // ============= having the header row follow the column widths of an existing row should somewhat overcome this
      }
      this.setReportSectionOfSelectedField(clonedFields);

      if (removingFooter && this.getFieldTypeOfSelectedField() === 'tablefoot') {
        // if the user clicked on the table footer, and then clicked on remove footer
        this.clearSelectedField(); // the selected field ID needs to be cleared, because it will point to a non-existant field after the table footer was removed
      }
    },

    addRowClicked: function () {
      const fieldType = this.getFieldTypeOfSelectedField();
      const bodyRows = this.getBodyRowsOfSelectedTableField();
      const columnSpans = this.getCellsOfSelectedRowOfSelectedTableField().map((cell) => cell.colSpan);
      switch (fieldType) {
        case 'tablehead':
        case 'tablefoot':
          bodyRows.push(this.createNewTableRow(columnSpans));
          // ============= on second thought, new rows should follow the column widths of the row it is based off of
          // ============= currently, when editing an existing template and adding a new row, it is very likely for the table to go out of proportion
          // ============= because the column widths are equally divided into however many cells there are in the new row, which can be vastly different to how the other table rows are configured
          // ============= having the new row follow the column widths of an existing row should somewhat overcome this
          break;

        case 'tablebody': {
          const indexOfSelectedRow = this.getIndexOfSelectedRowOfSelectedTableField();
          bodyRows.splice(indexOfSelectedRow + 1, 0, this.createNewTableRow(columnSpans));
          // ============= on second thought, new rows should follow the column widths of the row it is based off of
          // ============= currently, when editing an existing template and adding a new row, it is very likely for the table to go out of proportion
          // ============= because the column widths are equally divided into however many cells there are in the new row, which can be vastly different to how the other table rows are configured
          // ============= having the new row follow the column widths of an existing row should somewhat overcome this
          break;
        }
      }
    },
    addColumnClicked: function () {
      // coining the terms "unit cell" and "unit column"
      // a unit cell is the smallest possible unmerged cell
      // the number of cells may vary per row depending on whether there are merged cells, but the number of unit cells will always be equal across all rows
      // example, a merged cell with a column span of 3 consists of 3 unit cells
      // a unit column is a column of unit cells

      // this function adds a unit column on the right of the rightmost unit cell of the selected cell
      // if the rightmost unit column of the selected cell falls within the middle of a merged cell in another row, the new unit cell will be added on the right on the intersected merged cell for that row

      // first, figure out where the new unit column should be inserted to by counting the number of unit cells up till the end of the selected cell
      const selectedRow = this.getSelectedRowOfSelectedTableField();
      const selectedCellIndex = this.getIndexOfSelectedCellOfSelectedTableField();
      let unitColumnToInsertAt = 0;
      for (let index = 0; index <= selectedCellIndex; index++) {
        unitColumnToInsertAt += selectedRow.cell[index].colSpan;
      }

      // add a unit cell to the header row if there is one
      if (this.selectedTableFieldHasHeader) {
        const headerRow = this.getHeaderRowOfSelectedTableField();
        let indexToInsertAt;
        let unitColumnsPassed = 0;
        for (let index = 0; index < headerRow.cell.length; index++) {
          unitColumnsPassed += headerRow.cell[index].colSpan;
          if (unitColumnsPassed >= unitColumnToInsertAt) {
            indexToInsertAt = index + 1; // insert after the selected unit column
            break;
          }
        }

        headerRow.cell.splice(indexToInsertAt, 0, this.createNewTableCell(1));

        // can't dynamically calculate the width of the new column based on the widths of the existing columns
        // because the existing column widths may already add up to 100%, and you don't know which one to deduct from to allocate to the new column
        // just hardcode it to 50% and let the developer handle it
        headerRow.columnWidths.splice(indexToInsertAt, 0, '50%');
      }

      // add a unit cell to each body row
      const bodyRows = this.getBodyRowsOfSelectedTableField();
      for (const bodyRow of bodyRows) {
        let indexToInsertAt;
        let unitColumnsPassed = 0;
        for (let index = 0; index < bodyRow.cell.length; index++) {
          unitColumnsPassed += bodyRow.cell[index].colSpan;
          if (unitColumnsPassed >= unitColumnToInsertAt) {
            indexToInsertAt = index + 1; // insert after the selected unit column
            break;
          }
        }

        bodyRow.cell.splice(indexToInsertAt, 0, this.createNewTableCell(1));

        // can't dynamically calculate the width of the new column based on the widths of the existing columns
        // because the existing column widths may already add up to 100%, and you don't know which one to deduct from to allocate to the new column
        // just hardcode it to 50% and let the developer handle it
        bodyRow.columnWidths.splice(indexToInsertAt, 0, '50%');
      }

      // add a unit cell to the footer row if there is one
      if (this.selectedTableFieldHasFooter) {
        const footerRow = this.getFooterRowOfSelectedTableField();
        let indexToInsertAt;
        let unitColumnsPassed = 0;
        for (let index = 0; index < footerRow.cell.length; index++) {
          unitColumnsPassed += footerRow.cell[index].colSpan;
          if (unitColumnsPassed >= unitColumnToInsertAt) {
            indexToInsertAt = index + 1; // insert after the selected unit column
            break;
          }
        }

        footerRow.cell.splice(indexToInsertAt, 0, this.createNewTableCell(1));

        // can't dynamically calculate the width of the new column based on the widths of the existing columns
        // because the existing column widths may already add up to 100%, and you don't know which one to deduct from to allocate to the new column
        // just hardcode it to 50% and let the developer handle it
        footerRow.columnWidths.splice(indexToInsertAt, 0, '50%');
      }
    },

    mergeLeftClicked: function (_pointerEvent) {
      const selectedRow = this.getSelectedRowOfSelectedTableField();
      const selectedCellIndex = this.getIndexOfSelectedCellOfSelectedTableField();

      selectedRow.columnWidths.splice(selectedCellIndex - 1, 1);
      selectedRow.cell[selectedCellIndex].colSpan += selectedRow.cell[selectedCellIndex - 1].colSpan;
      selectedRow.cell[selectedCellIndex].value =
        `${selectedRow.cell[selectedCellIndex - 1].value} ${selectedRow.cell[selectedCellIndex].value}`;
      selectedRow.cell.splice(selectedCellIndex - 1, 1);

      this.clearSelectedField(); // the selected field ID will reference a non-existant table cell if the rightmost cell is merged leftwards, which will cause errors
      // the selected field will move right with every merge, since the number of cells is decreasing but the selected ID isn't changing to compensate, which can be confusing to the user
      // clear selected field to prevent issues
    },
    mergeRightClicked: function (_pointerEvent) {
      const selectedRow = this.getSelectedRowOfSelectedTableField();
      const selectedCellIndex = this.getIndexOfSelectedCellOfSelectedTableField();

      selectedRow.columnWidths.splice(selectedCellIndex + 1, 1);
      selectedRow.cell[selectedCellIndex].colSpan += selectedRow.cell[selectedCellIndex + 1].colSpan;
      selectedRow.cell[selectedCellIndex].value += ` ${selectedRow.cell[selectedCellIndex + 1].value}`;
      selectedRow.cell.splice(selectedCellIndex + 1, 1);

      this.clearSelectedField(); // doesn't have the same issues as merging left, but still clear off the selected field so that the merge right behaviour is consistent with merge left
    },
    splitLeftClicked: function (_pointerEvent) {
      const selectedRow = this.getSelectedRowOfSelectedTableField();
      const selectedCellIndex = this.getIndexOfSelectedCellOfSelectedTableField();

      selectedRow.columnWidths.splice(selectedCellIndex, 0, '50%'); // need to update columnWidths too
      selectedRow.cell[selectedCellIndex].colSpan--;
      selectedRow.cell.splice(selectedCellIndex, 0, this.createNewTableCell(1));

      this.clearSelectedField(); // the selected field will move left with every split, since the number of cells is increasing but the selected ID isn't changing to compensate, which can be confusing to the user
      // clear selected field to prevent issues
    },
    splitRightClicked: function (_pointerEvent) {
      const selectedRow = this.getSelectedRowOfSelectedTableField();
      const selectedCellIndex = this.getIndexOfSelectedCellOfSelectedTableField();

      selectedRow.columnWidths.splice(selectedCellIndex + 1, 0, '50%'); // need to update columnWidths too
      selectedRow.cell[selectedCellIndex].colSpan--;
      selectedRow.cell.splice(selectedCellIndex + 1, 0, this.createNewTableCell(1));

      this.clearSelectedField(); // doesn't have the same issues as splitting left, but still clear off the selected field so that the split right behaviour is consistent with splitting left
    },

    deleteRowClicked: async function () {
      const deleteConfirmation = await this.$showAlert(
        'warning',
        'Delete Row',
        'Are you sure you want to delete the currently selected row?',
        [
          { text: 'Delete', color: 'red', textColor: 'white' },
          { text: 'Cancel', color: 'lightgray', textColor: 'black' },
        ],
      );

      if (deleteConfirmation === 'Cancel') {
        return;
      }
      // ============== simplify this later
      const { reportSection, fieldType } = this.selectedFieldID;
      switch (reportSection) {
        case 'header':
          {
            // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
            const { field: fieldIndex } = this.selectedFieldID.indexes;
            if (this.header[fieldIndex].tableDetails.row.length <= 1 && !this.selectedTableFieldHasHeader) {
              // if there is only one row, or for some reason there are no rows at all, and there is no header
              this.header.splice(fieldIndex, 1);
              break;
            }
          }

          switch (fieldType) {
            case 'tablehead':
              this.addRemoveTableHeaderClicked(); // the function will check if a header exists or not and remove it if it does
              break;

            case 'tablebody':
              {
                // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                const { field: fieldIndex, row: rowIndex } = this.selectedFieldID.indexes;
                this.header[fieldIndex].tableDetails.row.splice(rowIndex, 1);
              }
              break;

            case 'tablefoot':
              this.addRemoveTableFooterClicked(); // the function will check if a footer exists or not and remove it if it does
              break;
          }
          break;

        case 'content':
          {
            // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
            const { page: pageIndex, field: fieldIndex } = this.selectedFieldID.indexes;
            if (
              this.pages[pageIndex].fields[fieldIndex].tableDetails.row.length <= 1 &&
              !this.selectedTableFieldHasHeader
            ) {
              // if there is only one row, or for some reason there are no rows at all, and there is no header
              this.pages[pageIndex].fields.splice(fieldIndex, 1);
              break;
            }
          }

          switch (fieldType) {
            case 'tablehead':
              this.addRemoveTableHeaderClicked(); // the function will check if a header exists or not and remove it if it does
              break;

            case 'tablebody':
              {
                // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                const { page: pageIndex, field: fieldIndex, row: rowIndex } = this.selectedFieldID.indexes;
                this.pages[pageIndex].fields[fieldIndex].tableDetails.row.splice(rowIndex, 1);
              }
              break;

            case 'tablefoot':
              this.addRemoveTableFooterClicked(); // the function will check if a footer exists or not and remove it if it does
              break;
          }
          break;

        case 'footer':
          {
            // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
            const { field: fieldIndex } = this.selectedFieldID.indexes;
            if (this.footer[fieldIndex].tableDetails.row.length <= 1 && !this.selectedTableFieldHasHeader) {
              // if there is only one row, or for some reason there are no rows at all, and there is no header
              this.footer.splice(fieldIndex, 1);
              break;
            }
          }

          switch (fieldType) {
            case 'tablehead':
              this.addRemoveTableHeaderClicked(); // the function will check if a header exists or not and remove it if it does
              break;

            case 'tablebody':
              {
                // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                const { field: fieldIndex, row: rowIndex } = this.selectedFieldID.indexes;
                this.footer[fieldIndex].tableDetails.row.splice(rowIndex, 1);
              }
              break;

            case 'tablefoot':
              this.addRemoveTableFooterClicked(); // the function will check if a footer exists or not and remove it if it does
              break;
          }
          break;
      }

      this.clearSelectedField();
    },
    deleteColumnClicked: async function () {
      const deleteConfirmation = await this.$showAlert(
        'warning',
        'Delete Column',
        'Are you sure you want to delete the currently selected column?',
        [
          { text: 'Delete', color: 'red', textColor: 'white' },
          { text: 'Cancel', color: 'lightgray', textColor: 'black' },
        ],
      );

      if (deleteConfirmation === 'Cancel') {
        return;
      }
      // ============== modify to delete the entire table if there is only one column
      // ============== similar to adding/removing table header, footer, and rows

      // coining the terms "unit cell" and "unit column"
      // a unit cell is the smallest possible unmerged cell
      // the number of cells may vary per row depending on whether there are merged cells, but the number of unit cells will always be equal across all rows
      // example, a merged cell with a column span of 3 consists of 3 unit cells
      // a unit column is a column of unit cells

      // this function deletes the leftmost unit column of the selected cell

      // first, figure out the unit column to be deleted by counting the number of unit cells to the left of the selected cell
      const selectedRow = this.getSelectedRowOfSelectedTableField();
      const selectedCellIndex = this.getIndexOfSelectedCellOfSelectedTableField();
      let unitColumnToDelete = 0;
      for (let index = 0; index < selectedCellIndex; index++) {
        unitColumnToDelete += selectedRow.cell[index].colSpan;
      }
      unitColumnToDelete++;

      // remove the unit cell from the header row if there is one
      if (this.selectedTableFieldHasHeader) {
        const headerRow = this.getHeaderRowOfSelectedTableField();
        let indexOfCellToDelete;
        let unitColumnsPassed = 0;
        for (let index = 0; index < headerRow.cell.length; index++) {
          unitColumnsPassed += headerRow.cell[index].colSpan;
          if (unitColumnsPassed >= unitColumnToDelete) {
            indexOfCellToDelete = index;
            break;
          }
        }

        if (headerRow.cell[indexOfCellToDelete].colSpan > 1) {
          headerRow.cell[indexOfCellToDelete].colSpan--;
        } else {
          headerRow.cell.splice(indexOfCellToDelete, 1);
          headerRow.columnWidths.splice(indexOfCellToDelete, 1);
        }
      }

      // remove the unit cell from each body cell
      const bodyRows = this.getBodyRowsOfSelectedTableField();
      for (const bodyRow of bodyRows) {
        let indexOfCellToDelete;
        let unitColumnsPassed = 0;
        for (let index = 0; index < bodyRow.cell.length; index++) {
          unitColumnsPassed += bodyRow.cell[index].colSpan;
          if (unitColumnsPassed >= unitColumnToDelete) {
            indexOfCellToDelete = index;
            break;
          }
        }

        if (bodyRow.cell[indexOfCellToDelete].colSpan > 1) {
          bodyRow.cell[indexOfCellToDelete].colSpan--;
        } else {
          bodyRow.cell.splice(indexOfCellToDelete, 1);
          bodyRow.columnWidths.splice(indexOfCellToDelete, 1);
        }
      }

      // remove the unit cell from the footer row if there is one
      if (this.selectedTableFieldHasFooter) {
        const footerRow = this.getFooterRowOfSelectedTableField();
        let indexOfCellToDelete;
        let unitColumnsPassed = 0;
        for (let index = 0; index < footerRow.cell.length; index++) {
          unitColumnsPassed += footerRow.cell[index].colSpan;
          if (unitColumnsPassed >= unitColumnToDelete) {
            indexOfCellToDelete = index;
            break;
          }
        }

        if (footerRow.cell[indexOfCellToDelete].colSpan > 1) {
          footerRow.cell[indexOfCellToDelete].colSpan--;
        } else {
          footerRow.cell.splice(indexOfCellToDelete, 1);
          footerRow.columnWidths.splice(indexOfCellToDelete, 1);
        }
      }

      this.clearSelectedField();
    },

    deleteTableClicked: async function () {
      const deleteConfirmation = await this.$showAlert(
        'warning',
        'Delete Table',
        'Are you sure you want to delete the currently selected table?',
        [
          { text: 'Delete', color: 'red', textColor: 'white' },
          { text: 'Cancel', color: 'lightgray', textColor: 'black' },
        ],
      );

      if (deleteConfirmation === 'Cancel') {
        return;
      }

      const { reportSection } = this.selectedFieldID;
      switch (reportSection) {
        case 'header':
          {
            // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
            const { field: fieldIndex } = this.selectedFieldID.indexes;
            this.header.splice(fieldIndex, 1);
          }
          break;

        case 'content':
          {
            // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
            const { page: pageIndex, field: fieldIndex } = this.selectedFieldID.indexes;
            this.pages[pageIndex].fields.splice(fieldIndex, 1);
          }
          break;

        case 'footer':
          {
            // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
            const { field: fieldIndex } = this.selectedFieldID.indexes;
            this.footer.splice(fieldIndex, 1);
          }
          break;
      }

      this.clearSelectedField();
    },

    deleteTextClicked: async function () {
      const deleteConfirmation = await this.$showAlert(
        'warning',
        'Delete Text',
        'Are you sure you want to delete the currently selected text?',
        [
          { text: 'Delete', color: 'red', textColor: 'white' },
          { text: 'Cancel', color: 'lightgray', textColor: 'black' },
        ],
      );

      if (deleteConfirmation === 'Cancel') {
        return;
      }

      const allFieldsWithinSection = this.getAllFieldsWithinReportSectionOfSelectedField();
      const fieldIndexToDelete = this.getFieldIndexOfSelectedField();
      allFieldsWithinSection.splice(fieldIndexToDelete, 1);

      this.clearSelectedField();
    },

    stylingSectionDropdownSelected: function (selectedSection) {
      if (!selectedSection) {
        this.clearSelectedField();
        return;
      }

      // is image, table key and text content don't differ based on what the selected styling section is
      // just populate those that differ based on the selected styling section
      // ============== simplify this later
      const { reportSection, fieldType } = this.selectedFieldID;
      switch (reportSection) {
        case 'header':
          switch (fieldType) {
            case 'tablehead':
              switch (selectedSection.name) {
                case 'Table':
                  {
                    const { field: fieldIndex } = this.selectedFieldID.indexes;
                    this.selectedField = this.header[fieldIndex];
                    this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
                      return fontFamily.value === this.header[fieldIndex].CSSStyles.fontFamily;
                    });
                    this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
                      return borderStyle.style === this.header[fieldIndex].CSSStyles.borderStyle;
                    });
                  }
                  break;

                case 'Row':
                  {
                    const { field: fieldIndex } = this.selectedFieldID.indexes;
                    this.selectedField = this.header[fieldIndex].tableDetails.header;
                    this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
                      return fontFamily.value === this.header[fieldIndex].tableDetails.header.CSSStyles.fontFamily;
                    });
                    this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
                      return borderStyle.style === this.header[fieldIndex].tableDetails.header.CSSStyles.borderStyle;
                    });
                  }
                  break;

                case 'Cell':
                  {
                    const { field: fieldIndex, cell: cellIndex } = this.selectedFieldID.indexes;
                    this.selectedField = this.header[fieldIndex].tableDetails.header.cell[cellIndex];
                    this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
                      return (
                        fontFamily.value ===
                        this.header[fieldIndex].tableDetails.header.cell[cellIndex].CSSStyles.fontFamily
                      );
                    });
                    this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
                      return (
                        borderStyle.style ===
                        this.header[fieldIndex].tableDetails.header.cell[cellIndex].CSSStyles.borderStyle
                      );
                    });
                  }
                  break;
              }
              break;

            case 'tablebody':
              switch (selectedSection.name) {
                case 'Table':
                  // same as tablehead
                  {
                    // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                    const { field: fieldIndex } = this.selectedFieldID.indexes;
                    this.selectedField = this.header[fieldIndex];
                    this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
                      return fontFamily.value === this.header[fieldIndex].CSSStyles.fontFamily;
                    });
                    this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
                      return borderStyle.style === this.header[fieldIndex].CSSStyles.borderStyle;
                    });
                  }
                  break;

                case 'Row':
                  {
                    // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                    const { field: fieldIndex, row: rowIndex } = this.selectedFieldID.indexes;
                    this.selectedField = this.header[fieldIndex].tableDetails.row[rowIndex];
                    this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
                      return (
                        fontFamily.value === this.header[fieldIndex].tableDetails.row[rowIndex].CSSStyles.fontFamily
                      );
                    });
                    this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
                      return (
                        borderStyle.style === this.header[fieldIndex].tableDetails.row[rowIndex].CSSStyles.borderStyle
                      );
                    });
                  }
                  break;

                case 'Cell':
                  {
                    // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                    const { field: fieldIndex, row: rowIndex, cell: cellIndex } = this.selectedFieldID.indexes;
                    this.selectedField = this.header[fieldIndex].tableDetails.row[rowIndex].cell[cellIndex];
                    this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
                      return (
                        fontFamily.value ===
                        this.header[fieldIndex].tableDetails.row[rowIndex].cell[cellIndex].CSSStyles.fontFamily
                      );
                    });
                    this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
                      return (
                        borderStyle.style ===
                        this.header[fieldIndex].tableDetails.row[rowIndex].cell[cellIndex].CSSStyles.borderStyle
                      );
                    });
                  }
                  break;
              }
              break;

            case 'tablefoot':
              switch (selectedSection.name) {
                case 'Table':
                  {
                    // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                    const { field: fieldIndex } = this.selectedFieldID.indexes;
                    this.selectedField = this.header[fieldIndex];
                    this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
                      return fontFamily.value === this.header[fieldIndex].CSSStyles.fontFamily;
                    });
                    this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
                      return borderStyle.style === this.header[fieldIndex].CSSStyles.borderStyle;
                    });
                  }
                  break;

                case 'Row':
                  {
                    // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                    const { field: fieldIndex } = this.selectedFieldID.indexes;
                    this.selectedField = this.header[fieldIndex].tableDetails.footer;
                    this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
                      return fontFamily.value === this.header[fieldIndex].tableDetails.footer.CSSStyles.fontFamily;
                    });
                    this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
                      return borderStyle.style === this.header[fieldIndex].tableDetails.footer.CSSStyles.borderStyle;
                    });
                  }
                  break;

                case 'Cell':
                  {
                    // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                    const { field: fieldIndex, cell: cellIndex } = this.selectedFieldID.indexes;
                    this.selectedField = this.header[fieldIndex].tableDetails.footer.cell[cellIndex];
                    this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
                      return (
                        fontFamily.value ===
                        this.header[fieldIndex].tableDetails.footer.cell[cellIndex].CSSStyles.fontFamily
                      );
                    });
                    this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
                      return (
                        borderStyle.style ===
                        this.header[fieldIndex].tableDetails.footer.cell[cellIndex].CSSStyles.borderStyle
                      );
                    });
                  }
                  break;
              }
              break;
          }
          break;

        case 'content':
          switch (fieldType) {
            case 'tablehead':
              switch (selectedSection.name) {
                case 'Table':
                  // same as tablebody
                  {
                    // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                    const { page: pageIndex, field: fieldIndex } = this.selectedFieldID.indexes;
                    this.selectedField = this.pages[pageIndex].fields[fieldIndex];
                    this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
                      return fontFamily.value === this.pages[pageIndex].fields[fieldIndex].CSSStyles.fontFamily;
                    });
                    this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
                      return borderStyle.style === this.pages[pageIndex].fields[fieldIndex].CSSStyles.borderStyle;
                    });
                  }
                  break;

                case 'Row':
                  {
                    // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                    const { page: pageIndex, field: fieldIndex } = this.selectedFieldID.indexes;
                    this.selectedField = this.pages[pageIndex].fields[fieldIndex].tableDetails.header;
                    this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
                      return (
                        fontFamily.value ===
                        this.pages[pageIndex].fields[fieldIndex].tableDetails.header.CSSStyles.fontFamily
                      );
                    });
                    this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
                      return (
                        borderStyle.style ===
                        this.pages[pageIndex].fields[fieldIndex].tableDetails.header.CSSStyles.borderStyle
                      );
                    });
                  }
                  break;

                case 'Cell':
                  {
                    // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                    const { page: pageIndex, field: fieldIndex, cell: cellIndex } = this.selectedFieldID.indexes;
                    this.selectedField = this.pages[pageIndex].fields[fieldIndex].tableDetails.header.cell[cellIndex];
                    this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
                      return (
                        fontFamily.value ===
                        this.pages[pageIndex].fields[fieldIndex].tableDetails.header.cell[cellIndex].CSSStyles
                          .fontFamily
                      );
                    });
                    this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
                      return (
                        borderStyle.style ===
                        this.pages[pageIndex].fields[fieldIndex].tableDetails.header.cell[cellIndex].CSSStyles
                          .borderStyle
                      );
                    });
                  }
                  break;
              }
              break;

            case 'tablebody':
              switch (selectedSection.name) {
                case 'Table':
                  // same as tablehead
                  {
                    // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                    const { page: pageIndex, field: fieldIndex } = this.selectedFieldID.indexes;
                    this.selectedField = this.pages[pageIndex].fields[fieldIndex];
                    this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
                      return fontFamily.value === this.pages[pageIndex].fields[fieldIndex].CSSStyles.fontFamily;
                    });
                    this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
                      return borderStyle.style === this.pages[pageIndex].fields[fieldIndex].CSSStyles.borderStyle;
                    });
                  }
                  break;

                case 'Row':
                  {
                    // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                    const { page: pageIndex, field: fieldIndex, row: rowIndex } = this.selectedFieldID.indexes;
                    this.selectedField = this.pages[pageIndex].fields[fieldIndex].tableDetails.row[rowIndex];
                    this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
                      return (
                        fontFamily.value ===
                        this.pages[pageIndex].fields[fieldIndex].tableDetails.row[rowIndex].CSSStyles.fontFamily
                      );
                    });
                    this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
                      return (
                        borderStyle.style ===
                        this.pages[pageIndex].fields[fieldIndex].tableDetails.row[rowIndex].CSSStyles.borderStyle
                      );
                    });
                  }
                  break;

                case 'Cell':
                  {
                    // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                    const {
                      page: pageIndex,
                      field: fieldIndex,
                      row: rowIndex,
                      cell: cellIndex,
                    } = this.selectedFieldID.indexes;
                    this.selectedField =
                      this.pages[pageIndex].fields[fieldIndex].tableDetails.row[rowIndex].cell[cellIndex];
                    this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
                      return (
                        fontFamily.value ===
                        this.pages[pageIndex].fields[fieldIndex].tableDetails.row[rowIndex].cell[cellIndex].CSSStyles
                          .fontFamily
                      );
                    });
                    this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
                      return (
                        borderStyle.style ===
                        this.pages[pageIndex].fields[fieldIndex].tableDetails.row[rowIndex].cell[cellIndex].CSSStyles
                          .borderStyle
                      );
                    });
                  }
                  break;
              }
              break;

            case 'tablefoot':
              switch (selectedSection.name) {
                case 'Table':
                  {
                    // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                    const { page: pageIndex, field: fieldIndex } = this.selectedFieldID.indexes;
                    this.selectedField = this.pages[pageIndex].fields[fieldIndex];
                    this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
                      return fontFamily.value === this.pages[pageIndex].fields[fieldIndex].CSSStyles.fontFamily;
                    });
                    this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
                      return borderStyle.style === this.pages[pageIndex].fields[fieldIndex].CSSStyles.borderStyle;
                    });
                  }
                  break;

                case 'Row':
                  {
                    // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                    const { page: pageIndex, field: fieldIndex } = this.selectedFieldID.indexes;
                    this.selectedField = this.pages[pageIndex].fields[fieldIndex].tableDetails.footer;
                    this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
                      return (
                        fontFamily.value ===
                        this.pages[pageIndex].fields[fieldIndex].tableDetails.footer.CSSStyles.fontFamily
                      );
                    });
                    this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
                      return (
                        borderStyle.style ===
                        this.pages[pageIndex].fields[fieldIndex].tableDetails.footer.CSSStyles.borderStyle
                      );
                    });
                  }
                  break;

                case 'Cell':
                  {
                    // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                    const { page: pageIndex, field: fieldIndex, cell: cellIndex } = this.selectedFieldID.indexes;
                    this.selectedField = this.pages[pageIndex].fields[fieldIndex].tableDetails.footer.cell[cellIndex];
                    this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
                      return (
                        fontFamily.value ===
                        this.pages[pageIndex].fields[fieldIndex].tableDetails.footer.cell[cellIndex].CSSStyles
                          .fontFamily
                      );
                    });
                    this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
                      return (
                        borderStyle.style ===
                        this.pages[pageIndex].fields[fieldIndex].tableDetails.footer.cell[cellIndex].CSSStyles
                          .borderStyle
                      );
                    });
                  }
                  break;
              }
              break;
          }
          break;

        case 'footer':
          switch (fieldType) {
            case 'tablehead':
              switch (selectedSection.name) {
                case 'Table':
                  {
                    // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                    const { field: fieldIndex } = this.selectedFieldID.indexes;
                    this.selectedField = this.footer[fieldIndex];
                    this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
                      return fontFamily.value === this.footer[fieldIndex].CSSStyles.fontFamily;
                    });
                    this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
                      return borderStyle.style === this.footer[fieldIndex].CSSStyles.borderStyle;
                    });
                  }
                  break;

                case 'Row':
                  {
                    // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                    const { field: fieldIndex } = this.selectedFieldID.indexes;
                    this.selectedField = this.footer[fieldIndex].tableDetails.header;
                    this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
                      return fontFamily.value === this.footer[fieldIndex].tableDetails.header.CSSStyles.fontFamily;
                    });
                    this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
                      return borderStyle.style === this.footer[fieldIndex].tableDetails.header.CSSStyles.borderStyle;
                    });
                  }
                  break;

                case 'Cell':
                  {
                    // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                    const { field: fieldIndex, cell: cellIndex } = this.selectedFieldID.indexes;
                    this.selectedField = this.footer[fieldIndex].tableDetails.header.cell[cellIndex];
                    this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
                      return (
                        fontFamily.value ===
                        this.footer[fieldIndex].tableDetails.header.cell[cellIndex].CSSStyles.fontFamily
                      );
                    });
                    this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
                      return (
                        borderStyle.style ===
                        this.footer[fieldIndex].tableDetails.header.cell[cellIndex].CSSStyles.borderStyle
                      );
                    });
                  }
                  break;
              }
              break;

            case 'tablebody':
              switch (selectedSection.name) {
                case 'Table':
                  // same as tablehead
                  {
                    // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                    const { field: fieldIndex } = this.selectedFieldID.indexes;
                    this.selectedField = this.footer[fieldIndex];
                    this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
                      return fontFamily.value === this.footer[fieldIndex].CSSStyles.fontFamily;
                    });
                    this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
                      return borderStyle.style === this.footer[fieldIndex].CSSStyles.borderStyle;
                    });
                  }
                  break;

                case 'Row':
                  {
                    // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                    const { field: fieldIndex, row: rowIndex } = this.selectedFieldID.indexes;
                    this.selectedField = this.footer[fieldIndex].tableDetails.row[rowIndex];
                    this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
                      return (
                        fontFamily.value === this.footer[fieldIndex].tableDetails.row[rowIndex].CSSStyles.fontFamily
                      );
                    });
                    this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
                      return (
                        borderStyle.style === this.footer[fieldIndex].tableDetails.row[rowIndex].CSSStyles.borderStyle
                      );
                    });
                  }
                  break;

                case 'Cell':
                  {
                    // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                    const { field: fieldIndex, row: rowIndex, cell: cellIndex } = this.selectedFieldID.indexes;
                    this.selectedField = this.footer[fieldIndex].tableDetails.row[rowIndex].cell[cellIndex];
                    this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
                      return (
                        fontFamily.value ===
                        this.footer[fieldIndex].tableDetails.row[rowIndex].cell[cellIndex].CSSStyles.fontFamily
                      );
                    });
                    this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
                      return (
                        borderStyle.style ===
                        this.footer[fieldIndex].tableDetails.row[rowIndex].cell[cellIndex].CSSStyles.borderStyle
                      );
                    });
                  }
                  break;
              }
              break;

            case 'tablefoot':
              switch (selectedSection.name) {
                case 'Table':
                  {
                    // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                    const { field: fieldIndex } = this.selectedFieldID.indexes;
                    this.selectedField = this.footer[fieldIndex];
                    this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
                      return fontFamily.value === this.footer[fieldIndex].CSSStyles.fontFamily;
                    });
                    this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
                      return borderStyle.style === this.footer[fieldIndex].CSSStyles.borderStyle;
                    });
                  }
                  break;

                case 'Row':
                  {
                    // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                    const { field: fieldIndex } = this.selectedFieldID.indexes;
                    this.selectedField = this.footer[fieldIndex].tableDetails.footer;
                    this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
                      return fontFamily.value === this.footer[fieldIndex].tableDetails.footer.CSSStyles.fontFamily;
                    });
                    this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
                      return borderStyle.style === this.footer[fieldIndex].tableDetails.footer.CSSStyles.borderStyle;
                    });
                  }
                  break;

                case 'Cell':
                  {
                    // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                    const { field: fieldIndex, cell: cellIndex } = this.selectedFieldID.indexes;
                    this.selectedField = this.footer[fieldIndex].tableDetails.footer.cell[cellIndex];
                    this.selectedFieldFontFamily = this.fontFamilyListing.find((fontFamily) => {
                      return (
                        fontFamily.value ===
                        this.footer[fieldIndex].tableDetails.footer.cell[cellIndex].CSSStyles.fontFamily
                      );
                    });
                    this.selectedFieldBorderStyle = this.borderStyles.find((borderStyle) => {
                      return (
                        borderStyle.style ===
                        this.footer[fieldIndex].tableDetails.footer.cell[cellIndex].CSSStyles.borderStyle
                      );
                    });
                  }
                  break;
              }
              break;
          }
          break;
      }
    },
    //#region Text Formatting
    // Handles when the font family changes
    fontFamilyDropdownSelected: function (selectedFont) {
      if (!selectedFont) {
        return; // don't update anything, leave the dropdown as blank, leave the field with its current font family
      }

      // ============== simplify this later
      const { reportSection, fieldType } = this.selectedFieldID;
      switch (reportSection) {
        case 'header':
          switch (fieldType) {
            case 'string':
              this.selectedField.CSSStyles.fontFamily = selectedFont.value;
              break;

            case 'tablehead':
              this.selectedField.CSSStyles.fontFamily = selectedFont.value;
              break;

            case 'tablebody':
              this.selectedField.CSSStyles.fontFamily = selectedFont.value;
              break;

            case 'tablefoot':
              this.selectedField.CSSStyles.fontFamily = selectedFont.value;
              break;
          }
          break;

        case 'content':
          switch (fieldType) {
            case 'string':
              this.selectedField.CSSStyles.fontFamily = selectedFont.value;
              break;

            case 'tablehead':
              this.selectedField.CSSStyles.fontFamily = selectedFont.value;
              break;

            case 'tablebody':
              this.selectedField.CSSStyles.fontFamily = selectedFont.value;
              break;

            case 'tablefoot':
              this.selectedField.CSSStyles.fontFamily = selectedFont.value;
              break;
          }
          break;

        case 'footer':
          switch (fieldType) {
            case 'string':
              this.selectedField.CSSStyles.fontFamily = selectedFont.value;
              break;

            case 'tablehead':
              this.selectedField.CSSStyles.fontFamily = selectedFont.value;
              break;

            case 'tablebody':
              this.selectedField.CSSStyles.fontFamily = selectedFont.value;
              break;

            case 'tablefoot':
              this.selectedField.CSSStyles.fontFamily = selectedFont.value;
              break;
          }
          break;
      }
    },
    // Handles when changing the text align
    horizontalTextAlignClicked: function (alignmentType) {
      // ============== simplify this later
      const { reportSection, fieldType } = this.selectedFieldID;
      switch (reportSection) {
        case 'header':
          switch (fieldType) {
            case 'string':
              this.selectedField.CSSStyles.textAlign = alignmentType;
              break;

            case 'tablehead':
              this.selectedField.CSSStyles.textAlign = alignmentType;
              break;

            case 'tablebody':
              this.selectedField.CSSStyles.textAlign = alignmentType;
              break;

            case 'tablefoot':
              this.selectedField.CSSStyles.textAlign = alignmentType;
              break;
          }
          break;

        case 'content':
          switch (fieldType) {
            case 'string':
              this.selectedField.CSSStyles.textAlign = alignmentType;
              break;

            case 'tablehead':
              this.selectedField.CSSStyles.textAlign = alignmentType;
              break;

            case 'tablebody':
              this.selectedField.CSSStyles.textAlign = alignmentType;
              break;

            case 'tablefoot':
              this.selectedField.CSSStyles.textAlign = alignmentType;
              break;
          }
          break;

        case 'footer':
          switch (fieldType) {
            case 'string':
              this.selectedField.CSSStyles.textAlign = alignmentType;
              break;

            case 'tablehead':
              this.selectedField.CSSStyles.textAlign = alignmentType;
              break;

            case 'tablebody':
              this.selectedField.CSSStyles.textAlign = alignmentType;
              break;

            case 'tablefoot':
              this.selectedField.CSSStyles.textAlign = alignmentType;
              break;
          }
          break;
      }
    },
    verticalTextAlignClicked: function (alignmentType) {
      // ============== simplify this later
      const { reportSection, fieldType } = this.selectedFieldID;
      switch (reportSection) {
        case 'header':
          switch (fieldType) {
            case 'string':
              this.selectedField.CSSStyles.verticalAlign = alignmentType;
              break;

            case 'tablehead':
              this.selectedField.CSSStyles.verticalAlign = alignmentType;
              break;

            case 'tablebody':
              this.selectedField.CSSStyles.verticalAlign = alignmentType;
              break;

            case 'tablefoot':
              this.selectedField.CSSStyles.verticalAlign = alignmentType;
              break;
          }
          break;

        case 'content':
          switch (fieldType) {
            case 'string':
              this.selectedField.CSSStyles.verticalAlign = alignmentType;
              break;

            case 'tablehead':
              this.selectedField.CSSStyles.verticalAlign = alignmentType;
              break;

            case 'tablebody':
              this.selectedField.CSSStyles.verticalAlign = alignmentType;
              break;

            case 'tablefoot':
              this.selectedField.CSSStyles.verticalAlign = alignmentType;
              break;
          }
          break;

        case 'footer':
          switch (fieldType) {
            case 'string':
              this.selectedField.CSSStyles.verticalAlign = alignmentType;
              break;

            case 'tablehead':
              this.selectedField.CSSStyles.verticalAlign = alignmentType;
              break;

            case 'tablebody':
              this.selectedField.CSSStyles.verticalAlign = alignmentType;
              break;

            case 'tablefoot':
              this.selectedField.CSSStyles.verticalAlign = alignmentType;
              break;
          }
          break;
      }
    },
    //#endregion
    columnSizingInputChanged: function (columnIndex, newColumnSize) {
      const selectedRow = this.getSelectedRowOfSelectedTableField();
      selectedRow.columnWidths[columnIndex] = newColumnSize;
    },

    // Handles when the save report button is clicked
    saveReportClicked: function () {
      if (this.pages.length == 0) {
        // No pages to save
        // Show save error since there are no page
        this.$showAlert('error', 'Could Not Save', 'There are no pages to save, please add a page to save this report');
        return;
      }

      // populate popup fields
      if (this.reportPath) {
        let folderName;
        // for now, reports are always saved only 1 folder deep, reportPath should always be {folderName}/{reportName}
        [folderName, this.saveReportName] = this.reportPath.split('/'); // destructuring

        const foundFolder = this.saveReportFolderDropdownItems.find((item) => {
          return item['folder'] === folderName;
        });
        if (foundFolder) {
          this.saveReportFolderSelectedObject = foundFolder;
        }
      }

      // Show the save popup
      this.showSavePopup = true;
    },
    // Handles when the save popup button is clicked
    savePopupButtonClicked: async function (button) {
      if (button === 'Cancel') {
        // if the user clicked on cancel
        this.showSavePopup = false; // hide popup and don't do anything else
        return;
      }

      let error = false;

      if (!this.saveReportFolderSelectedObject || Object.keys(this.saveReportFolderSelectedObject).length === 0) {
        this.saveReportFolderError = 'Report Folder is required!';
        error = true;
      }

      if (!this.saveReportName) {
        // Report name is empty
        // Set the input error
        this.saveReportNameError = 'Report Name is required!';
        error = true;
      }

      if (error) {
        // if there is at least one error
        return; // don't do anything else
      }

      this.showSavePopup = false; // hide popup

      if (!this.saveReportName.endsWith('.json')) {
        // If the report name does not ends with .json
        // Append .json at the end of the report name
        this.saveReportName += '.json';
      }

      // Saving the report
      this.setLoadingOverlay('Saving Report');
      const saveReportResponse = await this.$apiCallV2('/DesignerV3/SaveReport', {
        folder: this.saveReportFolderSelectedObject['folder'],
        report: this.saveReportName,
        data: {
          version: this.currentTemplateVersion,
          paperSize: this.selectedPaper['name'],
          footerPreserveSpace: this.footerPreserveSpace,
          header: this.header,
          headerStyle: this.headerStyle,
          pages: this.pages,
          pagesStyle: this.pagesStyle,
          footer: this.footer,
          footerStyle: this.footerStyle,
        },
      });
      this.setLoadingOverlay();

      switch (saveReportResponse) {
        case '0':
          this.$showAlert('success', 'Successfully Saved', 'Your report has been successfully saved', [{ text: 'OK' }]);
          break;

        case '1':
          this.$showAlert('error', 'Empty Report Name', 'Your report name cannot be empty', [{ text: 'OK' }]);
          break;

        case '2':
          this.$showAlert('error', 'Nothing To Save', 'There were no changes made to your report', [{ text: 'OK' }]);
          break;

        case '-2':
          this.$showAlert('error', 'In Development Mode', 'Saving reports is not allowed in development mode', [
            { text: 'OK' },
          ]);
          break;

        default:
          this.$showAlert('error', 'Error Saving', 'An error occurred saving your report', [{ text: 'OK' }]);
          break;
      }
    },
    getReportTemplate: async function (reportName, folderName) {
      // =============== change the folder priority order in the controller
      // it should try to get the template from the CoRefName specific folder first, before going to the company general folder
      let APIURL = `/DesignerV3/LoadReport?reportName=${reportName}`;
      if (folderName) {
        APIURL += `&folderName=${folderName}`;
      }
      if (this.companyReportFolder) {
        // ==================== have the controller get the SettingsSystem value itself, instead of relying on vue to pass it back which is potentially dangerous
        APIURL += `&companyFolderName=${this.companyReportFolder}`;
      }
      return await this.$apiCallV2(APIURL);
    },
    populateTemplateViewer: async function (reportDetails) {
      if (!reportDetails) {
        return;
      }

      // destructure
      const { Path: path } = reportDetails;
      let { Template: template } = reportDetails;
      const { version } = template;

      // need to convert outdated templates to the latest version before it can be rendered
      // can consider removing old converter functions if there are no more templates of that version lying around
      if (version !== this.currentTemplateVersion) {
        switch (version) {
          default:
            template = this.convertTemplateVersionZeroToOne(template);

          case 1: // eslint-disable-line no-fallthrough -- read below for explanation why
            template = this.convertTemplateVersionOneToTwo(template);

          case 2: // eslint-disable-line no-fallthrough -- read below for explanation why
            template = this.convertTemplateVersionTwoToThree(template);

          case 3: // eslint-disable-line no-fallthrough -- read below for explanation why
            template = this.convertTemplateVersionThreeToFour(template);

          case 4: // eslint-disable-line no-fallthrough -- read below for explanation why
            template = this.convertTemplateVersionFourToFive(template);

          // there's not supposed to be a break statement, outdated template versions need to be converted one step at a time until the latest version
          // whenever the template version is incremented, implement a converter function and add a case statement to the bottom
          // then, it will run each converter until it reaches the latest version
        }
      }

      // populate reportPath if it was passed from the controller
      this.reportPath = path ?? '';

      // ============= there is a bug where the page height would be incorrectly calculated after loading mutliple reports
      // ============= to reproduce:
      // =============       load DSM/Quotation.json
      // =============       without refreshing or unloading the template, load General/General Invoice.json
      // =============       and then, load DSM/Quotation.json again
      // =============       notice how the page is shorter and some of the report contents overflow out of the page height
      // =============       printing also has a similar issue, from printing the 2nd time onwards
      // =============       because the pdf viewer wasn't being hidden when the header and footer heights, leading to the top and bottom margins being larger than needed
      // =============       might be the same cause
      // ============= debug later

      //version 5 update - paperSize, footerPreserveSpace
      if (template.paperSize) {
        this.selectedPaper = this.reportPaperList.getSelected(template.paperSize, 'name');
      }

      this.footerPreserveSpace = template.footerPreserveSpace;

      // render the report template
      if (template.header) {
        this.header = template.header; // else leave it as an empty array, the default is set in clearTemplateViewer
      }
      if (template.headerStyle) {
        this.headerStyle = template.headerStyle; // else leave it as the default styling, the default is set in clearTemplateViewer
      }

      if (template['pages']) {
        // If pages exists
        if (Array.isArray(template['pages'])) {
          // If pages is an array
          // Loop through the pages
          for (const page of template['pages']) {
            // Add the page
            this.addPageClicked(page);
            // Sleep to get the correct height of the page first
            // await this.$sleep(500);
          }
        }
      }
      if (template.pagesStyle) {
        this.pagesStyle = template.pagesStyle; // else leave it as the default styling, the default is set in clearTemplateViewer
      }

      if (template.footer) {
        this.footer = template.footer; // else leave it as an empty array, the default is set in clearTemplateViewer
      }
      if (template.footerStyle) {
        this.footerStyle = template.footerStyle; // else leave it as the default styling, the default is set in clearTemplateViewer
      }

      this.populateReportSectionStylingInputValues(this.selectedReportSection.name); // this shouldn't be null, since a default is set in clearTemplateViewer
    },
    clearTemplateViewer: function () {
      // Clearing / Set the details to default
      this.header = [];
      this.headerStyle = {
        paddingTop: '0mm',
        paddingRight: '0mm',
        paddingBottom: '0mm',
        paddingLeft: '0mm',
      };
      // =========== clear off header height?

      this.pages = [];
      this.pagesRatio = [];
      this.pagesStyle = {
        paddingTop: '0mm',
        paddingRight: '0mm',
        paddingBottom: '0mm',
        paddingLeft: '0mm',
      };

      this.footer = [];
      this.footerStyle = {
        paddingTop: '0mm',
        paddingRight: '0mm',
        paddingBottom: '0mm',
        paddingLeft: '0mm',
      };
      // =========== clear off footer height?

      this.selectedReportSection = this.reportSections.find((reportSection) => reportSection.name === 'Content');
      this.backgroundImage = '';
      this.reportPath = '';

      this.clearSelectedField();
      this.selectedPageIndex = -1;
    },
    convertTemplateVersionZeroToOne: function (template) {
      const updatedTemplate = {
        version: 1,
        header: [],
        headerStyle: {},
        pages: [],
        footer: [],
        footerStyle: {},
      };

      if (template.header) {
        for (const header of template.header) {
          const updatedHeader = {};

          switch (
            header.type // version 0 should only have string and table
          ) {
            case 'string':
              {
                updatedHeader.type = 'string';
                updatedHeader.value = header.value;
                updatedHeader.isImage = header.isImage;

                let borderTopWidth;
                let borderRightWidth;
                let borderBottomWidth;
                let borderLeftWidth;
                let borderTopValue;
                let borderRightValue;
                let borderBottomValue;
                let borderLeftValue;
                const noBorder = header.style.noBorder;
                if (noBorder) {
                  // noticed in version 0, the css property value doesn't match up with the input values at times, probably due to bugs in version 0
                  borderTopWidth = '0mm'; // logic to handle such cases
                  borderRightWidth = '0mm';
                  borderBottomWidth = '0mm';
                  borderLeftWidth = '0mm';

                  borderTopValue = header.style.borderTopValue;
                  borderRightValue = header.style.borderRightValue;
                  borderBottomValue = header.style.borderBottomValue;
                  borderLeftValue = header.style.borderLeftValue;
                } else {
                  const borderWidthSplit = header.style.borderWidth.split(' '); // ignore input values if noBorder is false, assume css property to be the source of truth
                  if (borderWidthSplit.length === 4) {
                    // in version 0, border width property may have all 4 side specified or only one
                    [borderTopWidth, borderRightWidth, borderBottomWidth, borderLeftWidth] = borderWidthSplit;
                    [borderTopValue, borderRightValue, borderBottomValue, borderLeftValue] = borderWidthSplit;
                  } else {
                    borderTopWidth = borderWidthSplit[0];
                    borderRightWidth = borderWidthSplit[0];
                    borderBottomWidth = borderWidthSplit[0];
                    borderLeftWidth = borderWidthSplit[0];
                    borderTopValue = borderWidthSplit[0];
                    borderRightValue = borderWidthSplit[0];
                    borderBottomValue = borderWidthSplit[0];
                    borderLeftValue = borderWidthSplit[0];
                  }
                }

                let paddingTop;
                let paddingRight;
                let paddingBottom;
                let paddingLeft;
                const paddingSplit = header.style.padding.split(' '); // ignore input values, assume css property to be the source of truth
                if (paddingSplit.length === 4) {
                  // in version 0, padding property may have all 4 side specified or only one
                  [paddingTop, paddingRight, paddingBottom, paddingLeft] = paddingSplit;
                } else {
                  paddingTop = paddingSplit[0];
                  paddingRight = paddingSplit[0];
                  paddingBottom = paddingSplit[0];
                  paddingLeft = paddingSplit[0];
                }

                let marginTop;
                let marginRight;
                let marginBottom;
                let marginLeft;
                const marginSplit = header.style.margin.split(' '); // ignore input values, assume css property to be the source of truth
                if (marginSplit.length === 4) {
                  // in version 0, margin property may have all 4 side specified or only one
                  [marginTop, marginRight, marginBottom, marginLeft] = marginSplit;
                } else {
                  marginTop = paddingSplit[0];
                  marginRight = paddingSplit[0];
                  marginBottom = paddingSplit[0];
                  marginLeft = paddingSplit[0];
                }

                let height;
                let heightValue;
                const fitHeight = header.style.fitHeight;
                if (fitHeight) {
                  height = 'fit-content';
                  heightValue = header.style.heightValue;
                } else {
                  height = header.style.height;
                  heightValue = header.style.height;
                }

                updatedHeader.CSSStyles = {
                  position: header.style.position,
                  width: header.style.width,

                  borderTopWidth,
                  borderRightWidth,
                  borderBottomWidth,
                  borderLeftWidth,
                  borderStyle: header.style.borderStyle,
                  borderColor: header.style.borderColor,

                  paddingTop,
                  paddingRight,
                  paddingBottom,
                  paddingLeft,

                  marginTop,
                  marginRight,
                  marginBottom,
                  marginLeft,

                  left: header.style.left,
                  top: header.style.top,

                  height,
                  fontFamily: header.style.fontFamily,
                  backgroundColor: header.style.backgroundColor,
                  fontSize: header.style.fontSize,
                  textAlign: header.style.textAlign,
                };

                updatedHeader.styleInputValues = {
                  noBorder,
                  borderTopValue,
                  borderRightValue,
                  borderBottomValue,
                  borderLeftValue,

                  fitHeight,
                  heightValue,
                };
              }
              break;

            case 'table':
              {
                updatedHeader.type = 'table';
                updatedHeader.columnWidths = []; // number of elements in this array need to match the number of columns, populated later in the column for-loop

                let borderTopWidth;
                let borderRightWidth;
                let borderBottomWidth;
                let borderLeftWidth;
                let borderTopValue;
                let borderRightValue;
                let borderBottomValue;
                let borderLeftValue;
                const noBorder = header.style.noBorder;
                if (noBorder) {
                  // noticed in version 0, the css property value doesn't match up with the input values at times, probably due to bugs in version 0
                  borderTopWidth = '0mm'; // logic to handle such cases
                  borderRightWidth = '0mm';
                  borderBottomWidth = '0mm';
                  borderLeftWidth = '0mm';

                  borderTopValue = header.style.borderTopValue;
                  borderRightValue = header.style.borderRightValue;
                  borderBottomValue = header.style.borderBottomValue;
                  borderLeftValue = header.style.borderLeftValue;
                } else {
                  const borderWidthSplit = header.style.borderWidth.split(' '); // ignore input values if noBorder is false, assume css property to be the source of truth
                  if (borderWidthSplit.length === 4) {
                    // in version 0, border width property may have all 4 side specified or only one
                    [borderTopWidth, borderRightWidth, borderBottomWidth, borderLeftWidth] = borderWidthSplit;
                    [borderTopValue, borderRightValue, borderBottomValue, borderLeftValue] = borderWidthSplit;
                  } else {
                    borderTopWidth = borderWidthSplit[0];
                    borderRightWidth = borderWidthSplit[0];
                    borderBottomWidth = borderWidthSplit[0];
                    borderLeftWidth = borderWidthSplit[0];
                    borderTopValue = borderWidthSplit[0];
                    borderRightValue = borderWidthSplit[0];
                    borderBottomValue = borderWidthSplit[0];
                    borderLeftValue = borderWidthSplit[0];
                  }
                }

                let paddingTop;
                let paddingRight;
                let paddingBottom;
                let paddingLeft;
                const paddingSplit = header.style.padding.split(' '); // ignore input values, assume css property to be the source of truth
                if (paddingSplit.length === 4) {
                  // in version 0, padding property may have all 4 side specified or only one
                  [paddingTop, paddingRight, paddingBottom, paddingLeft] = paddingSplit;
                } else {
                  paddingTop = paddingSplit[0];
                  paddingRight = paddingSplit[0];
                  paddingBottom = paddingSplit[0];
                  paddingLeft = paddingSplit[0];
                }

                let marginTop;
                let marginRight;
                let marginBottom;
                let marginLeft;
                const marginSplit = header.style.margin.split(' '); // ignore input values, assume css property to be the source of truth
                if (marginSplit.length === 4) {
                  // in version 0, margin property may have all 4 side specified or only one
                  [marginTop, marginRight, marginBottom, marginLeft] = marginSplit;
                } else {
                  marginTop = paddingSplit[0];
                  marginRight = paddingSplit[0];
                  marginBottom = paddingSplit[0];
                  marginLeft = paddingSplit[0];
                }

                let height;
                let heightValue;
                const fitHeight = header.style.fitHeight;
                if (fitHeight) {
                  height = 'fit-content';
                  heightValue = header.style.heightValue;
                } else {
                  height = header.style.height;
                  heightValue = header.style.height;
                }

                updatedHeader.CSSStyles = {
                  position: header.style.position,
                  width: header.style.width,

                  borderTopWidth,
                  borderRightWidth,
                  borderBottomWidth,
                  borderLeftWidth,
                  borderStyle: header.style.borderStyle,
                  borderColor: header.style.borderColor,

                  paddingTop,
                  paddingRight,
                  paddingBottom,
                  paddingLeft,

                  marginTop,
                  marginRight,
                  marginBottom,
                  marginLeft,

                  left: header.style.left,
                  top: header.style.top,

                  height,
                  fontFamily: header.style.fontFamily,
                  backgroundColor: header.style.backgroundColor,
                  fontSize: header.style.fontSize,
                  textAlign: header.style.textAlign,

                  tableLayout: 'fixed', // was hardcoded in version 0, continue to hardcode in version 1
                };

                updatedHeader.styleInputValues = {
                  noBorder,
                  borderTopValue,
                  borderRightValue,
                  borderBottomValue,
                  borderLeftValue,

                  fitHeight,
                  heightValue,
                };

                updatedHeader.tableDetails = {
                  tableKey: header.tableDetails.tableKey,
                  row: [],
                };

                for (const row of header.tableDetails.row) {
                  const updatedRow = {};

                  let borderTopWidth;
                  let borderRightWidth;
                  let borderBottomWidth;
                  let borderLeftWidth;
                  let borderTopValue;
                  let borderRightValue;
                  let borderBottomValue;
                  let borderLeftValue;
                  const noBorder = row.style.noBorder;
                  if (noBorder) {
                    // noticed in version 0, the css property value doesn't match up with the input values at times, probably due to bugs in version 0
                    borderTopWidth = '0mm'; // logic to handle such cases
                    borderRightWidth = '0mm';
                    borderBottomWidth = '0mm';
                    borderLeftWidth = '0mm';

                    borderTopValue = row.style.borderTopValue;
                    borderRightValue = row.style.borderRightValue;
                    borderBottomValue = row.style.borderBottomValue;
                    borderLeftValue = row.style.borderLeftValue;
                  } else {
                    const borderWidthSplit = row.style.borderWidth.split(' '); // ignore input values if noBorder is false, assume css property to be the source of truth
                    if (borderWidthSplit.length === 4) {
                      // in version 0, border width property may have all 4 side specified or only one
                      [borderTopWidth, borderRightWidth, borderBottomWidth, borderLeftWidth] = borderWidthSplit;
                      [borderTopValue, borderRightValue, borderBottomValue, borderLeftValue] = borderWidthSplit;
                    } else {
                      borderTopWidth = borderWidthSplit[0];
                      borderRightWidth = borderWidthSplit[0];
                      borderBottomWidth = borderWidthSplit[0];
                      borderLeftWidth = borderWidthSplit[0];
                      borderTopValue = borderWidthSplit[0];
                      borderRightValue = borderWidthSplit[0];
                      borderBottomValue = borderWidthSplit[0];
                      borderLeftValue = borderWidthSplit[0];
                    }
                  }

                  let paddingTop;
                  let paddingRight;
                  let paddingBottom;
                  let paddingLeft;
                  const paddingSplit = row.style.padding.split(' '); // ignore input values, assume css property to be the source of truth
                  if (paddingSplit.length === 4) {
                    // in version 0, padding property may have all 4 side specified or only one
                    [paddingTop, paddingRight, paddingBottom, paddingLeft] = paddingSplit;
                  } else {
                    paddingTop = paddingSplit[0];
                    paddingRight = paddingSplit[0];
                    paddingBottom = paddingSplit[0];
                    paddingLeft = paddingSplit[0];
                  }

                  updatedRow.CSSStyles = {
                    borderTopWidth,
                    borderRightWidth,
                    borderBottomWidth,
                    borderLeftWidth,
                    borderStyle: row.style.borderStyle,
                    borderColor: row.style.borderColor,

                    paddingTop,
                    paddingRight,
                    paddingBottom,
                    paddingLeft,

                    fontFamily: header.style.fontFamily, // version 0 doesn't store this for row, take from header instead
                    backgroundColor: row.style.backgroundColor,
                    fontSize: header.style.fontSize, // version 0 doesn't store this for row, take from header instead
                    textAlign: header.style.textAlign, // version 0 doesn't store this for row, take from header instead
                  };

                  updatedRow.styleInputValues = {
                    noBorder,
                    borderTopValue,
                    borderRightValue,
                    borderBottomValue,
                    borderLeftValue,
                  };

                  updatedRow.cell = [];

                  for (const column of row.column) {
                    const updatedColumn = {};

                    updatedColumn.value = column.value;
                    updatedColumn.isImage = column.isImage;

                    let borderTopWidth;
                    let borderRightWidth;
                    let borderBottomWidth;
                    let borderLeftWidth;
                    let borderTopValue;
                    let borderRightValue;
                    let borderBottomValue;
                    let borderLeftValue;
                    const noBorder = column.style.noBorder;
                    if (noBorder) {
                      // noticed in version 0, the css property value doesn't match up with the input values at times, probably due to bugs in version 0
                      borderTopWidth = '0mm'; // logic to handle such cases
                      borderRightWidth = '0mm';
                      borderBottomWidth = '0mm';
                      borderLeftWidth = '0mm';

                      borderTopValue = column.style.borderTopValue;
                      borderRightValue = column.style.borderRightValue;
                      borderBottomValue = column.style.borderBottomValue;
                      borderLeftValue = column.style.borderLeftValue;
                    } else {
                      const borderWidthSplit = column.style.borderWidth.split(' '); // ignore input values if noBorder is false, assume css property to be the source of truth
                      if (borderWidthSplit.length === 4) {
                        // in version 0, border width property may have all 4 side specified or only one
                        [borderTopWidth, borderRightWidth, borderBottomWidth, borderLeftWidth] = borderWidthSplit;
                        [borderTopValue, borderRightValue, borderBottomValue, borderLeftValue] = borderWidthSplit;
                      } else {
                        borderTopWidth = borderWidthSplit[0];
                        borderRightWidth = borderWidthSplit[0];
                        borderBottomWidth = borderWidthSplit[0];
                        borderLeftWidth = borderWidthSplit[0];
                        borderTopValue = borderWidthSplit[0];
                        borderRightValue = borderWidthSplit[0];
                        borderBottomValue = borderWidthSplit[0];
                        borderLeftValue = borderWidthSplit[0];
                      }
                    }

                    let paddingTop;
                    let paddingRight;
                    let paddingBottom;
                    let paddingLeft;
                    const paddingSplit = column.style.padding.split(' '); // ignore input values, assume css property to be the source of truth
                    if (paddingSplit.length === 4) {
                      // in version 0, padding property may have all 4 side specified or only one
                      [paddingTop, paddingRight, paddingBottom, paddingLeft] = paddingSplit;
                    } else {
                      paddingTop = paddingSplit[0];
                      paddingRight = paddingSplit[0];
                      paddingBottom = paddingSplit[0];
                      paddingLeft = paddingSplit[0];
                    }

                    updatedColumn.CSSStyles = {
                      borderTopWidth,
                      borderRightWidth,
                      borderBottomWidth,
                      borderLeftWidth,
                      borderStyle: column.style.borderStyle,
                      borderColor: column.style.borderColor,

                      paddingTop,
                      paddingRight,
                      paddingBottom,
                      paddingLeft,

                      fontFamily: column.style.fontFamily,
                      backgroundColor: column.style.backgroundColor,
                      fontSize: column.style.fontSize,
                      textAlign: column.style.textAlign,
                    };

                    updatedColumn.styleInputValues = {
                      noBorder,
                      borderTopValue,
                      borderRightValue,
                      borderBottomValue,
                      borderLeftValue,
                    };

                    updatedRow.cell.push(updatedColumn);
                  }

                  updatedHeader.tableDetails.row.push(updatedRow);
                }

                // to extract column widths, only loop through the first row
                for (const column of header.tableDetails.row[0].column) {
                  updatedHeader.columnWidths.push(column.style.width);
                }

                if (header.tableDetails.header) {
                  const updatedTableHeader = {};

                  let borderTopWidth;
                  let borderRightWidth;
                  let borderBottomWidth;
                  let borderLeftWidth;
                  let borderTopValue;
                  let borderRightValue;
                  let borderBottomValue;
                  let borderLeftValue;
                  const noBorder = header.tableDetails.header.style.noBorder;
                  if (noBorder) {
                    // noticed in version 0, the css property value doesn't match up with the input values at times, probably due to bugs in version 0
                    borderTopWidth = '0mm'; // logic to handle such cases
                    borderRightWidth = '0mm';
                    borderBottomWidth = '0mm';
                    borderLeftWidth = '0mm';

                    borderTopValue = header.tableDetails.header.style.borderTopValue;
                    borderRightValue = header.tableDetails.header.style.borderRightValue;
                    borderBottomValue = header.tableDetails.header.style.borderBottomValue;
                    borderLeftValue = header.tableDetails.header.style.borderLeftValue;
                  } else {
                    const borderWidthSplit = header.tableDetails.header.style.borderWidth.split(' '); // ignore input values if noBorder is false, assume css property to be the source of truth
                    if (borderWidthSplit.length === 4) {
                      // in version 0, border width property may have all 4 side specified or only one
                      [borderTopWidth, borderRightWidth, borderBottomWidth, borderLeftWidth] = borderWidthSplit;
                      [borderTopValue, borderRightValue, borderBottomValue, borderLeftValue] = borderWidthSplit;
                    } else {
                      borderTopWidth = borderWidthSplit[0];
                      borderRightWidth = borderWidthSplit[0];
                      borderBottomWidth = borderWidthSplit[0];
                      borderLeftWidth = borderWidthSplit[0];
                      borderTopValue = borderWidthSplit[0];
                      borderRightValue = borderWidthSplit[0];
                      borderBottomValue = borderWidthSplit[0];
                      borderLeftValue = borderWidthSplit[0];
                    }
                  }

                  let paddingTop;
                  let paddingRight;
                  let paddingBottom;
                  let paddingLeft;
                  const paddingSplit = header.tableDetails.header.style.padding.split(' '); // ignore input values, assume css property to be the source of truth
                  if (paddingSplit.length === 4) {
                    // in version 0, padding property may have all 4 side specified or only one
                    [paddingTop, paddingRight, paddingBottom, paddingLeft] = paddingSplit;
                  } else {
                    paddingTop = paddingSplit[0];
                    paddingRight = paddingSplit[0];
                    paddingBottom = paddingSplit[0];
                    paddingLeft = paddingSplit[0];
                  }

                  updatedTableHeader.CSSStyles = {
                    borderTopWidth,
                    borderRightWidth,
                    borderBottomWidth,
                    borderLeftWidth,
                    borderStyle: header.tableDetails.header.style.borderStyle,
                    borderColor: header.tableDetails.header.style.borderColor,

                    paddingTop,
                    paddingRight,
                    paddingBottom,
                    paddingLeft,

                    fontFamily: header.style.fontFamily, // version 0 doesn't store this for row, take from header instead
                    backgroundColor: header.tableDetails.header.style.backgroundColor,
                    fontSize: header.style.fontSize, // version 0 doesn't store this for row, take from header instead
                    textAlign: header.style.textAlign, // version 0 doesn't store this for row, take from header instead
                  };

                  updatedTableHeader.styleInputValues = {
                    noBorder,
                    borderTopValue,
                    borderRightValue,
                    borderBottomValue,
                    borderLeftValue,
                  };

                  updatedTableHeader.cell = [];

                  for (const column of header.tableDetails.header.column) {
                    const updatedTableHeaderColumn = {};

                    updatedTableHeaderColumn.value = column.value;
                    updatedTableHeaderColumn.isImage = column.isImage;

                    let borderTopWidth;
                    let borderRightWidth;
                    let borderBottomWidth;
                    let borderLeftWidth;
                    let borderTopValue;
                    let borderRightValue;
                    let borderBottomValue;
                    let borderLeftValue;
                    const noBorder = column.style.noBorder;
                    if (noBorder) {
                      // noticed in version 0, the css property value doesn't match up with the input values at times, probably due to bugs in version 0
                      borderTopWidth = '0mm'; // logic to handle such cases
                      borderRightWidth = '0mm';
                      borderBottomWidth = '0mm';
                      borderLeftWidth = '0mm';

                      borderTopValue = column.style.borderTopValue;
                      borderRightValue = column.style.borderRightValue;
                      borderBottomValue = column.style.borderBottomValue;
                      borderLeftValue = column.style.borderLeftValue;
                    } else {
                      const borderWidthSplit = column.style.borderWidth.split(' '); // ignore input values if noBorder is false, assume css property to be the source of truth
                      if (borderWidthSplit.length === 4) {
                        // in version 0, border width property may have all 4 side specified or only one
                        [borderTopWidth, borderRightWidth, borderBottomWidth, borderLeftWidth] = borderWidthSplit;
                        [borderTopValue, borderRightValue, borderBottomValue, borderLeftValue] = borderWidthSplit;
                      } else {
                        borderTopWidth = borderWidthSplit[0];
                        borderRightWidth = borderWidthSplit[0];
                        borderBottomWidth = borderWidthSplit[0];
                        borderLeftWidth = borderWidthSplit[0];
                        borderTopValue = borderWidthSplit[0];
                        borderRightValue = borderWidthSplit[0];
                        borderBottomValue = borderWidthSplit[0];
                        borderLeftValue = borderWidthSplit[0];
                      }
                    }

                    let paddingTop;
                    let paddingRight;
                    let paddingBottom;
                    let paddingLeft;
                    const paddingSplit = column.style.padding.split(' '); // ignore input values, assume css property to be the source of truth
                    if (paddingSplit.length === 4) {
                      // in version 0, padding property may have all 4 side specified or only one
                      [paddingTop, paddingRight, paddingBottom, paddingLeft] = paddingSplit;
                    } else {
                      paddingTop = paddingSplit[0];
                      paddingRight = paddingSplit[0];
                      paddingBottom = paddingSplit[0];
                      paddingLeft = paddingSplit[0];
                    }

                    updatedTableHeaderColumn.CSSStyles = {
                      borderTopWidth,
                      borderRightWidth,
                      borderBottomWidth,
                      borderLeftWidth,
                      borderStyle: column.style.borderStyle,
                      borderColor: column.style.borderColor,

                      paddingTop,
                      paddingRight,
                      paddingBottom,
                      paddingLeft,

                      fontFamily: column.style.fontFamily,
                      backgroundColor: column.style.backgroundColor,
                      fontSize: column.style.fontSize,
                      textAlign: column.style.textAlign,
                    };

                    updatedTableHeaderColumn.styleInputValues = {
                      noBorder,
                      borderTopValue,
                      borderRightValue,
                      borderBottomValue,
                      borderLeftValue,
                    };

                    updatedTableHeader.cell.push(updatedTableHeaderColumn);
                  }

                  updatedHeader.tableDetails.header = updatedTableHeader;
                }
              }
              break;
          }

          updatedTemplate.header.push(updatedHeader);
        }
      }

      if (template.headerStyle) {
        updatedTemplate.headerStyle = {
          paddingTop: template.headerStyle.paddingTopValue,
          paddingRight: template.headerStyle.paddingRightValue,
          paddingBottom: template.headerStyle.paddingBottomValue,
          paddingLeft: template.headerStyle.paddingLeftValue,
        };
      } else {
        updatedTemplate.headerStyle = {
          paddingTop: '0mm',
          paddingRight: '0mm',
          paddingBottom: '0mm',
          paddingLeft: '0mm',
        };
      }

      if (template.pages) {
        for (const page of template.pages) {
          const updatedPage = {
            fields: [],
          };

          for (const field of page.fields) {
            const updatedField = {};

            switch (
              field.type // version 0 should only have string and table
            ) {
              case 'string':
                {
                  updatedField.type = 'string';
                  updatedField.value = field.value;
                  updatedField.isImage = field.isImage;

                  let borderTopWidth;
                  let borderRightWidth;
                  let borderBottomWidth;
                  let borderLeftWidth;
                  let borderTopValue;
                  let borderRightValue;
                  let borderBottomValue;
                  let borderLeftValue;
                  const noBorder = field.style.noBorder;
                  if (noBorder) {
                    // noticed in version 0, the css property value doesn't match up with the input values at times, probably due to bugs in version 0
                    borderTopWidth = '0mm'; // logic to handle such cases
                    borderRightWidth = '0mm';
                    borderBottomWidth = '0mm';
                    borderLeftWidth = '0mm';

                    borderTopValue = field.style.borderTopValue;
                    borderRightValue = field.style.borderRightValue;
                    borderBottomValue = field.style.borderBottomValue;
                    borderLeftValue = field.style.borderLeftValue;
                  } else {
                    const borderWidthSplit = field.style.borderWidth.split(' '); // ignore input values if noBorder is false, assume css property to be the source of truth
                    if (borderWidthSplit.length === 4) {
                      // in version 0, border width property may have all 4 side specified or only one
                      [borderTopWidth, borderRightWidth, borderBottomWidth, borderLeftWidth] = borderWidthSplit;
                      [borderTopValue, borderRightValue, borderBottomValue, borderLeftValue] = borderWidthSplit;
                    } else {
                      borderTopWidth = borderWidthSplit[0];
                      borderRightWidth = borderWidthSplit[0];
                      borderBottomWidth = borderWidthSplit[0];
                      borderLeftWidth = borderWidthSplit[0];
                      borderTopValue = borderWidthSplit[0];
                      borderRightValue = borderWidthSplit[0];
                      borderBottomValue = borderWidthSplit[0];
                      borderLeftValue = borderWidthSplit[0];
                    }
                  }

                  let paddingTop;
                  let paddingRight;
                  let paddingBottom;
                  let paddingLeft;
                  const paddingSplit = field.style.padding.split(' '); // ignore input values, assume css property to be the source of truth
                  if (paddingSplit.length === 4) {
                    // in version 0, padding property may have all 4 side specified or only one
                    [paddingTop, paddingRight, paddingBottom, paddingLeft] = paddingSplit;
                  } else {
                    paddingTop = paddingSplit[0];
                    paddingRight = paddingSplit[0];
                    paddingBottom = paddingSplit[0];
                    paddingLeft = paddingSplit[0];
                  }

                  let marginTop;
                  let marginRight;
                  let marginBottom;
                  let marginLeft;
                  const marginSplit = field.style.margin.split(' '); // ignore input values, assume css property to be the source of truth
                  if (marginSplit.length === 4) {
                    // in version 0, margin property may have all 4 side specified or only one
                    [marginTop, marginRight, marginBottom, marginLeft] = marginSplit;
                  } else {
                    marginTop = paddingSplit[0];
                    marginRight = paddingSplit[0];
                    marginBottom = paddingSplit[0];
                    marginLeft = paddingSplit[0];
                  }

                  let height;
                  let heightValue;
                  const fitHeight = field.style.fitHeight;
                  if (fitHeight) {
                    height = 'fit-content';
                    heightValue = field.style.heightValue;
                  } else {
                    height = field.style.height;
                    heightValue = field.style.height;
                  }

                  updatedField.CSSStyles = {
                    position: field.style.position,
                    width: field.style.width,

                    borderTopWidth,
                    borderRightWidth,
                    borderBottomWidth,
                    borderLeftWidth,
                    borderStyle: field.style.borderStyle,
                    borderColor: field.style.borderColor,

                    paddingTop,
                    paddingRight,
                    paddingBottom,
                    paddingLeft,

                    marginTop,
                    marginRight,
                    marginBottom,
                    marginLeft,

                    left: field.style.left,
                    top: field.style.top,

                    height,
                    fontFamily: field.style.fontFamily,
                    backgroundColor: field.style.backgroundColor,
                    fontSize: field.style.fontSize,
                    textAlign: field.style.textAlign,
                  };

                  updatedField.styleInputValues = {
                    noBorder,
                    borderTopValue,
                    borderRightValue,
                    borderBottomValue,
                    borderLeftValue,

                    fitHeight,
                    heightValue,
                  };
                }
                break;

              case 'table':
                {
                  updatedField.type = 'table';
                  updatedField.columnWidths = []; // number of elements in this array need to match the number of columns

                  let borderTopWidth;
                  let borderRightWidth;
                  let borderBottomWidth;
                  let borderLeftWidth;
                  let borderTopValue;
                  let borderRightValue;
                  let borderBottomValue;
                  let borderLeftValue;
                  const noBorder = field.style.noBorder;
                  if (noBorder) {
                    // noticed in version 0, the css property value doesn't match up with the input values at times, probably due to bugs in version 0
                    borderTopWidth = '0mm'; // logic to handle such cases
                    borderRightWidth = '0mm';
                    borderBottomWidth = '0mm';
                    borderLeftWidth = '0mm';

                    borderTopValue = field.style.borderTopValue;
                    borderRightValue = field.style.borderRightValue;
                    borderBottomValue = field.style.borderBottomValue;
                    borderLeftValue = field.style.borderLeftValue;
                  } else {
                    const borderWidthSplit = field.style.borderWidth.split(' '); // ignore input values if noBorder is false, assume css property to be the source of truth
                    if (borderWidthSplit.length === 4) {
                      // in version 0, border width property may have all 4 side specified or only one
                      [borderTopWidth, borderRightWidth, borderBottomWidth, borderLeftWidth] = borderWidthSplit;
                      [borderTopValue, borderRightValue, borderBottomValue, borderLeftValue] = borderWidthSplit;
                    } else {
                      borderTopWidth = borderWidthSplit[0];
                      borderRightWidth = borderWidthSplit[0];
                      borderBottomWidth = borderWidthSplit[0];
                      borderLeftWidth = borderWidthSplit[0];
                      borderTopValue = borderWidthSplit[0];
                      borderRightValue = borderWidthSplit[0];
                      borderBottomValue = borderWidthSplit[0];
                      borderLeftValue = borderWidthSplit[0];
                    }
                  }

                  let paddingTop;
                  let paddingRight;
                  let paddingBottom;
                  let paddingLeft;
                  const paddingSplit = field.style.padding.split(' '); // ignore input values, assume css property to be the source of truth
                  if (paddingSplit.length === 4) {
                    // in version 0, padding property may have all 4 side specified or only one
                    [paddingTop, paddingRight, paddingBottom, paddingLeft] = paddingSplit;
                  } else {
                    paddingTop = paddingSplit[0];
                    paddingRight = paddingSplit[0];
                    paddingBottom = paddingSplit[0];
                    paddingLeft = paddingSplit[0];
                  }

                  let marginTop;
                  let marginRight;
                  let marginBottom;
                  let marginLeft;
                  const marginSplit = field.style.margin.split(' '); // ignore input values, assume css property to be the source of truth
                  if (marginSplit.length === 4) {
                    // in version 0, margin property may have all 4 side specified or only one
                    [marginTop, marginRight, marginBottom, marginLeft] = marginSplit;
                  } else {
                    marginTop = paddingSplit[0];
                    marginRight = paddingSplit[0];
                    marginBottom = paddingSplit[0];
                    marginLeft = paddingSplit[0];
                  }

                  let height;
                  let heightValue;
                  const fitHeight = field.style.fitHeight;
                  if (fitHeight) {
                    height = 'fit-content';
                    heightValue = field.style.heightValue;
                  } else {
                    height = field.style.height;
                    heightValue = field.style.height;
                  }

                  updatedField.CSSStyles = {
                    position: field.style.position,
                    width: field.style.width,

                    borderTopWidth,
                    borderRightWidth,
                    borderBottomWidth,
                    borderLeftWidth,
                    borderStyle: field.style.borderStyle,
                    borderColor: field.style.borderColor,

                    paddingTop,
                    paddingRight,
                    paddingBottom,
                    paddingLeft,

                    marginTop,
                    marginRight,
                    marginBottom,
                    marginLeft,

                    left: field.style.left,
                    top: field.style.top,

                    height,
                    fontFamily: field.style.fontFamily,
                    backgroundColor: field.style.backgroundColor,
                    fontSize: field.style.fontSize,
                    textAlign: field.style.textAlign,

                    tableLayout: 'fixed', // was hardcoded in version 0, continue to hardcode in version 1
                  };

                  updatedField.styleInputValues = {
                    noBorder,
                    borderTopValue,
                    borderRightValue,
                    borderBottomValue,
                    borderLeftValue,

                    fitHeight,
                    heightValue,
                  };

                  updatedField.tableDetails = {
                    tableKey: field.tableDetails.tableKey,
                    row: [],
                  };

                  for (const row of field.tableDetails.row) {
                    const updatedRow = {};

                    let borderTopWidth;
                    let borderRightWidth;
                    let borderBottomWidth;
                    let borderLeftWidth;
                    let borderTopValue;
                    let borderRightValue;
                    let borderBottomValue;
                    let borderLeftValue;
                    const noBorder = row.style.noBorder;
                    if (noBorder) {
                      // noticed in version 0, the css property value doesn't match up with the input values at times, probably due to bugs in version 0
                      borderTopWidth = '0mm'; // logic to handle such cases
                      borderRightWidth = '0mm';
                      borderBottomWidth = '0mm';
                      borderLeftWidth = '0mm';

                      borderTopValue = row.style.borderTopValue;
                      borderRightValue = row.style.borderRightValue;
                      borderBottomValue = row.style.borderBottomValue;
                      borderLeftValue = row.style.borderLeftValue;
                    } else {
                      const borderWidthSplit = row.style.borderWidth.split(' '); // ignore input values if noBorder is false, assume css property to be the source of truth
                      if (borderWidthSplit.length === 4) {
                        // in version 0, border width property may have all 4 side specified or only one
                        [borderTopWidth, borderRightWidth, borderBottomWidth, borderLeftWidth] = borderWidthSplit;
                        [borderTopValue, borderRightValue, borderBottomValue, borderLeftValue] = borderWidthSplit;
                      } else {
                        borderTopWidth = borderWidthSplit[0];
                        borderRightWidth = borderWidthSplit[0];
                        borderBottomWidth = borderWidthSplit[0];
                        borderLeftWidth = borderWidthSplit[0];
                        borderTopValue = borderWidthSplit[0];
                        borderRightValue = borderWidthSplit[0];
                        borderBottomValue = borderWidthSplit[0];
                        borderLeftValue = borderWidthSplit[0];
                      }
                    }

                    let paddingTop;
                    let paddingRight;
                    let paddingBottom;
                    let paddingLeft;
                    const paddingSplit = row.style.padding.split(' '); // ignore input values, assume css property to be the source of truth
                    if (paddingSplit.length === 4) {
                      // in version 0, padding property may have all 4 side specified or only one
                      [paddingTop, paddingRight, paddingBottom, paddingLeft] = paddingSplit;
                    } else {
                      paddingTop = paddingSplit[0];
                      paddingRight = paddingSplit[0];
                      paddingBottom = paddingSplit[0];
                      paddingLeft = paddingSplit[0];
                    }

                    updatedRow.CSSStyles = {
                      borderTopWidth,
                      borderRightWidth,
                      borderBottomWidth,
                      borderLeftWidth,
                      borderStyle: row.style.borderStyle,
                      borderColor: row.style.borderColor,

                      paddingTop,
                      paddingRight,
                      paddingBottom,
                      paddingLeft,

                      fontFamily: field.style.fontFamily, // version 0 doesn't store this for row, take from field instead
                      backgroundColor: row.style.backgroundColor,
                      fontSize: field.style.fontSize, // version 0 doesn't store this for row, take from field instead
                      textAlign: field.style.textAlign, // version 0 doesn't store this for row, take from field instead
                    };

                    updatedRow.styleInputValues = {
                      noBorder,
                      borderTopValue,
                      borderRightValue,
                      borderBottomValue,
                      borderLeftValue,
                    };

                    updatedRow.cell = [];

                    for (const column of row.column) {
                      const updatedColumn = {};

                      updatedColumn.value = column.value;
                      updatedColumn.isImage = column.isImage;

                      let borderTopWidth;
                      let borderRightWidth;
                      let borderBottomWidth;
                      let borderLeftWidth;
                      let borderTopValue;
                      let borderRightValue;
                      let borderBottomValue;
                      let borderLeftValue;
                      const noBorder = column.style.noBorder;
                      if (noBorder) {
                        // noticed in version 0, the css property value doesn't match up with the input values at times, probably due to bugs in version 0
                        borderTopWidth = '0mm'; // logic to handle such cases
                        borderRightWidth = '0mm';
                        borderBottomWidth = '0mm';
                        borderLeftWidth = '0mm';

                        borderTopValue = column.style.borderTopValue;
                        borderRightValue = column.style.borderRightValue;
                        borderBottomValue = column.style.borderBottomValue;
                        borderLeftValue = column.style.borderLeftValue;
                      } else {
                        const borderWidthSplit = column.style.borderWidth.split(' '); // ignore input values if noBorder is false, assume css property to be the source of truth
                        if (borderWidthSplit.length === 4) {
                          // in version 0, border width property may have all 4 side specified or only one
                          [borderTopWidth, borderRightWidth, borderBottomWidth, borderLeftWidth] = borderWidthSplit;
                          [borderTopValue, borderRightValue, borderBottomValue, borderLeftValue] = borderWidthSplit;
                        } else {
                          borderTopWidth = borderWidthSplit[0];
                          borderRightWidth = borderWidthSplit[0];
                          borderBottomWidth = borderWidthSplit[0];
                          borderLeftWidth = borderWidthSplit[0];
                          borderTopValue = borderWidthSplit[0];
                          borderRightValue = borderWidthSplit[0];
                          borderBottomValue = borderWidthSplit[0];
                          borderLeftValue = borderWidthSplit[0];
                        }
                      }

                      let paddingTop;
                      let paddingRight;
                      let paddingBottom;
                      let paddingLeft;
                      const paddingSplit = column.style.padding.split(' '); // ignore input values, assume css property to be the source of truth
                      if (paddingSplit.length === 4) {
                        // in version 0, padding property may have all 4 side specified or only one
                        [paddingTop, paddingRight, paddingBottom, paddingLeft] = paddingSplit;
                      } else {
                        paddingTop = paddingSplit[0];
                        paddingRight = paddingSplit[0];
                        paddingBottom = paddingSplit[0];
                        paddingLeft = paddingSplit[0];
                      }

                      updatedColumn.CSSStyles = {
                        borderTopWidth,
                        borderRightWidth,
                        borderBottomWidth,
                        borderLeftWidth,
                        borderStyle: column.style.borderStyle,
                        borderColor: column.style.borderColor,

                        paddingTop,
                        paddingRight,
                        paddingBottom,
                        paddingLeft,

                        fontFamily: column.style.fontFamily,
                        backgroundColor: column.style.backgroundColor,
                        fontSize: column.style.fontSize,
                        textAlign: column.style.textAlign,
                      };

                      updatedColumn.styleInputValues = {
                        noBorder,
                        borderTopValue,
                        borderRightValue,
                        borderBottomValue,
                        borderLeftValue,
                      };

                      updatedRow.cell.push(updatedColumn);
                    }

                    updatedField.tableDetails.row.push(updatedRow);
                  }

                  // to extract column widths, only loop through the first row
                  for (const column of field.tableDetails.row[0].column) {
                    updatedField.columnWidths.push(column.style.width);
                  }

                  if (field.tableDetails.header) {
                    const updatedTableHeader = {};

                    let borderTopWidth;
                    let borderRightWidth;
                    let borderBottomWidth;
                    let borderLeftWidth;
                    let borderTopValue;
                    let borderRightValue;
                    let borderBottomValue;
                    let borderLeftValue;
                    const noBorder = field.tableDetails.header.style.noBorder;
                    if (noBorder) {
                      // noticed in version 0, the css property value doesn't match up with the input values at times, probably due to bugs in version 0
                      borderTopWidth = '0mm'; // logic to handle such cases
                      borderRightWidth = '0mm';
                      borderBottomWidth = '0mm';
                      borderLeftWidth = '0mm';

                      borderTopValue = field.tableDetails.header.style.borderTopValue;
                      borderRightValue = field.tableDetails.header.style.borderRightValue;
                      borderBottomValue = field.tableDetails.header.style.borderBottomValue;
                      borderLeftValue = field.tableDetails.header.style.borderLeftValue;
                    } else {
                      const borderWidthSplit = field.tableDetails.header.style.borderWidth.split(' '); // ignore input values if noBorder is false, assume css property to be the source of truth
                      if (borderWidthSplit.length === 4) {
                        // in version 0, border width property may have all 4 side specified or only one
                        [borderTopWidth, borderRightWidth, borderBottomWidth, borderLeftWidth] = borderWidthSplit;
                        [borderTopValue, borderRightValue, borderBottomValue, borderLeftValue] = borderWidthSplit;
                      } else {
                        borderTopWidth = borderWidthSplit[0];
                        borderRightWidth = borderWidthSplit[0];
                        borderBottomWidth = borderWidthSplit[0];
                        borderLeftWidth = borderWidthSplit[0];
                        borderTopValue = borderWidthSplit[0];
                        borderRightValue = borderWidthSplit[0];
                        borderBottomValue = borderWidthSplit[0];
                        borderLeftValue = borderWidthSplit[0];
                      }
                    }

                    let paddingTop;
                    let paddingRight;
                    let paddingBottom;
                    let paddingLeft;
                    const paddingSplit = field.tableDetails.header.style.padding.split(' '); // ignore input values, assume css property to be the source of truth
                    if (paddingSplit.length === 4) {
                      // in version 0, padding property may have all 4 side specified or only one
                      [paddingTop, paddingRight, paddingBottom, paddingLeft] = paddingSplit;
                    } else {
                      paddingTop = paddingSplit[0];
                      paddingRight = paddingSplit[0];
                      paddingBottom = paddingSplit[0];
                      paddingLeft = paddingSplit[0];
                    }

                    updatedTableHeader.CSSStyles = {
                      borderTopWidth,
                      borderRightWidth,
                      borderBottomWidth,
                      borderLeftWidth,
                      borderStyle: field.tableDetails.header.style.borderStyle,
                      borderColor: field.tableDetails.header.style.borderColor,

                      paddingTop,
                      paddingRight,
                      paddingBottom,
                      paddingLeft,

                      fontFamily: field.style.fontFamily, // version 0 doesn't store this for row, take from field instead
                      backgroundColor: field.tableDetails.header.style.backgroundColor,
                      fontSize: field.style.fontSize, // version 0 doesn't store this for row, take from field instead
                      textAlign: field.style.textAlign, // version 0 doesn't store this for row, take from field instead
                    };

                    updatedTableHeader.styleInputValues = {
                      noBorder,
                      borderTopValue,
                      borderRightValue,
                      borderBottomValue,
                      borderLeftValue,
                    };

                    updatedTableHeader.cell = [];

                    for (const column of field.tableDetails.header.column) {
                      const updatedTableHeaderColumn = {};

                      updatedTableHeaderColumn.value = column.value;
                      updatedTableHeaderColumn.isImage = column.isImage;

                      let borderTopWidth;
                      let borderRightWidth;
                      let borderBottomWidth;
                      let borderLeftWidth;
                      let borderTopValue;
                      let borderRightValue;
                      let borderBottomValue;
                      let borderLeftValue;
                      const noBorder = column.style.noBorder;
                      if (noBorder) {
                        // noticed in version 0, the css property value doesn't match up with the input values at times, probably due to bugs in version 0
                        borderTopWidth = '0mm'; // logic to handle such cases
                        borderRightWidth = '0mm';
                        borderBottomWidth = '0mm';
                        borderLeftWidth = '0mm';

                        borderTopValue = column.style.borderTopValue;
                        borderRightValue = column.style.borderRightValue;
                        borderBottomValue = column.style.borderBottomValue;
                        borderLeftValue = column.style.borderLeftValue;
                      } else {
                        const borderWidthSplit = column.style.borderWidth.split(' '); // ignore input values if noBorder is false, assume css property to be the source of truth
                        if (borderWidthSplit.length === 4) {
                          // in version 0, border width property may have all 4 side specified or only one
                          [borderTopWidth, borderRightWidth, borderBottomWidth, borderLeftWidth] = borderWidthSplit;
                          [borderTopValue, borderRightValue, borderBottomValue, borderLeftValue] = borderWidthSplit;
                        } else {
                          borderTopWidth = borderWidthSplit[0];
                          borderRightWidth = borderWidthSplit[0];
                          borderBottomWidth = borderWidthSplit[0];
                          borderLeftWidth = borderWidthSplit[0];
                          borderTopValue = borderWidthSplit[0];
                          borderRightValue = borderWidthSplit[0];
                          borderBottomValue = borderWidthSplit[0];
                          borderLeftValue = borderWidthSplit[0];
                        }
                      }

                      let paddingTop;
                      let paddingRight;
                      let paddingBottom;
                      let paddingLeft;
                      const paddingSplit = column.style.padding.split(' '); // ignore input values, assume css property to be the source of truth
                      if (paddingSplit.length === 4) {
                        // in version 0, padding property may have all 4 side specified or only one
                        [paddingTop, paddingRight, paddingBottom, paddingLeft] = paddingSplit;
                      } else {
                        paddingTop = paddingSplit[0];
                        paddingRight = paddingSplit[0];
                        paddingBottom = paddingSplit[0];
                        paddingLeft = paddingSplit[0];
                      }

                      updatedTableHeaderColumn.CSSStyles = {
                        borderTopWidth,
                        borderRightWidth,
                        borderBottomWidth,
                        borderLeftWidth,
                        borderStyle: column.style.borderStyle,
                        borderColor: column.style.borderColor,

                        paddingTop,
                        paddingRight,
                        paddingBottom,
                        paddingLeft,

                        fontFamily: column.style.fontFamily,
                        backgroundColor: column.style.backgroundColor,
                        fontSize: column.style.fontSize,
                        textAlign: column.style.textAlign,
                      };

                      updatedTableHeaderColumn.styleInputValues = {
                        noBorder,
                        borderTopValue,
                        borderRightValue,
                        borderBottomValue,
                        borderLeftValue,
                      };

                      updatedTableHeader.cell.push(updatedTableHeaderColumn);
                    }

                    updatedField.tableDetails.header = updatedTableHeader;
                  }
                }
                break;
            }

            updatedPage.fields.push(updatedField);
          }

          updatedTemplate.pages.push(updatedPage);
        }
      }

      if (template.footer) {
        for (const footer of template.footer) {
          const updatedFooter = {};

          switch (
            footer.type // version 0 should only have string and table
          ) {
            case 'string':
              {
                updatedFooter.type = 'string';
                updatedFooter.value = footer.value;
                updatedFooter.isImage = footer.isImage;

                let borderTopWidth;
                let borderRightWidth;
                let borderBottomWidth;
                let borderLeftWidth;
                let borderTopValue;
                let borderRightValue;
                let borderBottomValue;
                let borderLeftValue;
                const noBorder = footer.style.noBorder;
                if (noBorder) {
                  // noticed in version 0, the css property value doesn't match up with the input values at times, probably due to bugs in version 0
                  borderTopWidth = '0mm'; // logic to handle such cases
                  borderRightWidth = '0mm';
                  borderBottomWidth = '0mm';
                  borderLeftWidth = '0mm';

                  borderTopValue = footer.style.borderTopValue;
                  borderRightValue = footer.style.borderRightValue;
                  borderBottomValue = footer.style.borderBottomValue;
                  borderLeftValue = footer.style.borderLeftValue;
                } else {
                  const borderWidthSplit = footer.style.borderWidth.split(' '); // ignore input values if noBorder is false, assume css property to be the source of truth
                  if (borderWidthSplit.length === 4) {
                    // in version 0, border width property may have all 4 side specified or only one
                    [borderTopWidth, borderRightWidth, borderBottomWidth, borderLeftWidth] = borderWidthSplit;
                    [borderTopValue, borderRightValue, borderBottomValue, borderLeftValue] = borderWidthSplit;
                  } else {
                    borderTopWidth = borderWidthSplit[0];
                    borderRightWidth = borderWidthSplit[0];
                    borderBottomWidth = borderWidthSplit[0];
                    borderLeftWidth = borderWidthSplit[0];
                    borderTopValue = borderWidthSplit[0];
                    borderRightValue = borderWidthSplit[0];
                    borderBottomValue = borderWidthSplit[0];
                    borderLeftValue = borderWidthSplit[0];
                  }
                }

                let paddingTop;
                let paddingRight;
                let paddingBottom;
                let paddingLeft;
                const paddingSplit = footer.style.padding.split(' '); // ignore input values, assume css property to be the source of truth
                if (paddingSplit.length === 4) {
                  // in version 0, padding property may have all 4 side specified or only one
                  [paddingTop, paddingRight, paddingBottom, paddingLeft] = paddingSplit;
                } else {
                  paddingTop = paddingSplit[0];
                  paddingRight = paddingSplit[0];
                  paddingBottom = paddingSplit[0];
                  paddingLeft = paddingSplit[0];
                }

                let marginTop;
                let marginRight;
                let marginBottom;
                let marginLeft;
                const marginSplit = footer.style.margin.split(' '); // ignore input values, assume css property to be the source of truth
                if (marginSplit.length === 4) {
                  // in version 0, margin property may have all 4 side specified or only one
                  [marginTop, marginRight, marginBottom, marginLeft] = marginSplit;
                } else {
                  marginTop = paddingSplit[0];
                  marginRight = paddingSplit[0];
                  marginBottom = paddingSplit[0];
                  marginLeft = paddingSplit[0];
                }

                let height;
                let heightValue;
                const fitHeight = footer.style.fitHeight;
                if (fitHeight) {
                  height = 'fit-content';
                  heightValue = footer.style.heightValue;
                } else {
                  height = footer.style.height;
                  heightValue = footer.style.height;
                }

                updatedFooter.CSSStyles = {
                  position: footer.style.position,
                  width: footer.style.width,

                  borderTopWidth,
                  borderRightWidth,
                  borderBottomWidth,
                  borderLeftWidth,
                  borderStyle: footer.style.borderStyle,
                  borderColor: footer.style.borderColor,

                  paddingTop,
                  paddingRight,
                  paddingBottom,
                  paddingLeft,

                  marginTop,
                  marginRight,
                  marginBottom,
                  marginLeft,

                  left: footer.style.left,
                  top: footer.style.top,

                  height,
                  fontFamily: footer.style.fontFamily,
                  backgroundColor: footer.style.backgroundColor,
                  fontSize: footer.style.fontSize,
                  textAlign: footer.style.textAlign,
                };

                updatedFooter.styleInputValues = {
                  noBorder,
                  borderTopValue,
                  borderRightValue,
                  borderBottomValue,
                  borderLeftValue,

                  fitHeight,
                  heightValue,
                };
              }
              break;

            case 'table':
              {
                updatedFooter.type = 'table';
                updatedFooter.columnWidths = []; // number of elements in this array need to match the number of columns, populated later in the column for-loop

                let borderTopWidth;
                let borderRightWidth;
                let borderBottomWidth;
                let borderLeftWidth;
                let borderTopValue;
                let borderRightValue;
                let borderBottomValue;
                let borderLeftValue;
                const noBorder = footer.style.noBorder;
                if (noBorder) {
                  // noticed in version 0, the css property value doesn't match up with the input values at times, probably due to bugs in version 0
                  borderTopWidth = '0mm'; // logic to handle such cases
                  borderRightWidth = '0mm';
                  borderBottomWidth = '0mm';
                  borderLeftWidth = '0mm';

                  borderTopValue = footer.style.borderTopValue;
                  borderRightValue = footer.style.borderRightValue;
                  borderBottomValue = footer.style.borderBottomValue;
                  borderLeftValue = footer.style.borderLeftValue;
                } else {
                  const borderWidthSplit = footer.style.borderWidth.split(' '); // ignore input values if noBorder is false, assume css property to be the source of truth
                  if (borderWidthSplit.length === 4) {
                    // in version 0, border width property may have all 4 side specified or only one
                    [borderTopWidth, borderRightWidth, borderBottomWidth, borderLeftWidth] = borderWidthSplit;
                    [borderTopValue, borderRightValue, borderBottomValue, borderLeftValue] = borderWidthSplit;
                  } else {
                    borderTopWidth = borderWidthSplit[0];
                    borderRightWidth = borderWidthSplit[0];
                    borderBottomWidth = borderWidthSplit[0];
                    borderLeftWidth = borderWidthSplit[0];
                    borderTopValue = borderWidthSplit[0];
                    borderRightValue = borderWidthSplit[0];
                    borderBottomValue = borderWidthSplit[0];
                    borderLeftValue = borderWidthSplit[0];
                  }
                }

                let paddingTop;
                let paddingRight;
                let paddingBottom;
                let paddingLeft;
                const paddingSplit = footer.style.padding.split(' '); // ignore input values, assume css property to be the source of truth
                if (paddingSplit.length === 4) {
                  // in version 0, padding property may have all 4 side specified or only one
                  [paddingTop, paddingRight, paddingBottom, paddingLeft] = paddingSplit;
                } else {
                  paddingTop = paddingSplit[0];
                  paddingRight = paddingSplit[0];
                  paddingBottom = paddingSplit[0];
                  paddingLeft = paddingSplit[0];
                }

                let marginTop;
                let marginRight;
                let marginBottom;
                let marginLeft;
                const marginSplit = footer.style.margin.split(' '); // ignore input values, assume css property to be the source of truth
                if (marginSplit.length === 4) {
                  // in version 0, margin property may have all 4 side specified or only one
                  [marginTop, marginRight, marginBottom, marginLeft] = marginSplit;
                } else {
                  marginTop = paddingSplit[0];
                  marginRight = paddingSplit[0];
                  marginBottom = paddingSplit[0];
                  marginLeft = paddingSplit[0];
                }

                let height;
                let heightValue;
                const fitHeight = footer.style.fitHeight;
                if (fitHeight) {
                  height = 'fit-content';
                  heightValue = footer.style.heightValue;
                } else {
                  height = footer.style.height;
                  heightValue = footer.style.height;
                }

                updatedFooter.CSSStyles = {
                  position: footer.style.position,
                  width: footer.style.width,

                  borderTopWidth,
                  borderRightWidth,
                  borderBottomWidth,
                  borderLeftWidth,
                  borderStyle: footer.style.borderStyle,
                  borderColor: footer.style.borderColor,

                  paddingTop,
                  paddingRight,
                  paddingBottom,
                  paddingLeft,

                  marginTop,
                  marginRight,
                  marginBottom,
                  marginLeft,

                  left: footer.style.left,
                  top: footer.style.top,

                  height,
                  fontFamily: footer.style.fontFamily,
                  backgroundColor: footer.style.backgroundColor,
                  fontSize: footer.style.fontSize,
                  textAlign: footer.style.textAlign,

                  tableLayout: 'fixed', // was hardcoded in version 0, continue to hardcode in version 1
                };

                updatedFooter.styleInputValues = {
                  noBorder,
                  borderTopValue,
                  borderRightValue,
                  borderBottomValue,
                  borderLeftValue,

                  fitHeight,
                  heightValue,
                };

                updatedFooter.tableDetails = {
                  tableKey: footer.tableDetails.tableKey,
                  row: [],
                };

                for (const row of footer.tableDetails.row) {
                  const updatedRow = {};

                  let borderTopWidth;
                  let borderRightWidth;
                  let borderBottomWidth;
                  let borderLeftWidth;
                  let borderTopValue;
                  let borderRightValue;
                  let borderBottomValue;
                  let borderLeftValue;
                  const noBorder = row.style.noBorder;
                  if (noBorder) {
                    // noticed in version 0, the css property value doesn't match up with the input values at times, probably due to bugs in version 0
                    borderTopWidth = '0mm'; // logic to handle such cases
                    borderRightWidth = '0mm';
                    borderBottomWidth = '0mm';
                    borderLeftWidth = '0mm';

                    borderTopValue = row.style.borderTopValue;
                    borderRightValue = row.style.borderRightValue;
                    borderBottomValue = row.style.borderBottomValue;
                    borderLeftValue = row.style.borderLeftValue;
                  } else {
                    const borderWidthSplit = row.style.borderWidth.split(' '); // ignore input values if noBorder is false, assume css property to be the source of truth
                    if (borderWidthSplit.length === 4) {
                      // in version 0, border width property may have all 4 side specified or only one
                      [borderTopWidth, borderRightWidth, borderBottomWidth, borderLeftWidth] = borderWidthSplit;
                      [borderTopValue, borderRightValue, borderBottomValue, borderLeftValue] = borderWidthSplit;
                    } else {
                      borderTopWidth = borderWidthSplit[0];
                      borderRightWidth = borderWidthSplit[0];
                      borderBottomWidth = borderWidthSplit[0];
                      borderLeftWidth = borderWidthSplit[0];
                      borderTopValue = borderWidthSplit[0];
                      borderRightValue = borderWidthSplit[0];
                      borderBottomValue = borderWidthSplit[0];
                      borderLeftValue = borderWidthSplit[0];
                    }
                  }

                  let paddingTop;
                  let paddingRight;
                  let paddingBottom;
                  let paddingLeft;
                  const paddingSplit = row.style.padding.split(' '); // ignore input values, assume css property to be the source of truth
                  if (paddingSplit.length === 4) {
                    // in version 0, padding property may have all 4 side specified or only one
                    [paddingTop, paddingRight, paddingBottom, paddingLeft] = paddingSplit;
                  } else {
                    paddingTop = paddingSplit[0];
                    paddingRight = paddingSplit[0];
                    paddingBottom = paddingSplit[0];
                    paddingLeft = paddingSplit[0];
                  }

                  updatedRow.CSSStyles = {
                    borderTopWidth,
                    borderRightWidth,
                    borderBottomWidth,
                    borderLeftWidth,
                    borderStyle: row.style.borderStyle,
                    borderColor: row.style.borderColor,

                    paddingTop,
                    paddingRight,
                    paddingBottom,
                    paddingLeft,

                    fontFamily: footer.style.fontFamily, // version 0 doesn't store this for row, take from footer instead
                    backgroundColor: row.style.backgroundColor,
                    fontSize: footer.style.fontSize, // version 0 doesn't store this for row, take from footer instead
                    textAlign: footer.style.textAlign, // version 0 doesn't store this for row, take from footer instead
                  };

                  updatedRow.styleInputValues = {
                    noBorder,
                    borderTopValue,
                    borderRightValue,
                    borderBottomValue,
                    borderLeftValue,
                  };

                  updatedRow.cell = [];

                  for (const column of row.column) {
                    const updatedColumn = {};

                    updatedColumn.value = column.value;
                    updatedColumn.isImage = column.isImage;

                    let borderTopWidth;
                    let borderRightWidth;
                    let borderBottomWidth;
                    let borderLeftWidth;
                    let borderTopValue;
                    let borderRightValue;
                    let borderBottomValue;
                    let borderLeftValue;
                    const noBorder = column.style.noBorder;
                    if (noBorder) {
                      // noticed in version 0, the css property value doesn't match up with the input values at times, probably due to bugs in version 0
                      borderTopWidth = '0mm'; // logic to handle such cases
                      borderRightWidth = '0mm';
                      borderBottomWidth = '0mm';
                      borderLeftWidth = '0mm';

                      borderTopValue = column.style.borderTopValue;
                      borderRightValue = column.style.borderRightValue;
                      borderBottomValue = column.style.borderBottomValue;
                      borderLeftValue = column.style.borderLeftValue;
                    } else {
                      const borderWidthSplit = column.style.borderWidth.split(' '); // ignore input values if noBorder is false, assume css property to be the source of truth
                      if (borderWidthSplit.length === 4) {
                        // in version 0, border width property may have all 4 side specified or only one
                        [borderTopWidth, borderRightWidth, borderBottomWidth, borderLeftWidth] = borderWidthSplit;
                        [borderTopValue, borderRightValue, borderBottomValue, borderLeftValue] = borderWidthSplit;
                      } else {
                        borderTopWidth = borderWidthSplit[0];
                        borderRightWidth = borderWidthSplit[0];
                        borderBottomWidth = borderWidthSplit[0];
                        borderLeftWidth = borderWidthSplit[0];
                        borderTopValue = borderWidthSplit[0];
                        borderRightValue = borderWidthSplit[0];
                        borderBottomValue = borderWidthSplit[0];
                        borderLeftValue = borderWidthSplit[0];
                      }
                    }

                    let paddingTop;
                    let paddingRight;
                    let paddingBottom;
                    let paddingLeft;
                    const paddingSplit = column.style.padding.split(' '); // ignore input values, assume css property to be the source of truth
                    if (paddingSplit.length === 4) {
                      // in version 0, padding property may have all 4 side specified or only one
                      [paddingTop, paddingRight, paddingBottom, paddingLeft] = paddingSplit;
                    } else {
                      paddingTop = paddingSplit[0];
                      paddingRight = paddingSplit[0];
                      paddingBottom = paddingSplit[0];
                      paddingLeft = paddingSplit[0];
                    }

                    updatedColumn.CSSStyles = {
                      borderTopWidth,
                      borderRightWidth,
                      borderBottomWidth,
                      borderLeftWidth,
                      borderStyle: column.style.borderStyle,
                      borderColor: column.style.borderColor,

                      paddingTop,
                      paddingRight,
                      paddingBottom,
                      paddingLeft,

                      fontFamily: column.style.fontFamily,
                      backgroundColor: column.style.backgroundColor,
                      fontSize: column.style.fontSize,
                      textAlign: column.style.textAlign,
                    };

                    updatedColumn.styleInputValues = {
                      noBorder,
                      borderTopValue,
                      borderRightValue,
                      borderBottomValue,
                      borderLeftValue,
                    };

                    updatedRow.cell.push(updatedColumn);
                  }

                  updatedFooter.tableDetails.row.push(updatedRow);
                }

                // to extract column widths, only loop through the first row
                for (const column of footer.tableDetails.row[0].column) {
                  updatedFooter.columnWidths.push(column.style.width);
                }

                if (footer.tableDetails.header) {
                  const updatedTableHeader = {};

                  let borderTopWidth;
                  let borderRightWidth;
                  let borderBottomWidth;
                  let borderLeftWidth;
                  let borderTopValue;
                  let borderRightValue;
                  let borderBottomValue;
                  let borderLeftValue;
                  const noBorder = footer.tableDetails.header.style.noBorder;
                  if (noBorder) {
                    // noticed in version 0, the css property value doesn't match up with the input values at times, probably due to bugs in version 0
                    borderTopWidth = '0mm'; // logic to handle such cases
                    borderRightWidth = '0mm';
                    borderBottomWidth = '0mm';
                    borderLeftWidth = '0mm';

                    borderTopValue = footer.tableDetails.header.style.borderTopValue;
                    borderRightValue = footer.tableDetails.header.style.borderRightValue;
                    borderBottomValue = footer.tableDetails.header.style.borderBottomValue;
                    borderLeftValue = footer.tableDetails.header.style.borderLeftValue;
                  } else {
                    const borderWidthSplit = footer.tableDetails.header.style.borderWidth.split(' '); // ignore input values if noBorder is false, assume css property to be the source of truth
                    if (borderWidthSplit.length === 4) {
                      // in version 0, border width property may have all 4 side specified or only one
                      [borderTopWidth, borderRightWidth, borderBottomWidth, borderLeftWidth] = borderWidthSplit;
                      [borderTopValue, borderRightValue, borderBottomValue, borderLeftValue] = borderWidthSplit;
                    } else {
                      borderTopWidth = borderWidthSplit[0];
                      borderRightWidth = borderWidthSplit[0];
                      borderBottomWidth = borderWidthSplit[0];
                      borderLeftWidth = borderWidthSplit[0];
                      borderTopValue = borderWidthSplit[0];
                      borderRightValue = borderWidthSplit[0];
                      borderBottomValue = borderWidthSplit[0];
                      borderLeftValue = borderWidthSplit[0];
                    }
                  }

                  let paddingTop;
                  let paddingRight;
                  let paddingBottom;
                  let paddingLeft;
                  const paddingSplit = footer.tableDetails.header.style.padding.split(' '); // ignore input values, assume css property to be the source of truth
                  if (paddingSplit.length === 4) {
                    // in version 0, padding property may have all 4 side specified or only one
                    [paddingTop, paddingRight, paddingBottom, paddingLeft] = paddingSplit;
                  } else {
                    paddingTop = paddingSplit[0];
                    paddingRight = paddingSplit[0];
                    paddingBottom = paddingSplit[0];
                    paddingLeft = paddingSplit[0];
                  }

                  updatedTableHeader.CSSStyles = {
                    borderTopWidth,
                    borderRightWidth,
                    borderBottomWidth,
                    borderLeftWidth,
                    borderStyle: footer.tableDetails.header.style.borderStyle,
                    borderColor: footer.tableDetails.header.style.borderColor,

                    paddingTop,
                    paddingRight,
                    paddingBottom,
                    paddingLeft,

                    fontFamily: footer.style.fontFamily, // version 0 doesn't store this for row, take from footer instead
                    backgroundColor: footer.tableDetails.header.style.backgroundColor,
                    fontSize: footer.style.fontSize, // version 0 doesn't store this for row, take from footer instead
                    textAlign: footer.style.textAlign, // version 0 doesn't store this for row, take from footer instead
                  };

                  updatedTableHeader.styleInputValues = {
                    noBorder,
                    borderTopValue,
                    borderRightValue,
                    borderBottomValue,
                    borderLeftValue,
                  };

                  updatedTableHeader.cell = [];

                  for (const column of footer.tableDetails.header.column) {
                    const updatedTableHeaderColumn = {};

                    updatedTableHeaderColumn.value = column.value;
                    updatedTableHeaderColumn.isImage = column.isImage;

                    let borderTopWidth;
                    let borderRightWidth;
                    let borderBottomWidth;
                    let borderLeftWidth;
                    let borderTopValue;
                    let borderRightValue;
                    let borderBottomValue;
                    let borderLeftValue;
                    const noBorder = column.style.noBorder;
                    if (noBorder) {
                      // noticed in version 0, the css property value doesn't match up with the input values at times, probably due to bugs in version 0
                      borderTopWidth = '0mm'; // logic to handle such cases
                      borderRightWidth = '0mm';
                      borderBottomWidth = '0mm';
                      borderLeftWidth = '0mm';

                      borderTopValue = column.style.borderTopValue;
                      borderRightValue = column.style.borderRightValue;
                      borderBottomValue = column.style.borderBottomValue;
                      borderLeftValue = column.style.borderLeftValue;
                    } else {
                      const borderWidthSplit = column.style.borderWidth.split(' '); // ignore input values if noBorder is false, assume css property to be the source of truth
                      if (borderWidthSplit.length === 4) {
                        // in version 0, border width property may have all 4 side specified or only one
                        [borderTopWidth, borderRightWidth, borderBottomWidth, borderLeftWidth] = borderWidthSplit;
                        [borderTopValue, borderRightValue, borderBottomValue, borderLeftValue] = borderWidthSplit;
                      } else {
                        borderTopWidth = borderWidthSplit[0];
                        borderRightWidth = borderWidthSplit[0];
                        borderBottomWidth = borderWidthSplit[0];
                        borderLeftWidth = borderWidthSplit[0];
                        borderTopValue = borderWidthSplit[0];
                        borderRightValue = borderWidthSplit[0];
                        borderBottomValue = borderWidthSplit[0];
                        borderLeftValue = borderWidthSplit[0];
                      }
                    }

                    let paddingTop;
                    let paddingRight;
                    let paddingBottom;
                    let paddingLeft;
                    const paddingSplit = column.style.padding.split(' '); // ignore input values, assume css property to be the source of truth
                    if (paddingSplit.length === 4) {
                      // in version 0, padding property may have all 4 side specified or only one
                      [paddingTop, paddingRight, paddingBottom, paddingLeft] = paddingSplit;
                    } else {
                      paddingTop = paddingSplit[0];
                      paddingRight = paddingSplit[0];
                      paddingBottom = paddingSplit[0];
                      paddingLeft = paddingSplit[0];
                    }

                    updatedTableHeaderColumn.CSSStyles = {
                      borderTopWidth,
                      borderRightWidth,
                      borderBottomWidth,
                      borderLeftWidth,
                      borderStyle: column.style.borderStyle,
                      borderColor: column.style.borderColor,

                      paddingTop,
                      paddingRight,
                      paddingBottom,
                      paddingLeft,

                      fontFamily: column.style.fontFamily,
                      backgroundColor: column.style.backgroundColor,
                      fontSize: column.style.fontSize,
                      textAlign: column.style.textAlign,
                    };

                    updatedTableHeaderColumn.styleInputValues = {
                      noBorder,
                      borderTopValue,
                      borderRightValue,
                      borderBottomValue,
                      borderLeftValue,
                    };

                    updatedTableHeader.cell.push(updatedTableHeaderColumn);
                  }

                  updatedFooter.tableDetails.header = updatedTableHeader;
                }
              }
              break;
          }

          updatedTemplate.footer.push(updatedFooter);
        }
      }

      if (template.footerStyle) {
        updatedTemplate.footerStyle = {
          paddingTop: template.footerStyle.paddingTopValue,
          paddingRight: template.footerStyle.paddingRightValue,
          paddingBottom: template.footerStyle.paddingBottomValue,
          paddingLeft: template.footerStyle.paddingLeftValue,
        };
      } else {
        updatedTemplate.footerStyle = {
          paddingTop: '0mm',
          paddingRight: '0mm',
          paddingBottom: '0mm',
          paddingLeft: '0mm',
        };
      }

      return updatedTemplate;
    },
    convertTemplateVersionOneToTwo: function (template) {
      const updatedTemplate = {
        version: 2,
        header: [],
        headerStyle: {},
        pages: [],
        footer: [],
        footerStyle: {},
      };

      if (template.header) {
        for (const header of template.header) {
          let updatedHeader = {};

          switch (
            header.type // version 1 should only have string and table
          ) {
            case 'string':
              {
                updatedHeader = { ...header };
                updatedHeader.fieldKey = '';
              }
              break;

            case 'table':
              {
                updatedHeader = { ...header };
                updatedHeader.CSSStyles.verticalAlign = 'middle';
                updatedHeader.tableDetails.fieldKey = '';
                for (const row of updatedHeader.tableDetails.row) {
                  row.CSSStyles.verticalAlign = 'inherit';

                  for (const cell of row.cell) {
                    cell.CSSStyles.verticalAlign = 'inherit';
                  }
                }

                if (header.tableDetails.header) {
                  updatedHeader.tableDetails.header = { ...header.tableDetails.header };
                  updatedHeader.tableDetails.header.CSSStyles.verticalAlign = 'inherit';

                  for (const cell of updatedHeader.tableDetails.header.cell) {
                    cell.CSSStyles.verticalAlign = 'inherit';
                  }
                }
              }
              break;
          }

          updatedTemplate.header.push(updatedHeader);
        }
      }

      if (template.headerStyle) {
        updatedTemplate.headerStyle = { ...template.headerStyle };
      } else {
        updatedTemplate.headerStyle = {
          paddingTop: '0mm',
          paddingRight: '0mm',
          paddingBottom: '0mm',
          paddingLeft: '0mm',
        };
      }

      if (template.pages) {
        for (const page of template.pages) {
          const updatedPage = {
            fields: [],
          };

          for (const field of page.fields) {
            let updatedField = {};

            switch (
              field.type // version 1 should only have string and table
            ) {
              case 'string':
                {
                  updatedField = { ...field };
                  updatedField.fieldKey = '';
                }
                break;

              case 'table':
                {
                  updatedField = { ...field };
                  updatedField.CSSStyles.verticalAlign = 'middle';
                  updatedField.tableDetails.fieldKey = '';
                  for (const row of updatedField.tableDetails.row) {
                    row.CSSStyles.verticalAlign = 'inherit';

                    for (const cell of row.cell) {
                      cell.CSSStyles.verticalAlign = 'inherit';
                    }
                  }

                  if (field.tableDetails.header) {
                    updatedField.tableDetails.header = { ...field.tableDetails.header };
                    updatedField.tableDetails.header.CSSStyles.verticalAlign = 'inherit';

                    for (const cell of updatedField.tableDetails.header.cell) {
                      cell.CSSStyles.verticalAlign = 'inherit';
                    }
                  }
                }
                break;
            }

            updatedPage.fields.push(updatedField);
          }

          updatedTemplate.pages.push(updatedPage);
        }
      }

      if (template.footer) {
        for (const footer of template.footer) {
          let updatedFooter = {};

          switch (
            footer.type // version 1 should only have string and table
          ) {
            case 'string':
              {
                updatedFooter = { ...footer };
                updatedFooter.fieldKey = '';
              }
              break;

            case 'table':
              {
                updatedFooter = { ...footer };
                updatedFooter.CSSStyles.verticalAlign = 'middle';
                updatedFooter.tableDetails.fieldKey = '';
                for (const row of updatedFooter.tableDetails.row) {
                  row.CSSStyles.verticalAlign = 'inherit';

                  for (const cell of row.cell) {
                    cell.CSSStyles.verticalAlign = 'inherit';
                  }
                }

                if (footer.tableDetails.header) {
                  updatedFooter.tableDetails.header = { ...footer.tableDetails.header };
                  updatedFooter.tableDetails.header.CSSStyles.verticalAlign = 'inherit';

                  for (const cell of updatedFooter.tableDetails.header.cell) {
                    cell.CSSStyles.verticalAlign = 'inherit';
                  }
                }
              }
              break;
          }

          updatedTemplate.footer.push(updatedFooter);
        }
      }

      if (template.footerStyle) {
        updatedTemplate.footerStyle = { ...template.footerStyle };
      } else {
        updatedTemplate.footerStyle = {
          paddingTop: '0mm',
          paddingRight: '0mm',
          paddingBottom: '0mm',
          paddingLeft: '0mm',
        };
      }

      return updatedTemplate;
    },
    convertTemplateVersionTwoToThree: function (template) {
      const updatedTemplate = {
        version: 3,
        header: [],
        headerStyle: { ...template.headerStyle }, // version 2 is guaranteed to have header style
        pages: [],
        pagesStyle: {
          // version 2 doesn't support page style, will definitely be missing
          paddingTop: '0mm',
          paddingRight: '0mm',
          paddingBottom: '0mm',
          paddingLeft: '0mm',
        },
        footer: [],
        footerStyle: { ...template.footerStyle }, // version 2 is guaranteed to have footer style
      };

      if (template.header) {
        updatedTemplate.header = template.header.map((header) => ({
          ...header,
          conditionalRendering: {
            // version 3 added conditional rendering, which works the same for both string and tables
            isConditionallyRendered: false,
            dataKey: '',
            condition: 'Truthy',
            preserveSpace: false,
            renderif: '',
          },
        }));
      }

      if (template.pages) {
        updatedTemplate.pages = template.pages.map((page) => ({
          fields: page.fields.map((field) => ({
            ...field,
            conditionalRendering: {
              // version 3 added conditional rendering, which works the same for both string and tables
              isConditionallyRendered: false,
              dataKey: '',
              condition: 'Truthy',
              preserveSpace: false,
              renderif: '',
            },
          })),
        }));
      }

      if (template.footer) {
        updatedTemplate.footer = template.footer.map((footer) => ({
          ...footer,
          conditionalRendering: {
            // version 3 added conditional rendering, which works the same for both string and tables
            isConditionallyRendered: false,
            dataKey: '',
            condition: 'Truthy',
            preserveSpace: false,
            renderif: '',
          },
        }));
      }

      return updatedTemplate;
    },
    convertTemplateVersionThreeToFour: function (template) {
      const convertField = (field) => {
        switch (field.type) {
          case 'string':
            {
              const { fieldKey } = field;
              delete field.fieldKey;

              if (!fieldKey) {
                field.looping = {
                  isLooped: false,
                  fieldKey: '',
                  marginInBetween: '0mm',
                  loopon: '',
                };
              } else {
                field.looping = {
                  isLooped: true,
                  fieldKey,
                  marginInBetween: field.CSSStyles.marginBottom || '0mm',
                  loopon: `${fieldKey},${field.CSSStyles.marginBottom || '0mm'}`,
                };
              }
            }

            break;

          case 'table': {
            delete field.CSSStyles.tableLayout;

            const { fieldKey, tableKey } = field.tableDetails;
            delete field.tableDetails.fieldKey;
            delete field.tableDetails.tableKey;
            if (Array.isArray(field.tableDetails.row) && field.tableDetails.row.length === 0) {
              // should never happen, but handle just in case
              field.looping = {
                isLooped: false,
                fieldKey: '',
                marginInBetween: '0mm',
                depthLevels: [],
                loopon: '',
              };
            } else if (!fieldKey && !tableKey) {
              field.looping = {
                isLooped: false,
                fieldKey: '',
                marginInBetween: '0mm',
                depthLevels: [],
                loopon: '',
              };
            } else if (!tableKey) {
              field.looping = {
                isLooped: true,
                fieldKey: '',
                marginInBetween: '0mm',
                depthLevels: [
                  {
                    depthKey: fieldKey,
                    fromRow: '1',
                    toRow: field.tableDetails.row.length.toString(),
                  },
                ],
                loopon: `;${fieldKey},1,${field.tableDetails.row.length.toString()}`,
              };
            } else if (!fieldKey) {
              field.looping = {
                isLooped: true,
                fieldKey: '',
                marginInBetween: '0mm',
                depthLevels: [
                  {
                    depthKey: tableKey,
                    fromRow: '1',
                    toRow: '1',
                  },
                ],
                loopon: `;${tableKey},1,1`,
              };
            } else {
              field.looping = {
                isLooped: true,
                fieldKey: '',
                marginInBetween: '0mm',
                depthLevels: [
                  {
                    depthKey: fieldKey,
                    fromRow: '1',
                    toRow: field.tableDetails.row.length.toString(),
                  },
                  {
                    depthKey: tableKey,
                    fromRow: '1',
                    toRow: '1',
                  },
                ],
                loopon: `;${fieldKey},1,${field.tableDetails.row.length.toString()};${tableKey},1,1`,
              };
            }

            let columnWidths = [];
            if (Array.isArray(field.columnWidths) && field.columnWidths.length > 0) {
              columnWidths = [...field.columnWidths];
            }
            delete field.columnWidths;

            const widthsAreAllPercentages = columnWidths.every((width) => /^\d+\.?\d*%$/.test(width));
            if (widthsAreAllPercentages) {
              const parsedWidths = columnWidths.map((width) => parseFloat(width.substring(0, width.length - 1)));
              const totalWidthPercentage = parsedWidths.reduce((total, currentWidth) => {
                return total + currentWidth;
              }, 0);

              if (totalWidthPercentage !== 100) {
                const normalisedWidths = parsedWidths.map((width) => (width / totalWidthPercentage) * 100);
                if (normalisedWidths.every((width) => !isNaN(width))) {
                  // none of them should ever be NaN, but checking just in case
                  columnWidths = normalisedWidths.map((width) => `${width}%`);
                }
              }
            }

            if (field.tableDetails.header) {
              field.tableDetails.header.columnWidths = [...columnWidths];

              if (Array.isArray(field.tableDetails.header.cell) && field.tableDetails.header.cell.length > 0) {
                field.tableDetails.header.cell = field.tableDetails.header.cell.map((cell) => ({
                  colSpan: 1,
                  ...cell,
                }));
              }
            }

            if (Array.isArray(field.tableDetails.row) && field.tableDetails.row.length > 0) {
              field.tableDetails.row = field.tableDetails.row.map((row) => {
                row.columnWidths = [...columnWidths];

                if (Array.isArray(row.cell) && row.cell.length > 0) {
                  row.cell = row.cell.map((cell) => ({
                    colSpan: 1,
                    ...cell,
                  }));
                }

                return row;
              });
            }

            if (field.tableDetails.footer) {
              field.tableDetails.footer.columnWidths = [...columnWidths];

              if (Array.isArray(field.tableDetails.footer.cell) && field.tableDetails.footer.cell.length > 0) {
                field.tableDetails.footer.cell = field.tableDetails.footer.cell.map((cell) => ({
                  colSpan: 1,
                  ...cell,
                }));
              }
            }
            break;
          }
        }
      };

      const updatedTemplate = {
        version: 4,
        header: [],
        headerStyle: { ...template.headerStyle }, // header style is unchanged from version 3 to version 4
        pages: [],
        pagesStyle: { ...template.pagesStyle }, // pages style is unchanged from version 3 to version 4
        footer: [],
        footerStyle: { ...template.footerStyle }, // footer style is unchanged from version 3 to version 4
      };

      if (Array.isArray(template.header) && template.header.length > 0) {
        updatedTemplate.header = template.header.map((field) => {
          convertField(field);

          field.conditionalRendering.operator = 'AND';
          field.conditionalRendering.onPages = 'All Pages';
          if (field.conditionalRendering.isConditionallyRendered) {
            field.conditionalRendering.renderif += ',AND,All Pages';
          }

          return field;
        });
      }

      if (Array.isArray(template.pages) && template.pages.length > 0) {
        updatedTemplate.pages = template.pages.map((page) => {
          if (Array.isArray(page.fields) && page.fields.length > 0) {
            page.fields = page.fields.map((field) => {
              convertField(field);
              return field;
            });
          }
          return page;
        });
      }

      if (Array.isArray(template.footer) && template.footer.length > 0) {
        updatedTemplate.footer = template.footer.map((field) => {
          convertField(field);

          field.conditionalRendering.operator = 'AND';
          field.conditionalRendering.onPages = 'All Pages';
          if (field.conditionalRendering.isConditionallyRendered) {
            field.conditionalRendering.renderif += ',AND,All Pages';
          }

          return field;
        });
      }

      return updatedTemplate;
    },
    convertTemplateVersionFourToFive: function (template) {
      const updatedTemplate = {
        version: 5,
        paperSize: 'A4', // version 5 added paper size to cater for different paper size, combine with landscape
        footerPreserveSpace: false, //version 5 added footerPreserveSpace to cater for report that need footer to occupy space
        header: template.header, // header is unchanged from version 4 to version 5
        headerStyle: { ...template.headerStyle }, // header style is unchanged from version 4 to version 5
        pages: template.pages, // pages is unchanged from version 4 to version 5
        pagesStyle: { ...template.pagesStyle }, // pages style is unchanged from version 4 to version 5
        footer: template.footer, // footer is unchanged from version 4 to version 5
        footerStyle: { ...template.footerStyle }, // footer style is unchanged from version 4 to version 5
      };

      return updatedTemplate;
    },
    // Handles when the load report button is clicked, only used in designer mode
    loadReportClicked: async function () {
      // Getting the latest report listing
      await this.getReportListing();

      // Show the load popup
      this.showLoadPopup = true;
    },
    // Handles when the user clicks on the report name to load the details of the report, only used in designer mode
    loadReportTemplate: async function (reportName, folderName) {
      // hide the load popup
      this.showLoadPopup = false;

      // Get the contents of the report
      this.setLoadingOverlay('Getting Report Contents');
      const reportDetails = await this.getReportTemplate(reportName, folderName);
      this.setLoadingOverlay();

      if (!reportDetails) {
        // if report could not be found
        this.$showAlert('error', 'Report Not Found', 'The report could not be found', [{ text: 'OK' }]);
        return;
      }

      this.clearTemplateViewer();
      await this.populateTemplateViewer(reportDetails);
    },
    // Handles when the delete report button is clicked
    deleteReportClicked: async function (reportName, folderName) {
      // hide the load popup
      this.showLoadPopup = false;

      // Show a confirmation popup whether user really wants to delete the report
      const deleteReport = await this.$showAlert(
        'warning',
        'Delete Report',
        `Are you sure you want to delete report ${reportName} from ${folderName}?`,
        [
          { text: 'Delete', color: 'red', textColor: 'white' },
          { text: 'Cancel', color: 'lightgray', textColor: 'black' },
        ],
      );

      if (deleteReport === 'Cancel') {
        // if the user clicks on cancel
        return; // don't do anything else
      }

      // Deleting the report
      this.setLoadingOverlay('Deleting Report');
      await this.$apiCallV2(`/DesignerV3/DeleteReport?reportName=${reportName}&folderName=${folderName}`);
      this.setLoadingOverlay();

      this.clearTemplateViewer();
    },
    // Function to loading the report listing
    getReportListing: async function () {
      // Get the report list
      this.setLoadingOverlay('Getting Reports');
      let APIURL = '/DesignerV3/LoadReportList';
      if (this.companyReportFolder) {
        // ==================== have the controller get the SettingsSystem value itself, instead of relying on vue to pass it back which is potentially dangerous
        APIURL += `?companyFolderName=${this.companyReportFolder}`;
      }
      this.reportListing = await this.$apiCallV2(APIURL);
      this.setLoadingOverlay();
    },

    // Getting the page ratio based on the size in px and mm
    getPageRatio: function (rect) {
      // Get the sizes in px
      const heightPx = rect.height;
      const widthPx = rect.width;

      // ============== FIX LATER
      // The actual page size in mm (Fixed to a4 now, need to changed based on the page size that the user select)
      // const heightMm = 297;
      // const widthMm = 210;

      const heightMm = this.selectedPaper['height'];
      const widthMm = this.selectedPaper['width'];

      // Get the height and width ratio
      const heightRatio = heightPx / heightMm; // number of pixels per millimetre
      const widthRatio = widthPx / widthMm; // these two numbers should be approximately the same

      // Get the average ratio
      const avgRatio = (heightRatio + widthRatio) / 2; // so that you only have to keep track of one number instead of two, since both numbers will be approximately the same anyways

      return avgRatio;
    },
    // Converting value from px to mm based on ratio from the page index
    pxToMm: function (px, pageIndex) {
      // Get the ratio based on the pageIndex
      const ratio = this.pagesRatio[pageIndex];

      // Return the size in px in mm, closest to 2 decimal point
      return Math.round((px / ratio) * 100) / 100;
    },

    // Clearing all the fields that are empty
    clearEmpty: function () {
      // ============ this will give errors, fix in the future
      //#region Clearing header string fields that are empty
      // Getting all the empty strings within the header
      const headerStringEmpty = this.header.filter((h) => h['type'] == 'string' && !h['value']);
      // Loop through all the empty strings if there is any
      headerStringEmpty.forEach((he) => {
        // Get the index of the empty header object if there is any
        const index = this.header.findIndex((h) => JSON.stringify(h) == JSON.stringify(he));
        if (index >= 0) {
          // If the index is more than or equals to 0
          // Delete the object from the header
          this.header.splice(index, 1);
        }
      });
      //#endregion

      //#region Clearing header table that are empty
      // Get all the tables within the header
      const headerTables = this.header.filter((h) => h['type'] == 'table');
      // Getting the tables that are emtpy
      const headerTableEmpty = headerTables.filter((ht) => {
        // Getting all the rows for the tableDetails
        const allRows = ht['tableDetails']['row'];
        // Getting all the rows that are emtpy
        const emptyRow = allRows.filter((r) => {
          // Getting all the columns in the row
          const allColumns = r['cell'];
          // Getting all the empty columns for the row
          const columnsEmpty = allColumns.filter((c) => !c['value']);
          // Return true if the columnsEmpty length is equal to all columns length
          return allColumns.length == columnsEmpty.length;
        });

        if (emptyRow.length == allRows.length) {
          // If the table is empty
          // Return true to indicate that the table is empty
          return true;
        }
        // If the table is not empty
        // Looping through all the empty row
        emptyRow.forEach((er) => {
          // Find the index of the empty row item against all the rows
          const index = allRows.findIndex((ar) => JSON.stringify(ar) == JSON.stringify(er));
          if (index >= 0) {
            // If the index is more than or equals to 0
            // Remove the item in the array based on the index
            allRows.splice(index, 1);
          }
        });
        // Return false for the table is empty
        return false;
      });

      // Looping through all the tables that are empty
      headerTableEmpty.forEach((et) => {
        // Get the empty table index
        const index = this.header.findIndex((ht) => JSON.stringify(ht) == JSON.stringify(et));
        if (index >= 0) {
          // If index is more than or equals to 0
          // Remove that table
          this.header.splice(index, 1);
        }
      });
      //#endregion

      //#region Clearing content string fields that are empty
      // Getting all the empty strings within the content
      const contentStringEmpty = this.pages[0]['fields'].filter((h) => h['type'] == 'string' && !h['value']);
      // Loop through all the empty strings if there is any
      contentStringEmpty.forEach((he) => {
        // Get the index of the empty content object if there is any
        const index = this.pages[0]['fields'].findIndex((h) => JSON.stringify(h) == JSON.stringify(he));
        if (index >= 0) {
          // If the index is more than or equals to 0
          // Delete the object from the content
          this.pages[0]['fields'].splice(index, 1);
        }
      });
      //#endregion

      //#region Clearing content table that are empty
      // Get all the tables within the content
      const contentTables = this.pages[0]['fields'].filter((h) => h['type'] == 'table');
      // Getting the tables that are emtpy
      const contentTableEmpty = contentTables.filter((ht) => {
        // Getting all the rows for the tableDetails
        const allRows = ht['tableDetails']['row'];
        // Getting all the rows that are emtpy
        const emptyRow = allRows.filter((r) => {
          // Getting all the columns in the row
          const allColumns = r['cell'];
          // Getting all the empty columns for the row
          const columnsEmpty = allColumns.filter((c) => !c['value']);
          // Return true if the columnsEmpty length is equal to all columns length
          return allColumns.length == columnsEmpty.length;
        });

        if (emptyRow.length == allRows.length) {
          // If the table is empty
          // Return true to indicate that the table is empty
          return true;
        }

        // If the table is not empty
        // Looping through all the empty row
        emptyRow.forEach((er) => {
          // Find the index of the empty row item against all the rows
          const index = allRows.findIndex((ar) => JSON.stringify(ar) == JSON.stringify(er));
          if (index >= 0) {
            // If the index is more than or equals to 0
            // Remove the item in the array based on the index
            allRows.splice(index, 1);
          }
        });
        // Return false for the table is empty
        return false;
      });

      // Looping through all the tables that are empty
      contentTableEmpty.forEach((et) => {
        // Get the empty table index
        const index = this.pages[0]['fields'].findIndex((ht) => JSON.stringify(ht) == JSON.stringify(et));
        if (index >= 0) {
          // If index is more than or equals to 0
          // Remove that table
          this.pages[0]['fields'].splice(index, 1);
        }
      });
      //#endregion

      //#region Clearing footer string fields that are empty
      // Getting all the empty strings within the footer
      const footerStringEmpty = this.footer.filter((h) => h['type'] == 'string' && !h['value']);
      // Loop through all the empty strings if there is any
      footerStringEmpty.forEach((he) => {
        // Get the index of the empty footer object if there is any
        const index = this.footer.findIndex((h) => JSON.stringify(h) == JSON.stringify(he));
        if (index >= 0) {
          // If the index is more than or equals to 0
          // Delete the object from the footer
          this.footer.splice(index, 1);
        }
      });
      //#endregion

      //#region Clearing footer table that are empty
      // Get all the tables within the footer
      const footerTables = this.footer.filter((h) => h['type'] == 'table');
      // Getting the tables that are emtpy
      const footerTableEmpty = footerTables.filter((ht) => {
        // Getting all the rows for the tableDetails
        const allRows = ht['tableDetails']['row'];
        // Getting all the rows that are emtpy
        const emptyRow = allRows.filter((r) => {
          // Getting all the columns in the row
          const allColumns = r['cell'];
          // Getting all the empty columns for the row
          const columnsEmpty = allColumns.filter((c) => !c['value']);
          // Return true if the columnsEmpty length is equal to all columns length
          return allColumns.length == columnsEmpty.length;
        });

        if (emptyRow.length == allRows.length) {
          // If the table is empty
          // Return true to indicate that the table is empty
          return true;
        }

        // If the table is not empty
        // Looping through all the empty row
        emptyRow.forEach((er) => {
          // Find the index of the empty row item against all the rows
          const index = allRows.findIndex((ar) => JSON.stringify(ar) == JSON.stringify(er));
          if (index >= 0) {
            // If the index is more than or equals to 0
            // Remove the item in the array based on the index
            allRows.splice(index, 1);
          }
        });
        // Return false for the table is empty
        return false;
      });

      // Looping through all the tables that are empty
      footerTableEmpty.forEach((et) => {
        // Get the empty table index
        const index = this.footer.findIndex((ht) => JSON.stringify(ht) == JSON.stringify(et));
        if (index >= 0) {
          // If index is more than or equals to 0
          // Remove that table
          this.footer.splice(index, 1);
        }
      });
      //#endregion
    },

    //#region Height Styling
    // the fit height checkbox is only shown when a string field is selected, or a table field is selected and the selected styling section is "Table"
    fitHeightCheckBoxClicked: function (isChecked) {
      // ============== simplify this later
      const { reportSection, fieldType } = this.selectedFieldID;
      switch (reportSection) {
        case 'header':
          switch (fieldType) {
            case 'string':
              if (isChecked) {
                this.selectedField.CSSStyles.height = 'fit-content';
              } else {
                this.selectedField.CSSStyles.height = this.selectedField.styleInputValues.heightValue;
              }
              break;

            case 'tablehead':
              if (isChecked) {
                this.selectedField.CSSStyles.height = 'fit-content';
              } else {
                this.selectedField.CSSStyles.height = this.selectedField.styleInputValues.heightValue;
              }
              break;

            case 'tablebody':
              if (isChecked) {
                this.selectedField.CSSStyles.height = 'fit-content';
              } else {
                this.selectedField.CSSStyles.height = this.selectedField.styleInputValues.heightValue;
              }
              break;

            case 'tablefoot':
              if (isChecked) {
                this.selectedField.CSSStyles.height = 'fit-content';
              } else {
                this.selectedField.CSSStyles.height = this.selectedField.styleInputValues.heightValue;
              }
              break;
          }
          break;

        case 'content':
          switch (fieldType) {
            case 'string':
              if (isChecked) {
                this.selectedField.CSSStyles.height = 'fit-content';
              } else {
                this.selectedField.CSSStyles.height = this.selectedField.styleInputValues.heightValue;
              }
              break;

            case 'tablehead':
              if (isChecked) {
                this.selectedField.CSSStyles.height = 'fit-content';
              } else {
                this.selectedField.CSSStyles.height = this.selectedField.styleInputValues.heightValue;
              }
              break;

            case 'tablebody':
              if (isChecked) {
                this.selectedField.CSSStyles.height = 'fit-content';
              } else {
                this.selectedField.CSSStyles.height = this.selectedField.styleInputValues.heightValue;
              }
              break;

            case 'tablefoot':
              if (isChecked) {
                this.selectedField.CSSStyles.height = 'fit-content';
              } else {
                this.selectedField.CSSStyles.height = this.selectedField.styleInputValues.heightValue;
              }
              break;
          }
          break;

        case 'footer':
          switch (fieldType) {
            case 'string':
              if (isChecked) {
                this.selectedField.CSSStyles.height = 'fit-content';
              } else {
                this.selectedField.CSSStyles.height = this.selectedField.styleInputValues.heightValue;
              }
              break;

            case 'tablehead':
              if (isChecked) {
                this.selectedField.CSSStyles.height = 'fit-content';
              } else {
                this.selectedField.CSSStyles.height = this.selectedField.styleInputValues.heightValue;
              }
              break;

            case 'tablebody':
              if (isChecked) {
                this.selectedField.CSSStyles.height = 'fit-content';
              } else {
                this.selectedField.CSSStyles.height = this.selectedField.styleInputValues.heightValue;
              }
              break;

            case 'tablefoot':
              if (isChecked) {
                this.selectedField.CSSStyles.height = 'fit-content';
              } else {
                this.selectedField.CSSStyles.height = this.selectedField.styleInputValues.heightValue;
              }
              break;
          }
          break;
      }
    },
    // the height input is only shown when a string field is selected, or a table field is selected and the selected styling section is "Table"
    heightValueInputChanged: function (newHeight, _oldHeight) {
      const { reportSection, fieldType } = this.selectedFieldID;
      // ============== simplify this later
      switch (reportSection) {
        case 'header':
          switch (fieldType) {
            case 'string':
              if (!this.selectedField.styleInputValues.fitHeight) {
                this.selectedField.CSSStyles.height = newHeight;
              }
              break;

            case 'tablehead':
              if (!this.selectedField.styleInputValues.fitHeight) {
                this.selectedField.CSSStyles.height = newHeight;
              }
              break;

            case 'tablebody':
              if (!this.selectedField.styleInputValues.fitHeight) {
                this.selectedField.CSSStyles.height = newHeight;
              }
              break;

            case 'tablefoot':
              if (!this.selectedField.styleInputValues.fitHeight) {
                this.selectedField.CSSStyles.height = newHeight;
              }
              break;
          }
          break;

        case 'content':
          switch (fieldType) {
            case 'string':
              if (!this.selectedField.styleInputValues.fitHeight) {
                this.selectedField.CSSStyles.height = newHeight;
              }
              break;

            case 'tablehead':
              if (!this.selectedField.styleInputValues.fitHeight) {
                this.selectedField.CSSStyles.height = newHeight;
              }
              break;

            case 'tablebody':
              if (!this.selectedField.styleInputValues.fitHeight) {
                this.selectedField.CSSStyles.height = newHeight;
              }
              break;

            case 'tablefoot':
              if (!this.selectedField.styleInputValues.fitHeight) {
                this.selectedField.CSSStyles.height = newHeight;
              }
              break;
          }
          break;

        case 'footer':
          switch (fieldType) {
            case 'string':
              if (!this.selectedField.styleInputValues.fitHeight) {
                this.selectedField.CSSStyles.height = newHeight;
              }
              break;

            case 'tablehead':
              if (!this.selectedField.styleInputValues.fitHeight) {
                this.selectedField.CSSStyles.height = newHeight;
              }
              break;

            case 'tablebody':
              if (!this.selectedField.styleInputValues.fitHeight) {
                this.selectedField.CSSStyles.height = newHeight;
              }
              break;

            case 'tablefoot':
              if (!this.selectedField.styleInputValues.fitHeight) {
                this.selectedField.CSSStyles.height = newHeight;
              }
              break;
          }
          break;
      }
    },
    //#endregion

    //#region Border Styling
    // When the checkbox for no border is clicked
    noBorderCheckBoxClicked: function (isChecked) {
      const { reportSection, fieldType } = this.selectedFieldID;
      // ============== simplify this later
      switch (reportSection) {
        case 'header':
          switch (fieldType) {
            case 'string':
              if (isChecked) {
                this.selectedField.CSSStyles.borderTopWidth = '0mm';
                this.selectedField.CSSStyles.borderRightWidth = '0mm';
                this.selectedField.CSSStyles.borderBottomWidth = '0mm';
                this.selectedField.CSSStyles.borderLeftWidth = '0mm';
              } else {
                this.selectedField.CSSStyles.borderTopWidth = this.selectedField.styleInputValues.borderTopValue;
                this.selectedField.CSSStyles.borderRightWidth = this.selectedField.styleInputValues.borderRightValue;
                this.selectedField.CSSStyles.borderBottomWidth = this.selectedField.styleInputValues.borderBottomValue;
                this.selectedField.CSSStyles.borderLeftWidth = this.selectedField.styleInputValues.borderLeftValue;
              }
              break;

            case 'tablehead':
              if (isChecked) {
                this.selectedField.CSSStyles.borderTopWidth = '0mm';
                this.selectedField.CSSStyles.borderRightWidth = '0mm';
                this.selectedField.CSSStyles.borderBottomWidth = '0mm';
                this.selectedField.CSSStyles.borderLeftWidth = '0mm';
              } else {
                this.selectedField.CSSStyles.borderTopWidth = this.selectedField.styleInputValues.borderTopValue;
                this.selectedField.CSSStyles.borderRightWidth = this.selectedField.styleInputValues.borderRightValue;
                this.selectedField.CSSStyles.borderBottomWidth = this.selectedField.styleInputValues.borderBottomValue;
                this.selectedField.CSSStyles.borderLeftWidth = this.selectedField.styleInputValues.borderLeftValue;
              }
              break;

            case 'tablebody':
              if (isChecked) {
                this.selectedField.CSSStyles.borderTopWidth = '0mm';
                this.selectedField.CSSStyles.borderRightWidth = '0mm';
                this.selectedField.CSSStyles.borderBottomWidth = '0mm';
                this.selectedField.CSSStyles.borderLeftWidth = '0mm';
              } else {
                this.selectedField.CSSStyles.borderTopWidth = this.selectedField.styleInputValues.borderTopValue;
                this.selectedField.CSSStyles.borderRightWidth = this.selectedField.styleInputValues.borderRightValue;
                this.selectedField.CSSStyles.borderBottomWidth = this.selectedField.styleInputValues.borderBottomValue;
                this.selectedField.CSSStyles.borderLeftWidth = this.selectedField.styleInputValues.borderLeftValue;
              }
              break;

            case 'tablefoot':
              if (isChecked) {
                this.selectedField.CSSStyles.borderTopWidth = '0mm';
                this.selectedField.CSSStyles.borderRightWidth = '0mm';
                this.selectedField.CSSStyles.borderBottomWidth = '0mm';
                this.selectedField.CSSStyles.borderLeftWidth = '0mm';
              } else {
                this.selectedField.CSSStyles.borderTopWidth = this.selectedField.styleInputValues.borderTopValue;
                this.selectedField.CSSStyles.borderRightWidth = this.selectedField.styleInputValues.borderRightValue;
                this.selectedField.CSSStyles.borderBottomWidth = this.selectedField.styleInputValues.borderBottomValue;
                this.selectedField.CSSStyles.borderLeftWidth = this.selectedField.styleInputValues.borderLeftValue;
              }
              break;
          }
          break;
        case 'content':
          switch (fieldType) {
            case 'string':
              if (isChecked) {
                this.selectedField.CSSStyles.borderTopWidth = '0mm';
                this.selectedField.CSSStyles.borderRightWidth = '0mm';
                this.selectedField.CSSStyles.borderBottomWidth = '0mm';
                this.selectedField.CSSStyles.borderLeftWidth = '0mm';
              } else {
                this.selectedField.CSSStyles.borderTopWidth = this.selectedField.styleInputValues.borderTopValue;
                this.selectedField.CSSStyles.borderRightWidth = this.selectedField.styleInputValues.borderRightValue;
                this.selectedField.CSSStyles.borderBottomWidth = this.selectedField.styleInputValues.borderBottomValue;
                this.selectedField.CSSStyles.borderLeftWidth = this.selectedField.styleInputValues.borderLeftValue;
              }
              break;

            case 'tablehead':
              if (isChecked) {
                this.selectedField.CSSStyles.borderTopWidth = '0mm';
                this.selectedField.CSSStyles.borderRightWidth = '0mm';
                this.selectedField.CSSStyles.borderBottomWidth = '0mm';
                this.selectedField.CSSStyles.borderLeftWidth = '0mm';
              } else {
                this.selectedField.CSSStyles.borderTopWidth = this.selectedField.styleInputValues.borderTopValue;
                this.selectedField.CSSStyles.borderRightWidth = this.selectedField.styleInputValues.borderRightValue;
                this.selectedField.CSSStyles.borderBottomWidth = this.selectedField.styleInputValues.borderBottomValue;
                this.selectedField.CSSStyles.borderLeftWidth = this.selectedField.styleInputValues.borderLeftValue;
              }
              break;

            case 'tablebody':
              if (isChecked) {
                this.selectedField.CSSStyles.borderTopWidth = '0mm';
                this.selectedField.CSSStyles.borderRightWidth = '0mm';
                this.selectedField.CSSStyles.borderBottomWidth = '0mm';
                this.selectedField.CSSStyles.borderLeftWidth = '0mm';
              } else {
                this.selectedField.CSSStyles.borderTopWidth = this.selectedField.styleInputValues.borderTopValue;
                this.selectedField.CSSStyles.borderRightWidth = this.selectedField.styleInputValues.borderRightValue;
                this.selectedField.CSSStyles.borderBottomWidth = this.selectedField.styleInputValues.borderBottomValue;
                this.selectedField.CSSStyles.borderLeftWidth = this.selectedField.styleInputValues.borderLeftValue;
              }
              break;

            case 'tablefoot':
              if (isChecked) {
                this.selectedField.CSSStyles.borderTopWidth = '0mm';
                this.selectedField.CSSStyles.borderRightWidth = '0mm';
                this.selectedField.CSSStyles.borderBottomWidth = '0mm';
                this.selectedField.CSSStyles.borderLeftWidth = '0mm';
              } else {
                this.selectedField.CSSStyles.borderTopWidth = this.selectedField.styleInputValues.borderTopValue;
                this.selectedField.CSSStyles.borderRightWidth = this.selectedField.styleInputValues.borderRightValue;
                this.selectedField.CSSStyles.borderBottomWidth = this.selectedField.styleInputValues.borderBottomValue;
                this.selectedField.CSSStyles.borderLeftWidth = this.selectedField.styleInputValues.borderLeftValue;
              }
              break;
          }
          break;
        case 'footer':
          switch (fieldType) {
            case 'string':
              if (isChecked) {
                this.selectedField.CSSStyles.borderTopWidth = '0mm';
                this.selectedField.CSSStyles.borderRightWidth = '0mm';
                this.selectedField.CSSStyles.borderBottomWidth = '0mm';
                this.selectedField.CSSStyles.borderLeftWidth = '0mm';
              } else {
                this.selectedField.CSSStyles.borderTopWidth = this.selectedField.styleInputValues.borderTopValue;
                this.selectedField.CSSStyles.borderRightWidth = this.selectedField.styleInputValues.borderRightValue;
                this.selectedField.CSSStyles.borderBottomWidth = this.selectedField.styleInputValues.borderBottomValue;
                this.selectedField.CSSStyles.borderLeftWidth = this.selectedField.styleInputValues.borderLeftValue;
              }
              break;

            case 'tablehead':
              if (isChecked) {
                this.selectedField.CSSStyles.borderTopWidth = '0mm';
                this.selectedField.CSSStyles.borderRightWidth = '0mm';
                this.selectedField.CSSStyles.borderBottomWidth = '0mm';
                this.selectedField.CSSStyles.borderLeftWidth = '0mm';
              } else {
                this.selectedField.CSSStyles.borderTopWidth = this.selectedField.styleInputValues.borderTopValue;
                this.selectedField.CSSStyles.borderRightWidth = this.selectedField.styleInputValues.borderRightValue;
                this.selectedField.CSSStyles.borderBottomWidth = this.selectedField.styleInputValues.borderBottomValue;
                this.selectedField.CSSStyles.borderLeftWidth = this.selectedField.styleInputValues.borderLeftValue;
              }
              break;

            case 'tablebody':
              if (isChecked) {
                this.selectedField.CSSStyles.borderTopWidth = '0mm';
                this.selectedField.CSSStyles.borderRightWidth = '0mm';
                this.selectedField.CSSStyles.borderBottomWidth = '0mm';
                this.selectedField.CSSStyles.borderLeftWidth = '0mm';
              } else {
                this.selectedField.CSSStyles.borderTopWidth = this.selectedField.styleInputValues.borderTopValue;
                this.selectedField.CSSStyles.borderRightWidth = this.selectedField.styleInputValues.borderRightValue;
                this.selectedField.CSSStyles.borderBottomWidth = this.selectedField.styleInputValues.borderBottomValue;
                this.selectedField.CSSStyles.borderLeftWidth = this.selectedField.styleInputValues.borderLeftValue;
              }
              break;

            case 'tablefoot':
              if (isChecked) {
                this.selectedField.CSSStyles.borderTopWidth = '0mm';
                this.selectedField.CSSStyles.borderRightWidth = '0mm';
                this.selectedField.CSSStyles.borderBottomWidth = '0mm';
                this.selectedField.CSSStyles.borderLeftWidth = '0mm';
              } else {
                this.selectedField.CSSStyles.borderTopWidth = this.selectedField.styleInputValues.borderTopValue;
                this.selectedField.CSSStyles.borderRightWidth = this.selectedField.styleInputValues.borderRightValue;
                this.selectedField.CSSStyles.borderBottomWidth = this.selectedField.styleInputValues.borderBottomValue;
                this.selectedField.CSSStyles.borderLeftWidth = this.selectedField.styleInputValues.borderLeftValue;
              }
              break;
          }
          break;
      }
    },
    borderValueInputChanged: function (borderDirection, newBorderWidth) {
      const { reportSection, fieldType } = this.selectedFieldID;
      // ============== simplify this later
      switch (reportSection) {
        case 'header':
          switch (fieldType) {
            case 'string':
              if (!this.selectedField.styleInputValues.noBorder) {
                switch (borderDirection) {
                  case 'top':
                    this.selectedField.CSSStyles.borderTopWidth = newBorderWidth;
                    break;

                  case 'right':
                    this.selectedField.CSSStyles.borderRightWidth = newBorderWidth;
                    break;

                  case 'bottom':
                    this.selectedField.CSSStyles.borderBottomWidth = newBorderWidth;
                    break;

                  case 'left':
                    this.selectedField.CSSStyles.borderLeftWidth = newBorderWidth;
                    break;
                }
              }
              break;

            case 'tablehead':
              if (!this.selectedField.styleInputValues.noBorder) {
                switch (borderDirection) {
                  case 'top':
                    this.selectedField.CSSStyles.borderTopWidth = newBorderWidth;
                    break;

                  case 'right':
                    this.selectedField.CSSStyles.borderRightWidth = newBorderWidth;
                    break;

                  case 'bottom':
                    this.selectedField.CSSStyles.borderBottomWidth = newBorderWidth;
                    break;

                  case 'left':
                    this.selectedField.CSSStyles.borderLeftWidth = newBorderWidth;
                    break;
                }
              }
              break;

            case 'tablebody':
              if (!this.selectedField.styleInputValues.noBorder) {
                switch (borderDirection) {
                  case 'top':
                    this.selectedField.CSSStyles.borderTopWidth = newBorderWidth;
                    break;

                  case 'right':
                    this.selectedField.CSSStyles.borderRightWidth = newBorderWidth;
                    break;

                  case 'bottom':
                    this.selectedField.CSSStyles.borderBottomWidth = newBorderWidth;
                    break;

                  case 'left':
                    this.selectedField.CSSStyles.borderLeftWidth = newBorderWidth;
                    break;
                }
              }
              break;

            case 'tablefoot':
              if (!this.selectedField.styleInputValues.noBorder) {
                switch (borderDirection) {
                  case 'top':
                    this.selectedField.CSSStyles.borderTopWidth = newBorderWidth;
                    break;

                  case 'right':
                    this.selectedField.CSSStyles.borderRightWidth = newBorderWidth;
                    break;

                  case 'bottom':
                    this.selectedField.CSSStyles.borderBottomWidth = newBorderWidth;
                    break;

                  case 'left':
                    this.selectedField.CSSStyles.borderLeftWidth = newBorderWidth;
                    break;
                }
              }
              break;
          }
          break;

        case 'content':
          switch (fieldType) {
            case 'string':
              if (!this.selectedField.styleInputValues.noBorder) {
                switch (borderDirection) {
                  case 'top':
                    this.selectedField.CSSStyles.borderTopWidth = newBorderWidth;
                    break;

                  case 'right':
                    this.selectedField.CSSStyles.borderRightWidth = newBorderWidth;
                    break;

                  case 'bottom':
                    this.selectedField.CSSStyles.borderBottomWidth = newBorderWidth;
                    break;

                  case 'left':
                    this.selectedField.CSSStyles.borderLeftWidth = newBorderWidth;
                    break;
                }
              }
              break;

            case 'tablehead':
              if (!this.selectedField.styleInputValues.noBorder) {
                switch (borderDirection) {
                  case 'top':
                    this.selectedField.CSSStyles.borderTopWidth = newBorderWidth;
                    break;

                  case 'right':
                    this.selectedField.CSSStyles.borderRightWidth = newBorderWidth;
                    break;

                  case 'bottom':
                    this.selectedField.CSSStyles.borderBottomWidth = newBorderWidth;
                    break;

                  case 'left':
                    this.selectedField.CSSStyles.borderLeftWidth = newBorderWidth;
                    break;
                }
              }
              break;

            case 'tablebody':
              if (!this.selectedField.styleInputValues.noBorder) {
                switch (borderDirection) {
                  case 'top':
                    this.selectedField.CSSStyles.borderTopWidth = newBorderWidth;
                    break;

                  case 'right':
                    this.selectedField.CSSStyles.borderRightWidth = newBorderWidth;
                    break;

                  case 'bottom':
                    this.selectedField.CSSStyles.borderBottomWidth = newBorderWidth;
                    break;

                  case 'left':
                    this.selectedField.CSSStyles.borderLeftWidth = newBorderWidth;
                    break;
                }
              }
              break;

            case 'tablefoot':
              if (!this.selectedField.styleInputValues.noBorder) {
                switch (borderDirection) {
                  case 'top':
                    this.selectedField.CSSStyles.borderTopWidth = newBorderWidth;
                    break;

                  case 'right':
                    this.selectedField.CSSStyles.borderRightWidth = newBorderWidth;
                    break;

                  case 'bottom':
                    this.selectedField.CSSStyles.borderBottomWidth = newBorderWidth;
                    break;

                  case 'left':
                    this.selectedField.CSSStyles.borderLeftWidth = newBorderWidth;
                    break;
                }
              }
              break;
          }
          break;

        case 'footer':
          switch (fieldType) {
            case 'string':
              if (!this.selectedField.styleInputValues.noBorder) {
                switch (borderDirection) {
                  case 'top':
                    this.selectedField.CSSStyles.borderTopWidth = newBorderWidth;
                    break;

                  case 'right':
                    this.selectedField.CSSStyles.borderRightWidth = newBorderWidth;
                    break;

                  case 'bottom':
                    this.selectedField.CSSStyles.borderBottomWidth = newBorderWidth;
                    break;

                  case 'left':
                    this.selectedField.CSSStyles.borderLeftWidth = newBorderWidth;
                    break;
                }
              }
              break;

            case 'tablehead':
              if (!this.selectedField.styleInputValues.noBorder) {
                switch (borderDirection) {
                  case 'top':
                    this.selectedField.CSSStyles.borderTopWidth = newBorderWidth;
                    break;

                  case 'right':
                    this.selectedField.CSSStyles.borderRightWidth = newBorderWidth;
                    break;

                  case 'bottom':
                    this.selectedField.CSSStyles.borderBottomWidth = newBorderWidth;
                    break;

                  case 'left':
                    this.selectedField.CSSStyles.borderLeftWidth = newBorderWidth;
                    break;
                }
              }
              break;

            case 'tablebody':
              if (!this.selectedField.styleInputValues.noBorder) {
                switch (borderDirection) {
                  case 'top':
                    this.selectedField.CSSStyles.borderTopWidth = newBorderWidth;
                    break;

                  case 'right':
                    this.selectedField.CSSStyles.borderRightWidth = newBorderWidth;
                    break;

                  case 'bottom':
                    this.selectedField.CSSStyles.borderBottomWidth = newBorderWidth;
                    break;

                  case 'left':
                    this.selectedField.CSSStyles.borderLeftWidth = newBorderWidth;
                    break;
                }
              }
              break;

            case 'tablefoot':
              if (!this.selectedField.styleInputValues.noBorder) {
                switch (borderDirection) {
                  case 'top':
                    this.selectedField.CSSStyles.borderTopWidth = newBorderWidth;
                    break;

                  case 'right':
                    this.selectedField.CSSStyles.borderRightWidth = newBorderWidth;
                    break;

                  case 'bottom':
                    this.selectedField.CSSStyles.borderBottomWidth = newBorderWidth;
                    break;

                  case 'left':
                    this.selectedField.CSSStyles.borderLeftWidth = newBorderWidth;
                    break;
                }
              }
              break;
          }
          break;
      }
    },
    // When the border style changes
    borderStyleDropdownSelected: function (selectedBorderStyle) {
      if (!selectedBorderStyle) {
        return; // don't update anything, leave the dropdown as blank, leave the field with its current border style
      }

      // ============== simplify this later
      const { reportSection, fieldType } = this.selectedFieldID;
      switch (reportSection) {
        case 'header':
          this.selectedField.CSSStyles.borderStyle = selectedBorderStyle.style;
          break;
        case 'content':
          switch (fieldType) {
            case 'string':
              this.selectedField.CSSStyles.borderStyle = selectedBorderStyle.style;
              break;

            case 'tablehead':
              this.selectedField.CSSStyles.borderStyle = selectedBorderStyle.style;
              break;

            case 'tablebody':
              this.selectedField.CSSStyles.borderStyle = selectedBorderStyle.style;
              break;

            case 'tablefoot':
              this.selectedField.CSSStyles.borderStyle = selectedBorderStyle.style;
              break;
          }
          break;
        case 'footer':
          this.selectedField.CSSStyles.borderStyle = selectedBorderStyle.style;
          break;
      }
    },
    //#endregion

    reportSectionDropdownSelected: function (selectedReportSection) {
      if (!selectedReportSection) {
        return; // don't update the input value data members, leave them as is, the input fields will be disabled anyways
      }

      this.populateReportSectionStylingInputValues(selectedReportSection.name);
    },
    populateReportSectionStylingInputValues: function (selectedReportSection) {
      switch (selectedReportSection) {
        case 'Header':
          this.reportSectionStylingPaddingTopInputValue = this.headerStyle.paddingTop;
          this.reportSectionStylingPaddingRightInputValue = this.headerStyle.paddingRight;
          this.reportSectionStylingPaddingBottomInputValue = this.headerStyle.paddingBottom;
          this.reportSectionStylingPaddingLeftInputValue = this.headerStyle.paddingLeft;
          break;

        case 'Content':
          this.reportSectionStylingPaddingTopInputValue = this.pagesStyle.paddingTop;
          this.reportSectionStylingPaddingRightInputValue = this.pagesStyle.paddingRight;
          this.reportSectionStylingPaddingBottomInputValue = this.pagesStyle.paddingBottom;
          this.reportSectionStylingPaddingLeftInputValue = this.pagesStyle.paddingLeft;
          break;

        case 'Footer':
          this.reportSectionStylingPaddingTopInputValue = this.footerStyle.paddingTop;
          this.reportSectionStylingPaddingRightInputValue = this.footerStyle.paddingRight;
          this.reportSectionStylingPaddingBottomInputValue = this.footerStyle.paddingBottom;
          this.reportSectionStylingPaddingLeftInputValue = this.footerStyle.paddingLeft;
          break;
      }
    },
    reportSectionStylingPaddingInputChanged: function (paddingDirection, newPaddingInput) {
      switch (
        this.selectedReportSection.name // this shouldn't ever be null, since the inputs are disabled when this is null
      ) {
        case 'Header':
          this.headerStyle[paddingDirection] = newPaddingInput;
          break;

        case 'Content':
          this.pagesStyle[paddingDirection] = newPaddingInput;
          break;

        case 'Footer':
          this.footerStyle[paddingDirection] = newPaddingInput;
          break;
      }
    },
    //#region Methods for getting header and footer height in MM
    getElementHeightInMm: function (ref) {
      const boundingRect = this.$refs[ref].getBoundingClientRect();
      const HeightInMm = this.pxToMm(boundingRect.height, 0); // ============== change later once support for multiple pages has been properly implemented
      return !isNaN(HeightInMm) ? HeightInMm : null;
    },
    //#endregion

    //#region Observing Header Footer Size
    headerFooterResized: function (e) {
      for (const element of e) {
        // Get the bound client rect of the target
        // let contentRect = element.target.getBoundingClientRect();
        // // Convert the height from px to mm
        // let contentHeightInMM = this.pxToMm(contentRect.height, 0);
        // if (!isNaN(contentHeightInMM)) { // If the content height in mm is not NaN
        //   if (element.target.className == 'page-header') { // If the target is header
        //     // Set the height height
        //     this.headerHeight = contentHeightInMM;
        //   } else { // If the target is footer
        //     // Set the footer height
        //     this.footerHeight = contentHeightInMM;
        //   }
        // }

        if (element.target.className == 'page-header') {
          // If the target is header
          const headerHeightInMm = this.getElementHeightInMm('pageHeader');
          if (headerHeightInMm) {
            // if a header height is returned
            this.headerHeight = headerHeightInMm; // assign to headerHeight
          }
        } else {
          // If the target is footer
          const footerHeightInMm = this.getElementHeightInMm('pageFooter');
          if (footerHeightInMm) {
            // if a footer height is returned
            this.footerHeight = footerHeightInMm; // assign to footerHeight
          }
        }
      }
    },
    //#endregion

    PDFViewerEventHandler: async function (event) {
      switch (event) {
        case 'togglebg':
          this.$emit('update:showBackground', !this.showBackground);
          await this.$nextTick(); // wait for the prop to change
          if (this.viewMode) {
            this.generateReport();
          } else {
            this.changeToDesignerLayout();
            this.changeToPreviewLayout();
          }
          break;
        case 'share':
          switch (this.shareBehaviour.behaviour) {
            case 'uploadAndPopup':
              {
                const generatedLink = await this.generateShareLink(
                  this.shareBehaviour?.options?.fileName,
                  this.shareBehaviour?.options?.folder,
                );
                if (!generatedLink) {
                  this.$showAlert('error', 'Share Failed', 'Failed to generate share link\n\nPlease try again', [
                    { text: 'OK' },
                  ]);
                  break;
                }

                this.sharePopupLink = generatedLink;
                this.showSharePopup = true;
              }
              break;

            case 'uploadAndEmit':
              {
                const generatedLink = await this.generateShareLink(
                  this.shareBehaviour?.options?.fileName,
                  this.shareBehaviour?.options?.folder,
                );
                if (!generatedLink) {
                  this.$showAlert('error', 'Share Failed', 'Failed to generate share link\n\nPlease try again', [
                    { text: 'OK' },
                  ]);
                  break;
                }

                this.$emit('shareClicked', generatedLink);
              }
              break;

            case 'emitOnly':
              if (this.showBackground) {
                this.$emit('shareClicked', this.PDFBlobWithBackground, this.PDFBlobURLWithBackground);
              } else {
                this.$emit('shareClicked', this.PDFBlobWithoutBackground, this.PDFBlobURLWithoutBackground);
              }
              break;
          }
          break;
        case 'print': {
          const hiddenIFrame = document.createElement('iframe');
          hiddenIFrame.style.display = 'none';
          hiddenIFrame.src = this.showBackground ? this.PDFBlobURLWithBackground : this.PDFBlobURLWithoutBackground;
          hiddenIFrame.onload = function () {
            hiddenIFrame.contentWindow.focus();
            hiddenIFrame.contentWindow.print();
          };

          document.body.appendChild(hiddenIFrame);
          this.$emit('printClicked');
          this.$emit('afterPrint');
          break;
        }
        case 'download': {
          const anchor = document.createElement('a');
          let href;
          let downloadFileName;
          if (this.ZipFileName !== '') {
            const downloadOption = await this.$showAlert('info', 'Select Download Option', 'Download as', 
              [ { text: 'Single File' }, { text: 'Multiple Files' }, { text: 'Cancel' } ]
            );
            if (downloadOption === 'Single File') {
              href = this.showBackground ? this.PDFBlobURLWithBackground : this.PDFBlobURLWithoutBackground;
              downloadFileName = `${this.downloadName}.pdf`;
            } else if (downloadOption === 'Multiple Files') {
              href = this.ZipBlobURL;
              downloadFileName = this.ZipFileName;
            } else {
              return;
            }
          } else {
            href = this.showBackground ? this.PDFBlobURLWithBackground : this.PDFBlobURLWithoutBackground;
            downloadFileName = `${this.downloadName}.pdf`;
          }
          anchor.href = href;
          anchor.download = downloadFileName;
          anchor.click();
          break;
        }
        case 'close':
          this.$emit('closeClicked');
          break;
        case 'afterprint':
          this.$emit('afterPrint');
          break;
      }
    },

    // try to upload the PDF to dsmlib, return the link to the report if successfully uploaded, return null otherwise
    generateShareLink: async function (fileName, additionalSubfolders) {
      if (!this.showPDFViewer) {
        // if the PDF viewer is not shown
        return false; // don't do anything
      }

      if (this.showBackground && !this.PDFBlobWithBackground) {
        // if the blob with background doesn't exist
        return false; // don't do anything
      }

      if (!this.showBackground && !this.PDFBlobWithoutBackground) {
        // if the blob without background doesn't exist
        return false; // don't do anything
      }

      if (this.ZipFileName && !this.ZipBlob) {
        //if the blob for zip doesn't exist
        return false; //don't do nothing
      }

      const [folderName, reportName] = this.reportPath.split('/');
      let resolvedFileName = '';
      let documentBlob = null;
      if (this.ZipFileName) {
        //if there is zip file(more than 1 document)
        resolvedFileName = `DocumentZip_${new Date().formatString('yyyyMMddHHmmss_sss')}.zip`;
        documentBlob = this.ZipBlob;
      } else {
        // if a file name is passed in, use that, else use the report template name and current time as the file name
        resolvedFileName =
          fileName || `${reportName.replaceAll('.json', '')}_${new Date().formatString('yyyyMMddHHmmss_sss')}.pdf`;
        documentBlob = this.showBackground ? this.PDFBlobWithBackground : this.PDFBlobWithoutBackground;
      }

      // reports are saved to DesignerV3\{report template folder name}\{any additional subfolders passed in}
      let resolvedFolderPath = `DesignerV3,${folderName}`;
      if (additionalSubfolders) {
        resolvedFolderPath += `,${additionalSubfolders}`;
      }

      const formData = new FormData();
      formData.append('file', documentBlob, resolvedFileName);
      formData.append('folder', resolvedFolderPath);

      this.setLoadingOverlay('Generating Link');
      const uploadFileResponse = await this.$apiCallV2('/Upload/UploadFile', formData);
      this.setLoadingOverlay();

      return uploadFileResponse?.Files?.[0] ?? null;
    },
    sharePopupCopyClicked: async function () {
      await navigator.clipboard.writeText(this.sharePopupLink);
      this.showCopiedNotification = true;
      setTimeout(() => {
        this.showCopiedNotification = false;
      }, 2000);
    },
    sharePopupButtonClicked: function (_buttonText) {
      this.showSharePopup = false;
    },

    setLoadingOverlay: function (content) {
      if (!content) {
        this.showLoadingOverlay = false;
      } else {
        this.showLoadingOverlay = true;
        this.loadingOverlayContent = content;
      }
    },
  },
  async mounted() {
    this.setLoadingOverlay('Initialising');

    this.userDetails = await this.$apiCallV2('/Login/GetDetails');
    this.companyReportFolder = (
      await this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=6G_ReportFolder')
    )?.[0]?.SettingsValue;

    const headerFooterResizeObserver = new ResizeObserver(this.headerFooterResized);

    if (this.$refs['pageHeader']) {
      // if the header element exists
      headerFooterResizeObserver.observe(this.$refs['pageHeader']); // observe it
    }

    if (this.$refs['pageFooter']) {
      // if the footer element exists
      headerFooterResizeObserver.observe(this.$refs['pageFooter']); // observe it
    }
    this.setLoadingOverlay();

    if (!this.viewMode) {
      // in designer mode (edit mode)
      this.showReportPreview = true;
    } else {
      // in report generation mode (view mode)
      await this.generateReport();
    }
  },
  beforeDestroy() {
    this.destroyPDFViewer();
  },
  watch: {
    selectedFieldTextContent: function (val) {
      if (!this.selectedFieldID) {
        return; // e.g. when the textarea is cleared when a field is deselected, etc.
      }

      const { reportSection, fieldType } = this.selectedFieldID;
      switch (reportSection) {
        case 'header':
          switch (fieldType) {
            case 'string':
              this.selectedField.value = val;
              break;

            case 'tablehead':
              {
                // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                const { field: fieldIndex, cell: cellIndex } = this.selectedFieldID.indexes;
                this.header[fieldIndex].tableDetails.header.cell[cellIndex].value = val;
              }
              break;

            case 'tablebody':
              {
                // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                const { field: fieldIndex, row: rowIndex, cell: cellIndex } = this.selectedFieldID.indexes;
                this.header[fieldIndex].tableDetails.row[rowIndex].cell[cellIndex].value = val;
              }
              break;

            case 'tablefoot':
              {
                // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                const { field: fieldIndex, cell: cellIndex } = this.selectedFieldID.indexes;
                this.header[fieldIndex].tableDetails.footer.cell[cellIndex].value = val;
              }
              break;
          }
          break;

        case 'content':
          switch (fieldType) {
            case 'string':
              this.selectedField.value = val;
              break;

            case 'tablehead':
              {
                // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                const { page: pageIndex, field: fieldIndex, cell: cellIndex } = this.selectedFieldID.indexes;
                this.pages[pageIndex].fields[fieldIndex].tableDetails.header.cell[cellIndex].value = val;
              }
              break;

            case 'tablebody':
              {
                // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                const {
                  page: pageIndex,
                  field: fieldIndex,
                  row: rowIndex,
                  cell: cellIndex,
                } = this.selectedFieldID.indexes;
                this.pages[pageIndex].fields[fieldIndex].tableDetails.row[rowIndex].cell[cellIndex].value = val;
              }
              break;

            case 'tablefoot':
              {
                // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                const { page: pageIndex, field: fieldIndex, cell: cellIndex } = this.selectedFieldID.indexes;
                this.pages[pageIndex].fields[fieldIndex].tableDetails.footer.cell[cellIndex].value = val;
              }
              break;
          }
          break;

        case 'footer':
          switch (fieldType) {
            case 'string':
              this.selectedField.value = val;
              break;

            case 'tablehead':
              {
                // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                const { field: fieldIndex, cell: cellIndex } = this.selectedFieldID.indexes;
                this.footer[fieldIndex].tableDetails.header.cell[cellIndex].value = val;
              }
              break;

            case 'tablebody':
              {
                // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                const { field: fieldIndex, row: rowIndex, cell: cellIndex } = this.selectedFieldID.indexes;
                this.footer[fieldIndex].tableDetails.row[rowIndex].cell[cellIndex].value = val;
              }
              break;

            case 'tablefoot':
              {
                // block statement to scope the const declarations to this branch https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Statements/block
                const { field: fieldIndex, cell: cellIndex } = this.selectedFieldID.indexes;
                this.footer[fieldIndex].tableDetails.footer.cell[cellIndex].value = val;
              }
              break;
          }
          break;
      }
    },
    reportFiles: {
      handler: async function (_val) {
        if (this.viewMode) {
          this.PDFBlobWithBackground = null;
          this.PDFBlobURLWithBackground = '';
          this.PDFBlobWithoutBackground = null;
          this.PDFBlobURLWithoutBackground = '';
          this.ZipBlob = null;
          this.ZipFileName = '';
          this.ZipBlobURL = '';
          await this.generateReport();
        }
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.designer-v3 {
  position: relative;
  height: 100%;
  width: 100%;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;

  > .left-side {
    width: 400px;
    height: 100%;
    padding: 5px;
    background-color: white;
    overflow: auto;
  }

  > .right-side {
    width: calc(100% - 400px);
    height: 100%;
    margin-left: 5px;
    display: flex;
    flex-direction: column;

    > .pdf-container {
      background-color: gray;
      height: 100%;
      width: 100%;
      overflow: auto;

      > .page-container {
        background-color: white;
        margin: 0 10px;
        position: relative;
        overflow: hidden; // to prevent the entire div from moving downwards if the first field in the page has a margin set https://stackoverflow.com/a/1939980, also to prevent the div from being scrollable if the content exceeds the page size
      }

      > .page-header,
      .page-footer {
        position: relative;
        // width: 210mm; // =============== change when there are more paper types in the future
        width: var(--content-width);
        height: fit-content;
        background-color: white;
        margin: 10px;
      }

      > .page-header {
        margin-bottom: 1px;
      }

      > .page-footer {
        margin-top: 1px;
      }
    }
  }

  > .height-measurer {
    height: fit-content;
    // width: 210mm; // =============== change when there are more paper types in the future
    width: var(--content-width);
  }

  > .pdf-viewer-container {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;

    > .template-breadcrumbs {
      border-radius: 5px 5px 0 0;
      background-color: #3f3f3f;
      padding: 5px 10px;
      font-size: 12px;
      color: white;
    }
  }
}

.field-select {
  background-color: limegreen;
}

.field-selected {
  outline: 3px dashed orangered;
  outline-offset: -2px;
}

.field-container {
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  word-break: break-all; // needed to force table columns to adhere to the width set
  // after merged cells were introduced, table-layout: fixed; can't be used anymore
  // https://stackoverflow.com/questions/5889508/using-word-wrap-break-word-within-a-table
  font-family: 'Times New Roman', Times, serif;
  line-height: 1.1;
  box-sizing: border-box;
}

.field-inner-container {
  white-space: inherit;
  overflow-wrap: inherit;
  word-break: inherit;
  font-family: inherit;
  line-height: inherit;
  box-sizing: inherit;
  padding-top: inherit; // padding is not inherited by default https://developer.mozilla.org/en-US/docs/Web/CSS/padding#formal_definition
  padding-right: inherit;
  padding-bottom: inherit;
  padding-left: inherit;
  border-top-width: inherit; // border-width is not inherited by default https://developer.mozilla.org/en-US/docs/Web/CSS/border-width#formal_definition
  border-right-width: inherit;
  border-bottom-width: inherit;
  border-left-width: inherit;
  border-style: inherit; // border-style is not inherited by default https://developer.mozilla.org/en-US/docs/Web/CSS/border-style#formal_definition
  border-color: inherit; // border-color is not inherited by default https://developer.mozilla.org/en-US/docs/Web/CSS/border-color#formal_definition
  vertical-align: inherit;
}

.field-content {
  // divs and imgs inside th and td shouldn't inherit border, otherwise it may lead to double stacked borders, padding isn't needed either
  white-space: inherit;
  overflow-wrap: inherit;
  word-break: inherit;
  font-family: inherit;
  line-height: inherit;
  box-sizing: inherit;
}

.value-textarea {
  resize: none;
  width: 100%;
  height: 100px;

  &:focus {
    outline: none;
  }
}

.field-details-row {
  display: flex;
  align-items: center;

  > :not(:first-child) {
    margin-left: 5px;
  }
}

.text-format-icons {
  border: 1px solid gray;
  padding: 5px;
  border-radius: 5px;
  height: 30px;
  cursor: pointer;
}

.text-format-selected {
  background-color: gray;
  filter: invert(1);
}

.delete-button {
  background-color: indianred;
  color: white;
}

.report-listing-body {
  height: 100%;

  > .listing-content {
    max-height: 400px;
    overflow: auto;

    > .report-listing-container {
      display: flex;
      border: 1px solid gray;
      padding: 5px;
      text-align: left;
      align-items: center;
      margin-top: 5px;

      > .report-filename,
      > img {
        cursor: pointer;
      }

      > .report-filename {
        width: 100%;
      }

      > img {
        height: 20px;
        margin-left: 5px;
      }
    }
  }
}

.save-popup {
  overflow: visible;

  ::v-deep {
    // deep needed to target the content class inside the PopupV2 component
    .content {
      // NOT the content class for slot, but content class inside PopupV2 itself
      overflow: visible;
    }
  }

  .folder-dropdown {
    overflow: visible;
  }
}

.share-popup {
  .popup-content {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 0 8px;

    > .anchor-container {
      width: 100%;
      display: flex;
      flex-direction: row;
      justify-content: space-between;

      a:hover {
        text-decoration: none;
      }

      .anchor-content {
        margin-bottom: 20px;
        height: 30px;
        width: 115px;
        border-radius: 5px;
        padding: 5px 10px;
        color: white;

        > img {
          height: 20px;
          width: 20px;
        }
      }

      .email {
        background-color: #505050;
      }

      .whatsapp {
        background-color: #25d366;
      }

      .telegram {
        background-color: #0088cc;
      }
    }

    > .copy-link {
      margin-bottom: 5px;
      width: 100%;
      display: flex;
      flex-direction: row;

      > input {
        flex: 1;
        border: 0;
        border-bottom: 1px solid rgb(200, 200, 200);
      }

      > button img {
        height: 20px;
        width: 20px;
      }
    }
  }
}

.loading-overlay {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 !important;

  > .loading-overlay-content {
    font-size: 1.5em;
    color: white;
    text-shadow: 5px 5px 5px black;

    &:after {
      animation: dotappear 1s alternate infinite;
      content: '.';
      display: inline-block;
    }
  }
}

@keyframes dotappear {
  0% {
    content: '.';
  }
  20% {
    content: '..';
  }
  40% {
    content: '...';
  }
  60% {
    content: '....';
  }
  80% {
    content: '.....';
  }
  100% {
    content: '......';
  }
}
</style>
